// Couche de données unique pour toute l'application.
// Deux implémentations derrière la même API :
//   • Firebase (Auth + Firestore + Storage) si js/firebase-config.js est rempli
//   • Mode local (localStorage + BroadcastChannel) sinon, pour tester sans compte
import { SERVICES, APP } from "./config.js";
import * as FB from "./firebase.js";
import * as S from "./securite.js";
import * as R from "./reactions.js";
const Securite = S;

// Choix du pilote de données. Ordre : ce qui est explicitement demandé, sinon
// Supabase s'il est configuré, sinon Firebase, sinon le mode local.
const SB = SERVICES?.supabase || {};
const SUPABASE_PRET = Boolean(SB.url && SB.cleAnonyme);
const DEMANDE = SERVICES?.base || "auto";

const MODE =
  DEMANDE === "local" ? "local"
  : DEMANDE === "supabase" || (DEMANDE === "auto" && SUPABASE_PRET) ? (SUPABASE_PRET ? "supabase" : "local")
  : DEMANDE === "firebase" || FB.configured() ? (FB.configured() ? "firebase" : "local")
  : "local";

/* ---------------------------------------------------------------- utilitaires */

const K = {
  users: "kn.users",
  session: "kn.session",
  posts: "kn.posts",
  amens: "kn.amens",
  // Le tiroir des réactions aux COMMENTAIRES, séparé de celui des publications :
  // deux identifiants venus du même générateur ne doivent jamais se croiser.
  amensCommentaires: "kn.amens.commentaires",
  comments: "kn.comments",
  convos: "kn.convos",
  messages: "kn.messages",
  reports: "kn.reports",
  presence: "kn.presence",
  stories: "kn.stories",
  notifs: "kn.notifs",
  amis: "kn.amis",
  abonnes: "kn.abonnes",
  blocages: "kn.blocages",
  groupes: "kn.groupes",
  groupeMembres: "kn.groupeMembres",
  pages: "kn.pages",
  pageAbonnes: "kn.pageAbonnes",
  evenements: "kn.evenements",
  evtReponses: "kn.evtReponses",
  vues: "kn.vues",
};

/**
 * État de la relation entre moi et un autre membre, calculé à partir de mes relations.
 * → "aucun" | "envoyee" (j'ai demandé) | "recue" (on m'a demandé) | "amis"
 */
export function etatAmitie(relations, moi, autre) {
  const r = (relations || []).find((x) => x.membres.includes(autre));
  if (!r) return "aucun";
  if (r.etat === "acceptee") return "amis";
  return r.de === moi ? "envoyee" : "recue";
}

/**
 * Lit UNE SEULE valeur d'un abonnement, puis se désabonne.
 *
 * À écrire soi-même, ce petit bout se trompe presque à tous les coups : les
 * abonnements de Koinonia émettent DÈS leur installation, donc la fonction
 * d'arrêt n'existe pas encore quand le premier appel arrive. Un
 * `const stop = subscribe(v => { ...; stop(); })` lève alors une erreur.
 * Ici l'arrêt est différé d'un tour de boucle : il existe forcément.
 *
 * @param {function} abonner  (cb) => fonction d'arrêt
 */
export function lireUneFois(abonner, secours = [], delaiMax = 6000) {
  return new Promise((resolve) => {
    let arreter = null;
    let fini = false;
    const finir = (valeur) => {
      if (fini) return;
      fini = true;
      clearTimeout(minuteur);
      resolve(valeur);
      setTimeout(() => arreter && arreter(), 0);
    };
    // Un abonnement refusé par les règles n'émet parfois RIEN — pas même une
    // erreur que l'on puisse attraper ici. Sans ce délai, l'écran qui attend
    // cette valeur reste figé indéfiniment sur « Chargement… ». Mieux vaut
    // afficher la page avec une information manquante que ne rien afficher.
    const minuteur = setTimeout(() => finir(secours), delaiMax);
    arreter = abonner(finir);
  });
}

// Nombre de publications chargées d'un coup. Le fil en demande d'autres au fil
// du défilement plutôt que de tout charger d'avance (voir ARCHITECTURE.md, limite n°1).
export const TAILLE_PAGE = 20;

// Une story reste visible 24 h.
export const DUREE_STORY = 24 * 3600 * 1000;

/**
 * L'attestation de baptême ne va JAMAIS dans le stockage de fichiers.
 *
 * Raison technique : les règles Storage ne savent pas lire un rôle en base.
 * Tout chemin lisible par un modérateur y serait donc lisible par n'importe
 * quel membre — inacceptable pour une donnée sur la religion. Et un fichier
 * déposé là ne peut être effacé que par son propriétaire, jamais par le
 * modérateur qui vient de statuer : la promesse « effacée après vérification »
 * aurait été fausse.
 *
 * Elle est donc gardée en base, dans `users/{uid}/prive/attestation`, une
 * sous-collection que seules deux personnes peuvent lire : l'intéressé et la
 * modération. La supprimer est alors atomique et vérifiable.
 *
 * Compression volontairement forte : une attestation doit être lisible, pas
 * belle. On reste très en dessous de la limite d'un document Firestore (1 Mio).
 */
export const ATTESTATION = { largeurMax: 1000, qualite: 0.6, poidsMax: 700 * 1024 };
/* Au-delà de 180 s sans signe de vie, un membre n'est plus « en ligne ».
   ⚠️ TOUJOURS PLUS DU DOUBLE DU BATTEMENT (80 s, dans js/app.js et App.js
   natif) : un battement manqué ne doit pas faire clignoter le badge. Le couple
   était 40 s / 90 s ; passé à 80 s / 180 s le 20 octobre 2026 pour payer deux
   fois moins d'écritures Firestore — en échange, la pastille peut rester
   allumée jusqu'à trois minutes après le départ. */
const FENETRE_PRESENCE = 180 * 1000;

/* Combien d'amis on interroge pour compter les amis en commun. Chacun coûte une
   requête : quarante est le point où le décompte reste juste pour presque tout
   le monde sans que l'écran d'un membre très entouré coûte une fortune. */
const PLAFOND_VOISINAGE = 40;

/* Les COMPTES D'ACCUEIL — le fondateur, et qui écrit au nom de la page.
   Ce sont les comptes auxquels un nouveau venu est abonné d'office (voir
   `js/decouverte.js`), et ils suivent une règle à part : tout ce qu'ils
   publient est ouvert à tous, comme les publications d'une page.

   ⚠️ C'EST UN COMPTE PUBLIC, ET IL FAUT LE SAVOIR. Le réglage « Qui peut voir
   mes publications » ne s'applique plus à eux : ils ne peuvent plus rien
   réserver à leurs amis depuis leur propre compte. C'est le prix de la
   promesse faite au nouveau venu — il doit voir la vie de la communauté dès
   son arrivée, et un fil vide ne se rattrape pas. Qui veut écrire à ses seuls
   amis le fait depuis un autre compte, ou retire le sien de `APP.decouverte`. */
const compteDAccueil = (uid) => Boolean(uid) && (APP.decouverte?.membres || []).includes(uid);

const read = (k, d) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : d;
  } catch {
    return d;
  }
};
const write = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch (e) {
    // localStorage plafonne autour de 5 Mo : c'est la limite du mode démo (photos/vidéos)
    throw new Error("La mémoire de ce navigateur est pleine. Supprimez d'anciennes publications, ou configurez Firebase pour un stockage illimité.");
  }
};

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

/* ══════════════════════════════════════════ L'HEURE, ET DE QUI ON LA TIENT ══

   ⚠️ `now()` DONNE L'HEURE DE L'APPAREIL, ET C'EST UN PROBLÈME. Tout ce qui
   s'écrit ici — messages, publications, commentaires — est daté par celui qui
   écrit, puis TRIÉ sur cette date chez tout le monde. Deux téléphones dont les
   horloges diffèrent de deux minutes produisent donc un fil dans le désordre.

   Le symptôme rapporté par Josaphat le 25 août 2026 : « quand j'envoie un
   message et que mon correspondant répond, mon message vient se placer EN
   DESSOUS de sa réponse ». Son téléphone avançait sur celui d'en face. Rien
   dans l'écran de conversation ne pouvait le corriger : il ne fait que trier
   ce qu'on lui donne.

   ⚠️ POURQUOI PAS `serverTimestamp()` DE FIRESTORE, la solution évidente.
   Parce qu'il rend un objet `Timestamp`, là où ce projet manipule partout un
   NOMBRE de millisecondes — dans les deux applications, dans le mode local,
   dans les règles, dans le banc d'essai. Basculer le type casserait cent sept
   endroits pour en réparer un. Et surtout : la valeur reste `null` tant que le
   serveur n'a pas confirmé, ce qui ferait sauter chaque message à l'écran.

   ⚠️ ON CORRIGE DONC L'HORLOGE, PAS LE FORMAT. Une fois par session, on
   demande l'heure à un serveur et l'on retient l'ÉCART. `now()` l'ajoute. Le
   type ne change pas, aucune règle ne bouge, et le désordre disparaît pour
   tout ce qui se date — pas seulement pour les messages.

   ⚠️ ET SI L'ON N'OBTIENT RIEN, ON NE CORRIGE RIEN. Hors réseau, l'écart reste
   à zéro et l'on retombe exactement sur le comportement d'avant : une horloge
   approximative vaut mieux qu'une application qui refuse d'écrire. */

let decalageHorloge = 0;

const now = () => Date.now() + decalageHorloge;

/**
 * Demande l'heure à un serveur et retient l'écart. À appeler au démarrage ;
 * ne rend jamais d'erreur, n'attend rien de personne.
 *
 * ⚠️ ON LIT LE CORPS, PAS L'EN-TÊTE `Date`. La première version lisait
 * l'en-tête — toute réponse HTTP en porte un — et obtenait `null` sans la
 * moindre erreur : un navigateur ne laisse PAS lire `Date` d'un autre domaine,
 * il ne fait pas partie des en-têtes exposés par défaut. Vérifié dans le
 * navigateur le 25 août 2026. D'où le point d'accès `/heure`, qui rend
 * simplement `{ ms }`.
 *
 * ⚠️ ON PREND LE MILIEU DE L'ALLER-RETOUR. L'heure reçue date du moment où le
 * serveur a répondu, pas de maintenant : sur une 4G à 600 ms, la compter
 * telle quelle introduirait un demi-écart à la place de celui qu'on corrige.
 *
 * ⚠️ ET L'ON NE CORRIGE QUE CE QUI MÉRITE DE L'ÊTRE. En dessous de deux
 * secondes, l'écart tient à la latence plus qu'à l'horloge : le corriger
 * ajouterait du bruit à une heure déjà juste.
 */
export async function reglerHorloge(adresse) {
  try {
    const depart = Date.now();
    const reponse = await fetch(`${adresse}/heure`, { method: "GET", cache: "no-store" });
    if (!reponse.ok) return 0;
    const { ms } = await reponse.json();
    const serveur = Number(ms);
    if (!Number.isFinite(serveur)) return 0;

    const allerRetour = Date.now() - depart;
    const ecart = serveur + allerRetour / 2 - Date.now();
    decalageHorloge = Math.abs(ecart) > 2000 ? Math.round(ecart) : 0;
    return decalageHorloge;
  } catch {
    return 0;   // hors réseau : on garde l'horloge de l'appareil
  }
}

// Identifiant de conversation déterministe : même paire → même document
const convoId = (a, b) => [a, b].sort().join("__");

/* ------------------------------------------------- bus de rafraîchissement local */

const listeners = new Set();
const channel = "BroadcastChannel" in window ? new BroadcastChannel("koinonia") : null;
if (channel) channel.onmessage = () => listeners.forEach((fn) => fn());

function touch() {
  listeners.forEach((fn) => fn());
  if (channel) channel.postMessage(1);
}

function onLocalChange(fn) {
  listeners.add(fn);
  fn();
  return () => listeners.delete(fn);
}

/* ------------------------------------------------------------------ démo locale */

function seed() {
  if (!window.DEMO_SEED || read(K.users, null)) return;
  const t = now();
  const users = {
    d1: { uid: "d1", nom: "Pasteur Emmanuel", email: "emmanuel@demo.app", mdp: "demo1234", bio: "Serviteur de Dieu. Passionné par la Parole et la jeunesse.", ville: "Parakou", eglise: "ICC Parakou", photo: "", couverture: "", createdAt: t - 9e7, role: "moderateur" },
    d2: { uid: "d2", nom: "Grâce Adjovi", email: "grace@demo.app", mdp: "demo1234", bio: "Choriste 🎶 « Que tout ce qui respire loue l'Éternel »", ville: "Cotonou", eglise: "ICC Cotonou", photo: "", couverture: "", createdAt: t - 8e7, role: "membre" },
    d3: { uid: "d3", nom: "Josué Kora", email: "josue@demo.app", mdp: "demo1234", bio: "Étudiant, groupe de prière du campus.", ville: "Parakou", eglise: "ICC Parakou", photo: "", couverture: "", createdAt: t - 7e7, role: "membre" },
  };
  const posts = [
    { id: "p1", type: "verset", authorId: "d1", auteurNom: users.d1.nom, auteurPhoto: "", texte: "« Car je connais les projets que j'ai formés sur vous, projets de paix et non de malheur, afin de vous donner un avenir et de l'espérance. »\n— Jérémie 29:11\n\nQue ce verset vous accompagne toute la semaine. 🙏", image: "", amens: 12, commentaires: 1, createdAt: t - 36e5 * 5 },
    { id: "p2", type: "priere", authorId: "d3", auteurNom: users.d3.nom, auteurPhoto: "", texte: "Je passe mes examens de fin d'année cette semaine. Merci de vous souvenir de moi dans vos prières 🙏", image: "", amens: 8, commentaires: 1, createdAt: t - 36e5 * 2 },
    { id: "p3", type: "temoignage", authorId: "d2", auteurNom: users.d2.nom, auteurPhoto: "", texte: "Gloire à Dieu ! Après 8 mois de recherche, j'ai enfin trouvé du travail. Il n'est jamais en retard. ✨", image: "", amens: 24, commentaires: 0, createdAt: t - 36e5 },
  ];
  const comments = [
    { id: "c1", postId: "p1", authorId: "d2", auteurNom: users.d2.nom, auteurPhoto: "", texte: "Amen ! Merci Pasteur 🙏", createdAt: t - 36e5 * 4 },
    { id: "c2", postId: "p2", authorId: "d1", auteurNom: users.d1.nom, auteurPhoto: "", texte: "Nous prions pour toi Josué. Le Seigneur te donnera l'intelligence.", createdAt: t - 36e5 },
  ];
  const stories = [
    { id: "s1", authorId: "d2", auteurNom: users.d2.nom, auteurPhoto: "", type: "texte", media: "", texte: "Répétition de la chorale ce soir à 18h 🎶", fond: 0, createdAt: t - 36e5 * 3 },
    { id: "s2", authorId: "d1", auteurNom: users.d1.nom, auteurPhoto: "", type: "texte", media: "", texte: "« Que la paix de Christ règne dans vos cœurs. » Colossiens 3:15", fond: 2, createdAt: t - 36e5 * 6 },
  ];
  write(K.users, users);
  write(K.posts, posts);
  write(K.comments, comments);
  write(K.amens, {});
  write(K.convos, []);
  write(K.messages, []);
  write(K.reports, []);
  write(K.stories, stories);
  write(K.notifs, []);
  write(K.presence, {});
  write(K.amis, [{ id: "d1__d2", membres: ["d1", "d2"], de: "d1", vers: "d2", etat: "acceptee", createdAt: t - 8e7 }]);
}
if (MODE === "local") seed();

/* -------------------------------------------------------------- images (commun) */

/**
 * L'attestation de baptême, prête à être enregistrée.
 *
 * ⚠️ ELLE ARRIVE SOUS DEUX FORMES, ET C'EST VOULU.
 *   · Le SITE envoie le fichier brut choisi dans le formulaire : on le réduit
 *     ici, avec le canevas du navigateur.
 *   · Le TÉLÉPHONE envoie une image DÉJÀ réduite : il n'a pas de canevas, et
 *     réduit avec ses propres outils (`expo-image-manipulator`).
 *
 * Avant le 15 août 2026, seule la première forme était acceptée — et
 * l'enregistrement de l'attestation vivait DANS le `if (fichier)`. Le téléphone
 * devait donc refaire ce travail de son côté : la même opération écrite à deux
 * endroits, sur la donnée la plus sensible du projet. Le jour où l'une aurait
 * changé sans l'autre, on aurait retrouvé la panne de Ketsia — quelqu'un
 * d'inscrit, en attente, sans attestation à examiner, et invisible.
 *
 * Rend `""` s'il n'y a rien à enregistrer.
 */
/* ⚠️ PLUS PERSONNE NE L'APPELLE DEPUIS LE 18 AOÛT 2026 — l'attestation de
   baptême a été retirée de l'inscription (voir `validerIdentite`). La fonction
   reste : elle porte la seule manière correcte de préparer une attestation, et
   la rature serait à réécrire si l'on y revenait un jour. */
async function attestationPrete(bapteme) {
  const fichier = bapteme?.fichier instanceof Blob ? bapteme.fichier : null;
  if (fichier) {
    const { dataURL } = await compressImage(fichier, ATTESTATION.largeurMax, ATTESTATION.qualite);
    return dataURL;
  }
  // Déjà réduite ailleurs : on la reprend telle quelle, après l'avoir bornée.
  // `lienSur` n'accepte qu'une image ; le poids est vérifié comme pour l'autre
  // forme, sinon le téléphone pourrait déposer ce que le site refuse.
  const dejaPrete = S.lienSur(bapteme?.photo);
  if (!dejaPrete) return "";
  if (dejaPrete.length > ATTESTATION.poidsMax)
    throw new S.ErreurValidation("Cette photo est trop lourde. Reprenez-la de plus près, ou plus nette.");
  return dejaPrete;
}

// Réduit et compresse une image avant envoi : indispensable en mode local
// (localStorage ~5 Mo) et très utile en ligne (économie de stockage/bande passante).
export function compressImage(file, max = 1280, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Image illisible"));
      img.onload = () => {
        const ratio = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * ratio);
        c.height = Math.round(img.height * ratio);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        c.toBlob(
          (blob) => (blob ? resolve({ blob, dataURL: c.toDataURL("image/jpeg", quality) }) : reject(new Error("Compression impossible"))),
          "image/jpeg",
          quality
        );
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export const fichierEnDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Lecture du fichier impossible"));
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });

/* ------------------------------------------------------- garde des écritures */
// Point de passage unique : Firebase comme mode local traversent ces fonctions,
// donc aucune vue ne peut écrire de données non validées, même par erreur.

function validerPost(d) {
  const valide = {
    ...d,
    texte: S.texte(d.texte, S.LIMITES.post, { champ: "La publication" }),
    image: S.lienSur(d.image),
    video: S.lienSur(d.video),
  };
  S.cadence("publication"); // seulement une fois la saisie jugée correcte
  return valide;
}

function validerCommentaire(d) {
  const valide = {
    ...d,
    texte: S.texte(d.texte, S.LIMITES.commentaire, { champ: "Le commentaire", requis: true }),
    // identifiant du commentaire auquel on répond, ou null. On le nettoie :
    // c'est une donnée qui vient du navigateur comme une autre.
    repondA: d.repondA ? S.texte(String(d.repondA), 64, { champ: "La réponse" }) : null,
  };
  S.cadence("commentaire");
  return valide;
}

function validerMessage(t) {
  const valide = S.texte(t, S.LIMITES.message, { champ: "Le message", requis: true });
  S.cadence("message");
  return valide;
}

function validerStory(d) {
  const valide = { ...d, texte: S.texte(d.texte, S.LIMITES.story, { champ: "Le statut" }), media: S.lienSur(d.media) };
  S.cadence("story");
  return valide;
}

function validerProfil(patch) {
  const p = { ...patch };
  // Le nom est RÉEL (prénom et nom, lettres seulement) — voir S.nomReel.
  if ("nom" in p) p.nom = S.nomReel(p.nom);
  if ("bio" in p) p.bio = S.texte(p.bio, S.LIMITES.bio, { champ: "La bio" });
  if ("ville" in p) p.ville = S.texte(p.ville, S.LIMITES.ville, { champ: "La ville" });
  if ("eglise" in p) p.eglise = S.texte(p.eglise, S.LIMITES.eglise, { champ: "L'église" });
  if ("photo" in p) p.photo = S.lienSur(p.photo);
  if ("couverture" in p) p.couverture = S.lienSur(p.couverture);
  if ("confidentialite" in p) p.confidentialite = S.confidentialiteSure(p.confidentialite);
  // champs qu'un membre ne fixe jamais lui-même
  delete p.role;
  delete p.uid;
  delete p.email;
  delete p.createdAt;
  return p;
}

function validerGroupe(d) {
  const valide = {
    ...d,
    nom: S.texte(d.nom, S.LIMITES.groupeNom, { champ: "Le nom du groupe", requis: true }),
    description: S.texte(d.description, S.LIMITES.groupeDescription, { champ: "La description" }),
    image: S.lienSur(d.image),
  };
  S.cadence("groupe");
  return valide;
}

function validerPage(d) {
  const valide = {
    ...d,
    nom: S.texte(d.nom, S.LIMITES.pageNom, { champ: "Le nom de la page", requis: true }),
    description: S.texte(d.description, S.LIMITES.pageDescription, { champ: "La description" }),
    image: S.lienSur(d.image),
    couverture: S.lienSur(d.couverture),
  };
  S.cadence("page");
  return valide;
}

/** Modification partielle d'une page : on ne valide que les champs présents. */
function validerPagePatch(patch) {
  const p = { ...patch };
  if ("nom" in p) p.nom = S.texte(p.nom, S.LIMITES.pageNom, { champ: "Le nom de la page", requis: true });
  if ("description" in p) p.description = S.texte(p.description, S.LIMITES.pageDescription, { champ: "La description" });
  if ("image" in p) p.image = S.lienSur(p.image);
  if ("couverture" in p) p.couverture = S.lienSur(p.couverture);
  // champs qu'un administrateur ne réécrit jamais à la main
  delete p.creeParId;
  delete p.createdAt;
  return p;
}

/**
 * Identité demandée à l'inscription, dans l'esprit des grands réseaux :
 * prénom, nom, date de naissance, genre — plus l'attestation de baptême,
 * propre à Koinonia.
 */
function validerIdentite(d) {
  const prenom = S.texte(d.prenom, S.LIMITES.prenom, { champ: "Le prénom", requis: true });
  const nomFamille = S.texte(d.nomFamille, S.LIMITES.nom, { champ: "Le nom", requis: true });
  // Le nom complet doit être RÉEL (lettres seulement, deux mots au moins) :
  // la même grammaire que la règle du serveur, dite ici avec de vrais mots.
  S.nomReel(`${prenom} ${nomFamille}`);
  const bapteme = d.bapteme || {};

  /* ⚠️ L'ATTESTATION DE BAPTÊME A ÉTÉ RETIRÉE LE 18 AOÛT 2026, sur décision de
     Josaphat. Ce n'est pas un allègement de confort, c'est une correction :

       · une photo d'attestation est TRIVIALE à contrefaire — celle de
         n'importe qui fait l'affaire, et personne ne peut la recouper ;
       · elle ÉCARTAIT de vrais croyants : beaucoup de baptisés n'ont aucun
         papier, surtout dans les assemblées modestes et pour les baptêmes
         anciens ;
       · elle obligeait Koinonia à CONSERVER un document religieux nominatif,
         la donnée personnelle la plus sensible qui soit.

     Un papier facile à falsifier, qui refuse des honnêtes gens et qui crée un
     risque : les trois raisons vont dans le même sens.

     ⚠️ CE QUI LA REMPLACE EST UN LIEN HUMAIN, PAS UN DOCUMENT. On demande
     maintenant deux choses — êtes-vous baptisé, et qui vous a fait connaître
     Koinonia — et c'est la MODÉRATION qui tranche. Le nom donné n'est pas
     vérifié par la machine et ne déclenche aucune confirmation automatique :
     il est là pour que le responsable reconnaisse, ou non, un lien de
     filiation réel. La décision reste entière entre ses mains.

     ⚠️ RÉPONDRE « NON » N'INTERDIT RIEN. Le compte est créé en attente comme
     tous les autres. Un catéchumène sincère ne doit pas être arrêté par une
     case — c'est précisément le genre de personne que cette communauté
     existe pour accueillir. */
  const baptise = bapteme.baptise;
  if (baptise !== true && baptise !== false)
    throw new S.ErreurValidation("Indiquez si vous êtes baptisé.");

  return {
    prenom,
    nomFamille,
    nom: `${prenom} ${nomFamille}`.trim(),
    dateNaissance: S.dateNaissanceSure(d.dateNaissance),
    genre: S.genreSur(d.genre),
    telephone: S.telephoneSur(d.telephone, { requis: true }),
    /* ⚠️ LE CHAMP GARDE SON NOM DE `bapteme`, ET C'EST VOULU.
       Il ne contient plus seulement le baptême : c'est devenu le DOSSIER
       D'ADMISSION. Le renommer obligerait à toucher `firestore.rules`, qui le
       protège nommément — seule la modération peut y écrire après coup — et à
       reprendre les fiches déjà en base. On garde la clé, on écrit ici ce
       qu'elle porte désormais. */
    bapteme: {
      baptise,
      connuPar: S.texte(bapteme.connuPar, S.LIMITES.nom, {
        champ: "La personne qui vous a fait connaître Koinonia",
        requis: true,
      }),
      // Facultatives depuis le 18 août : elles éclairent le responsable sans
      // barrer la route à qui ne s'en souvient pas.
      eglise: S.texte(bapteme.eglise, S.LIMITES.eglise, { champ: "L'église du baptême" }),
      date: S.texte(bapteme.date, 20, { champ: "La date du baptême" }),
      depose: now(),
    },
  };
}

function validerSignalement(d) {
  const valide = { ...d, detail: S.texte(d.detail, S.LIMITES.signalement, { champ: "La précision" }) };
  S.cadence("signalement");
  return valide;
}

/* ============================================================ IMPLÉMENTATION LOCALE */

const local = {
  _uid: null,

  auth: {
    onChange(cb) {
      local._uid = read(K.session, null);
      const emit = () => {
        const u = read(K.users, {})[read(K.session, null)] || null;
        local._uid = u ? u.uid : null;
        // Le mode démo n'envoie pas d'e-mail : les comptes y sont vérifiés d'office,
        // sauf si la fiche porte explicitement emailVerifie: false (utile pour éprouver l'écran).
        cb(u ? { ...sanitize(u), emailVerifie: u.emailVerifie !== false } : null);
      };
      emit();
      return onLocalChange(emit);
    },
    async signUp(donnees) {
      const { email, mdp } = donnees;
      const courriel = S.emailSur(email);
      S.motDePasseSur(mdp);
      const users = read(K.users, {});
      if (Object.values(users).some((u) => u.email.toLowerCase() === courriel))
        throw new Error("Un compte existe déjà avec cet e-mail.");
      const identite = validerIdentite(donnees);
      const profil = validerProfil({
        nom: identite.nom,
        ville: S.texte(donnees.ville, S.LIMITES.ville, { champ: "La ville", requis: true }),
        eglise: S.texte(donnees.eglise, S.LIMITES.eglise, { champ: "L'église", requis: true }),
      });
      S.cadence("inscription"); // uniquement quand tout est valide
      const id = uid();
      const selHex = S.sel();
      // seule l'empreinte est conservée : le mot de passe n'existe nulle part en clair
      users[id] = {
        uid: id,
        ...profil,
        ...identite,
        email: courriel,
        sel: selHex,
        empreinte: await S.empreinte(mdp, selHex),
        bio: "",
        photo: "",
        couverture: "",
        createdAt: now(),
        role: "membre",
        // le compte existe et peut lire ; il ne publie qu'une fois l'attestation validée
        validation: "en_attente",
      };
      write(K.users, users);
      write(K.session, id);
      touch();
      return sanitize(users[id]);
    },
    async signIn({ email, mdp }) {
      S.cadence("connexion");
      const courriel = S.emailSur(email);
      const users = read(K.users, {});
      const u = Object.values(users).find((x) => x.email.toLowerCase() === courriel);
      // message identique compte inexistant / mauvais mot de passe : on ne dit
      // jamais si une adresse est inscrite (évite d'énumérer les membres)
      const refus = "E-mail ou mot de passe incorrect.";
      if (!u) {
        await S.empreinte(mdp, "leurre"); // même temps de réponse qu'un vrai essai
        throw new Error(refus);
      }
      let ok = false;
      if (u.empreinte) {
        ok = S.memeEmpreinte(await S.empreinte(mdp, u.sel), u.empreinte);
      } else if (u.mdp) {
        // ancien compte enregistré en clair : on vérifie puis on le convertit
        ok = u.mdp === mdp;
        if (ok) {
          const selHex = S.sel();
          u.sel = selHex;
          u.empreinte = await S.empreinte(mdp, selHex);
          delete u.mdp;
          write(K.users, users);
        }
      }
      if (!ok) throw new Error(refus);
      S.oublier("connexion"); // réussite : les essais précédents sont oubliés
      write(K.session, u.uid);
      touch();
      return sanitize(u);
    },
    async signOut() {
      localStorage.removeItem(K.session);
      touch();
    },
    /**
     * Changement de mot de passe. L'ancien est toujours redemandé : sans cela,
     * un téléphone laissé déverrouillé une minute suffirait à voler un compte.
     */
    async changerMotDePasse(ancien, nouveau) {
      const users = read(K.users, {});
      const id = read(K.session, null);
      const u = id && users[id];
      if (!u) throw new Error("Non connecté");
      const bon = u.empreinte
        ? S.memeEmpreinte(await S.empreinte(ancien, u.sel), u.empreinte)
        : u.mdp === ancien;
      if (!bon) throw new S.ErreurValidation("Mot de passe actuel incorrect.");
      const selHex = S.sel();
      u.sel = selHex;
      u.empreinte = await S.empreinte(nouveau, selHex);
      delete u.mdp;
      write(K.users, users);
      touch();
    },
    async reinitialiserMotDePasse() {
      throw new S.ErreurValidation("La réinitialisation par e-mail demande Firebase.");
    },
    /* Le mode démo n'a pas de jeton : il n'y a rien à rafraîchir. La fonction
       existe pour que la façade n'ait pas à demander quel pilote elle enveloppe. */
    async rafraichirSession() { return false; },
    // Le mode démo ne peut pas envoyer d'e-mail : les comptes y sont réputés vérifiés.
    async envoyerVerification() {
      throw new Error("La vérification par e-mail demande Firebase ou Supabase.");
    },
    async rafraichirVerification() {},
    /* ⚠️ LE MODE DÉMO DOIT RÉPONDRE, MÊME POUR REFUSER. Sans ces deux-là,
       l'écran de la salle d'attente appellerait une fonction inexistante et
       planterait — au lieu d'afficher une phrase. Le mode démo n'envoie aucun
       courriel : ses comptes y sont réputés vérifiés. */
    async demanderCodeEmail() {
      throw new S.ErreurValidation("La vérification par code demande Firebase.");
    },
    async verifierCodeEmail() {
      throw new S.ErreurValidation("La vérification par code demande Firebase.");
    },
  },

  users: {
    async get(id) {
      const u = read(K.users, {})[id];
      return u ? sanitize(u) : null;
    },
    async update(patch) {
      const users = read(K.users, {});
      const id = read(K.session, null);
      if (!users[id]) throw new Error("Non connecté");
      Object.assign(users[id], patch);
      write(K.users, users);
      propagateAuthorLocal(id, users[id]);
      touch();
    },
    async search(q) {
      const s = norm(q);
      return Object.values(read(K.users, {}))
        .filter((u) => !s || norm(u.nom).includes(s) || norm(u.ville || "").includes(s) || norm(u.eglise || "").includes(s))
        .map(sanitize)
        // ⚠️ `a.nom` peut manquer. Une fiche sans nom — un compte à moitié créé,
        // une inscription interrompue — faisait planter le TRI, donc l'écran
        // entier, pour tous les autres membres. Un annuaire ne doit pas tomber
        // parce qu'une de ses fiches est incomplète.
        .sort((a, b) => String(a.nom || "").localeCompare(String(b.nom || "")));
    },
  },

  posts: {
    // le fil public ne montre pas ce qui a été publié dans un groupe
    subscribe(cb) {
      return onLocalChange(() => cb(read(K.posts, []).filter((p) => !p.groupeId).sort((a, b) => b.createdAt - a.createdAt).slice(0, TAILLE_PAGE)));
    },
    subscribeGroupe(id, cb) {
      return onLocalChange(() =>
        cb(read(K.posts, []).filter((p) => p.groupeId === id).sort((a, b) => b.createdAt - a.createdAt).slice(0, TAILLE_PAGE))
      );
    },
    subscribePage(id, cb) {
      return onLocalChange(() =>
        cb(read(K.posts, []).filter((p) => p.pageId === id).sort((a, b) => b.createdAt - a.createdAt).slice(0, TAILLE_PAGE))
      );
    },
    /**
     * Page suivante, repérée par un curseur (la date de la dernière publication
     * affichée) et non par un décalage : le coût ne grandit pas avec la taille.
     */
    async page({ avant, auteurId, groupeId, pageId } = {}) {
      return read(K.posts, [])
        .filter((p) => (pageId ? p.pageId === pageId : groupeId ? p.groupeId === groupeId : !p.groupeId))
        .filter((p) => (auteurId ? p.authorId === auteurId : true))
        .filter((p) => (avant ? p.createdAt < avant : true))
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, TAILLE_PAGE);
    },
    subscribeByAuthor(id, cb) {
      return onLocalChange(() => cb(read(K.posts, []).filter((p) => p.authorId === id).sort((a, b) => b.createdAt - a.createdAt).slice(0, TAILLE_PAGE)));
    },
    async get(id) {
      return read(K.posts, []).find((p) => p.id === id) || null;
    },
    async add(data) {
      const posts = read(K.posts, []);
      const id = uid();
      posts.push({ id, amens: 0, commentaires: 0, partages: 0, createdAt: now(), ...data });
      write(K.posts, posts);
      touch();
      return id;
    },
    /** Un partage de plus sur la publication d'ORIGINE, jamais sur le repartage. */
    async compterPartage(id) {
      const posts = read(K.posts, []);
      const p = posts.find((x) => x.id === id);
      if (!p) return;
      p.partages = (p.partages || 0) + 1;
      write(K.posts, posts);
      touch();
    },
    /* Le jumeau local de `posts.modifier` — voir le pilote Firebase pour le
       raisonnement. Le texte seul, et la trace de la correction. */
    async modifier(id, texte) {
      const posts = read(K.posts, []);
      const p = posts.find((x) => x.id === id);
      if (!p) throw new Error("Publication introuvable");
      p.texte = texte;
      p.modifieLe = now();
      write(K.posts, posts);
      touch();
    },
    async remove(id) {
      write(K.posts, read(K.posts, []).filter((p) => p.id !== id));
      write(K.comments, read(K.comments, []).filter((c) => c.postId !== id));
      touch();
    },
  },

  amens: {
    /* ⚠️ Rend une CARTE `postId → réaction`, là où c'était un ensemble.
       `Map` a un `.has()` : tout ce qui demandait seulement « ai-je soutenu
       celle-ci ? » continue de fonctionner sans une ligne de changement, et ce
       qui veut savoir LAQUELLE appelle `.get()`. C'est ce qui a permis
       d'ajouter six réactions sans toucher aux écrans qui n'en veulent pas. */
    subscribeMine(cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        const carte = new Map();
        for (const [k, v] of Object.entries(read(K.amens, {}))) {
          const coupure = k.indexOf("|");
          if (k.slice(0, coupure) !== me) continue;
          // `true` : une réaction posée avant que les six existent. C'est un Amen.
          carte.set(k.slice(coupure + 1), R.reactionSure(v && v.type) || R.CLE_DEFAUT);
        }
        cb(carte);
      });
    },
    /**
     * Pose, change ou retire une réaction.
     *
     * Reposer celle qu'on a déjà la RETIRE — c'est ce que fait le bouton Amen
     * depuis toujours, et il n'y a aucune raison que la palette se comporte
     * autrement. `cle` à `null` retire aussi.
     */
    async poser(postId, cle) {
      const me = read(K.session, null);
      if (!me) return;
      const all = read(K.amens, {});
      const key = `${me}|${postId}`;
      const posts = read(K.posts, []);
      const p = posts.find((x) => x.id === postId);
      if (!p) return;

      const avant = all[key] ? R.reactionSure(all[key].type) || R.CLE_DEFAUT : null;
      const apres = avant === R.reactionSure(cle) ? null : R.reactionSure(cle);
      if (avant === apres) return;

      // `reactions` compte le détail ; `amens` reste le TOTAL, parce que c'est
      // lui — et lui seul — que portent les publications d'avant les réactions.
      p.reactions = { ...(p.reactions || {}) };
      if (avant) p.reactions[avant] = Math.max(0, (p.reactions[avant] || 0) - 1);
      if (apres) p.reactions[apres] = (p.reactions[apres] || 0) + 1;
      p.amens = Math.max(0, (p.amens || 0) + (apres ? 1 : 0) - (avant ? 1 : 0));

      /* La même liste que côté Firebase — voir le pilote cloud pour le
         raisonnement. Deux pilotes qui divergent, c'est un défaut qu'on ne
         verra jamais au banc. */
      const inscrits = new Set(p.derniersAmens || []);
      if (apres) inscrits.add(me); else inscrits.delete(me);
      p.derniersAmens = [...inscrits];

      if (apres) all[key] = { type: apres, createdAt: now() };
      else delete all[key];

      write(K.amens, all);
      write(K.posts, posts);
      touch();
    },
    /**
     * Qui a réagi à cette publication. Le pilote Firebase a dû se faire un
     * second tiroir pour répondre ; ici la clé porte déjà les deux noms
     * (`moi|publication`), il n'y a qu'à la relire dans l'autre sens.
     */
    async qui(postId, { combien = 100 } = {}) {
      const sortie = [];
      for (const [k, v] of Object.entries(read(K.amens, {}))) {
        const coupure = k.indexOf("|");
        if (k.slice(coupure + 1) !== postId) continue;
        sortie.push({
          uid: k.slice(0, coupure),
          type: R.reactionSure(v && v.type) || R.CLE_DEFAUT,
        });
        if (sortie.length >= combien) break;
      }
      return sortie;
    },
    /** L'appui court : l'Amen, comme avant les six autres. */
    async toggle(postId) {
      return local.amens.poser(postId, R.CLE_DEFAUT);
    },
  },

  /**
   * Vues d'une publication.
   *
   * UNE seule écriture par membre et par publication, jamais une par
   * défilement : l'identifiant du membre sert de clé, donc revoir la même
   * publication n'écrit rien. C'est ce qui rend la fonction abordable —
   * sans cela, un fil de vingt publications parcouru cinq fois par jour
   * par cent membres ferait dix mille écritures quotidiennes.
   */
  vues: {
    async marquer(collection, id) {
      const me = read(K.session, null);
      if (!me) return;
      const cle = `${collection}:${id}`;
      const toutes = read(K.vues, {});
      if ((toutes[cle] || []).some((v) => v.uid === me)) return;
      const u = read(K.users, {})[me] || {};
      toutes[cle] = [...(toutes[cle] || []), { uid: me, nom: u.nom || "Membre", photo: u.photo || "", le: now() }];
      write(K.vues, toutes);
      const cleListe = collection === "stories" ? K.stories : K.posts;
      const liste = read(cleListe, []);
      const x = liste.find((y) => y.id === id);
      if (x) x.vues = (x.vues || 0) + 1;
      write(cleListe, liste);
      touch();
    },
    /** La liste n'est lisible que par l'auteur : c'est lui qui la demande. */
    async liste(collection, id) {
      return (read(K.vues, {})[`${collection}:${id}`] || []).slice().sort((a, b) => b.le - a.le);
    },
  },

  comments: {
    subscribe(postId, cb) {
      return onLocalChange(() => cb(read(K.comments, []).filter((c) => c.postId === postId).sort((a, b) => a.createdAt - b.createdAt)));
    },
    async add(postId, data) {
      const list = read(K.comments, []);
      list.push({ id: uid(), postId, createdAt: now(), ...data });
      write(K.comments, list);
      const posts = read(K.posts, []);
      const p = posts.find((x) => x.id === postId);
      if (p) p.commentaires = (p.commentaires || 0) + 1;
      write(K.posts, posts);
      touch();
    },
    async remove(postId, id) {
      write(K.comments, read(K.comments, []).filter((c) => c.id !== id));
      const posts = read(K.posts, []);
      const p = posts.find((x) => x.id === postId);
      if (p) p.commentaires = Math.max(0, (p.commentaires || 0) - 1);
      write(K.posts, posts);
      touch();
    },

    /* Les jumeaux locaux — voir le pilote Firebase pour le raisonnement.
       Le mode démonstration doit se comporter pareil, sinon on éprouve un
       écran sans éprouver ce qu'il fait. */
    async reagir(postId, cid) {
      const me = read(K.session, null);
      if (!me) return;
      const cle = `${me}|${cid}`;
      const tiroir = read(K.amensCommentaires, {});
      const avait = Boolean(tiroir[cle]);
      if (avait) delete tiroir[cle];
      else tiroir[cle] = { type: "amen", postId, createdAt: now() };
      write(K.amensCommentaires, tiroir);

      const comments = read(K.comments, []);
      const c = comments.find((x) => x.id === cid);
      if (c) c.amens = Math.max(0, (c.amens || 0) + (avait ? -1 : 1));
      write(K.comments, comments);
      touch();
      return !avait;
    },

    subscribeMesAmens(cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        const tiroir = read(K.amensCommentaires, {});
        const miennes = new Set();
        for (const k of Object.keys(tiroir)) {
          const coupure = k.indexOf("|");
          if (k.slice(0, coupure) === me) miennes.add(k.slice(coupure + 1));
        }
        cb(miennes);
      });
    },
  },

  convos: {
    subscribe(cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        cb(read(K.convos, [])
          .filter((c) => c.membres.includes(me))
          // le miroir du nuage : une conversation supprimée pour moi se cache
          .filter((c) => (c.dernierAt || c.createdAt || 0) > (c.cacheAvant?.[me] || 0))
          .sort((a, b) => (b.dernierAt || 0) - (a.dernierAt || 0)));
      });
    },
    async supprimer(id) {
      const me = read(K.session, null);
      const list = read(K.convos, []);
      const c = list.find((x) => x.id === id);
      if (c) { c.cacheAvant = { ...c.cacheAvant, [me]: now() }; write(K.convos, list); }
    },
    async ensure(otherUid) {
      const me = read(K.session, null);
      const id = convoId(me, otherUid);
      const list = read(K.convos, []);
      if (!list.some((c) => c.id === id)) {
        list.push({ id, membres: [me, otherUid], dernier: "", dernierAt: now(), lu: { [me]: true, [otherUid]: true } });
        write(K.convos, list);
        touch();
      }
      return id;
    },
    /**
     * Une conversation à plusieurs.
     *
     * Contrairement au tête-à-tête, son identifiant n'est PAS déductible des
     * membres : deux groupes peuvent réunir exactement les mêmes personnes sans
     * être la même conversation — les responsables et la chorale, par exemple.
     */
    async creerGroupe(uids, nom) {
      const me = read(K.session, null);
      const membres = [...new Set([me, ...(uids || [])])];
      if (membres.length < 3) throw new Error("Choisissez au moins deux personnes.");
      const id = uid();
      const list = read(K.convos, []);
      list.push({
        id, membres, nom: nom || "", groupe: true, creeParId: me,
        sentinelles: [me],
        dernier: "", dernierAt: now(),
        lu: Object.fromEntries(membres.map((m) => [m, m === me])),
      });
      write(K.convos, list);
      touch();
      return id;
    },
    async get(id) {
      return read(K.convos, []).find((c) => c.id === id) || null;
    },
    /**
     * Deux informations, et non une seule.
     *
     * `lu` dit s'il reste quelque chose à lire — c'est ce qui allume la pastille.
     * `luA` dit JUSQU'À QUAND on a lu, et c'est cela qui permet de marquer chaque
     * message d'un trait ou de deux : un message écrit après ce moment n'a pas
     * encore été vu, même si la conversation, elle, a déjà été ouverte.
     */
    /* Renommer un groupe, y ajouter du monde. Voir le pilote Firebase pour le
       raisonnement — il vaut des deux côtés. */
    async renommer(id, nom) {
      const list = read(K.convos, []);
      const c = list.find((x) => x.id === id);
      if (!c) return;
      c.nom = String(nom || "").trim().slice(0, 60);
      write(K.convos, list);
      touch();
    },
    async photoGroupe(id, url) {
      const list = read(K.convos, []);
      const c = list.find((x) => x.id === id);
      if (c) { c.photo = url || ""; write(K.convos, list); touch(); }
    },
    async nommerSentinelle(id, uid) {
      const list = read(K.convos, []);
      const c = list.find((x) => x.id === id);
      if (!c) return;
      c.sentinelles = [...new Set([...(c.sentinelles || []), uid])];
      write(K.convos, list);
      touch();
    },
    async ajouterMembres(id, uids) {
      const list = read(K.convos, []);
      const c = list.find((x) => x.id === id);
      if (!c) return;
      const avant = c.membres || [];
      c.membres = [...new Set([...avant, ...(uids || [])])].slice(0, 30);
      c.groupe = c.membres.length > 2;
      for (const u of c.membres) if (!(u in (c.lu || {}))) (c.lu ||= {})[u] = false;
      write(K.convos, list);
      touch();
    },

    async markRead(id) {
      const me = read(K.session, null);
      const list = read(K.convos, []);
      const c = list.find((x) => x.id === id);
      if (!c) return;
      if (c.lu?.[me] && (c.luA?.[me] || 0) >= (c.dernierAt || 0)) return; // rien de neuf
      c.lu = { ...(c.lu || {}), [me]: true };
      c.luA = { ...(c.luA || {}), [me]: now() };
      write(K.convos, list);
      touch();
    },
    /**
     * « En train d'écrire ».
     *
     * On note l'instant de la dernière frappe, pas un booléen : un booléen
     * resterait allumé pour toujours si l'autre fermait l'application au milieu
     * d'une phrase. Celui qui lit décide lui-même qu'au-delà de quelques
     * secondes, la frappe est finie.
     */
    async frappe(id, actif) {
      const me = read(K.session, null);
      const list = read(K.convos, []);
      const c = list.find((x) => x.id === id);
      if (!c) return;
      c.frappe = { ...(c.frappe || {}), [me]: actif ? now() : 0 };
      write(K.convos, list);
      touch();
    },
  },

  messages: {
    subscribe(id, cb) {
      return onLocalChange(() => cb(read(K.messages, []).filter((m) => m.convoId === id).sort((a, b) => a.createdAt - b.createdAt)));
    },
    async send(id, texte, extra = {}) {
      const me = read(K.session, null);
      const list = read(K.messages, []);
      list.push({ id: uid(), convoId: id, auteurId: me, texte, createdAt: now(), ...extra });
      write(K.messages, list);
      const convos = read(K.convos, []);
      const c = convos.find((x) => x.id === id);
      if (c) {
        // l'aperçu d'une conversation doit se lire : un vocal n'a pas de texte
        c.dernier = texte || (extra.audio ? "🎤 Message vocal" : "");
        c.dernierAt = now();
        c.lu = Object.fromEntries(c.membres.map((m) => [m, m === me]));
        // j'ai forcément lu ce que je viens d'écrire, et je n'écris plus
        c.luA = { ...(c.luA || {}), [me]: now() };
        c.frappe = { ...(c.frappe || {}), [me]: 0 };
      }
      write(K.convos, convos);
      touch();
    },
    /* Corriger, retirer, réagir : les trois gestes qui manquaient.
       Un message RETIRÉ n'est pas effacé — il reste, vidé, avec sa marque. Le
       supprimer pour de bon ouvrirait un trou dans la conversation, et l'autre
       verrait un fil dont il manque un morceau sans savoir lequel. */
    async modifier(convoId, id, texte) {
      const list = read(K.messages, []);
      const m = list.find((x) => x.id === id && x.convoId === convoId);
      if (!m || m.auteurId !== read(K.session, null) || m.supprime) return;
      m.texte = texte;
      m.modifieLe = now();
      write(K.messages, list);
      touch();
    },
    async supprimer(convoId, id) {
      const list = read(K.messages, []);
      const m = list.find((x) => x.id === id && x.convoId === convoId);
      if (!m || m.auteurId !== read(K.session, null)) return;
      m.supprime = true;
      m.supprimeLe = now();
      m.texte = "";
      delete m.image;
      delete m.audio;
      delete m.duree;
      delete m.fichier;
      write(K.messages, list);

      /* Le mode démo suit la même règle que Firebase : l'aperçu de la
         conversation repart du dernier message ENCORE DEBOUT. Voir le pilote
         cloud pour le pourquoi. Deux pilotes qui divergent, c'est un défaut
         qu'on ne verra jamais au banc. */
      const convos = read(K.convos, []);
      const c = convos.find((x) => x.id === convoId);
      if (c) {
        const vivant = list
          .filter((x) => x.convoId === convoId && !x.supprime)
          .sort((a, b) => b.createdAt - a.createdAt)[0];
        c.dernier = vivant ? (vivant.texte || (vivant.audio ? "🎤 Message vocal" : "")) : "";
        c.dernierAt = vivant?.createdAt || now();
        write(K.convos, convos);
      }
      touch();
    },
    async reagir(convoId, id, cle) {
      const me = read(K.session, null);
      const list = read(K.messages, []);
      const m = list.find((x) => x.id === id && x.convoId === convoId);
      if (!m || !me) return;
      m.reactions = { ...(m.reactions || {}) };
      const voulue = R.reactionSure(cle);
      if (!voulue || m.reactions[me] === voulue) delete m.reactions[me];
      else m.reactions[me] = voulue;
      write(K.messages, list);
      touch();
    },
  },

  reports: {
    async add(data) {
      const list = read(K.reports, []);
      list.push({ id: uid(), createdAt: now(), parId: read(K.session, null), traite: false, ...data });
      write(K.reports, list);
      touch();
    },
    // réservé aux modérateurs : la vue vérifie le rôle avant d'appeler
    subscribe(cb) {
      return onLocalChange(() => cb(read(K.reports, []).slice().sort((a, b) => b.createdAt - a.createdAt)));
    },
    async traiter(id, decision) {
      const list = read(K.reports, []);
      const r = list.find((x) => x.id === id);
      if (!r) return;
      r.traite = true;
      r.decision = decision;
      r.traiteLe = now();
      r.traitePar = read(K.session, null);
      write(K.reports, list);
      touch();
    },
  },

  // ------------------------------------------------------------- présence
  /* Les appareils d'un membre — voir le pilote Firebase pour le pourquoi.
     En mode local il n'y a pas de serveur d'envoi : on accepte les dépôts sans
     rien en faire, pour que l'application se comporte pareil des deux côtés. */
  appareils: {
    async deposer() {},
    async retirer() {},
  },

  presence: {
    battre() {
      const me = read(K.session, null);
      if (!me) return;
      const p = read(K.presence, {});
      p[me] = now();
      write(K.presence, p);
      touch();
    },
    /* On rend DEUX choses : qui est là maintenant, et quand chacun a été vu
       pour la dernière fois. La seconde manquait — sans elle, impossible
       d'écrire « vu il y a deux heures » : on savait seulement qu'un membre
       n'était pas en ligne, jamais depuis quand. */
    subscribe(cb) {
      return onLocalChange(() => {
        const p = read(K.presence, {});
        const limite = now() - FENETRE_PRESENCE;
        cb({
          enLigne: new Set(Object.keys(p).filter((u) => p[u] > limite)),
          vuA: new Map(Object.entries(p)),
        });
      });
    },
    async partir() {
      const me = read(K.session, null);
      const p = read(K.presence, {});
      delete p[me];
      write(K.presence, p);
      touch();
    },
  },

  // --------------------------------------------------------------- stories
  stories: {
    subscribe(cb) {
      return onLocalChange(() => {
        const limite = now() - DUREE_STORY;
        cb(read(K.stories, []).filter((s) => s.createdAt > limite).sort((a, b) => a.createdAt - b.createdAt));
      });
    },
    async add(data) {
      const list = read(K.stories, []);
      list.push({ id: uid(), createdAt: now(), ...data });
      write(K.stories, list);
      touch();
    },
    async remove(id) {
      write(K.stories, read(K.stories, []).filter((s) => s.id !== id));
      touch();
    },
    /* Un statut se regarde en plein écran, seul : il n'a ni compteur ni bouton
       à côté. La réaction y est donc une CARTE `uid → réaction` posée sur le
       statut lui-même — l'auteur voit qui, les autres voient combien. */
    async reagir(id, cle) {
      const me = read(K.session, null);
      const list = read(K.stories, []);
      const s = list.find((x) => x.id === id);
      if (!s || !me) return;
      s.reactions = { ...(s.reactions || {}) };
      const voulue = R.reactionSure(cle);
      if (!voulue || s.reactions[me] === voulue) delete s.reactions[me];
      else s.reactions[me] = voulue;
      write(K.stories, list);
      touch();
    },
  },

  // ------------------------------------------------------------------ pages
  // Une page se distingue d'un groupe : elle a des ABONNÉS (et non des membres),
  // ses publications sont publiques, et seuls ses ADMINISTRATEURS y publient.
  /* ---------------------------------------------------------- événements
     Une église vit de ses rendez-vous : culte, veillée, convention, répétition.
     Un événement se rattache à une page ou à un groupe, ou à personne — il est
     alors public.

     La réponse d'un membre est « oui » ou « peut-être », jamais « non » : dire
     qu'on ne vient pas n'apprend rien à personne, et le silence le dit déjà. */
  evenements: {
    subscribe(cb) {
      return onLocalChange(() =>
        cb(read(K.evenements, []).slice().sort((a, b) => (a.quand || 0) - (b.quand || 0)))
      );
    },
    async get(id) {
      return read(K.evenements, []).find((e) => e.id === id) || null;
    },
    async creer(data) {
      const me = read(K.session, null);
      const id = uid();
      const liste = read(K.evenements, []);
      liste.push({ id, creeParId: me, participants: 0, createdAt: now(), ...data });
      write(K.evenements, liste);
      // celui qui organise participe : sans quoi un événement s'ouvre à zéro
      const reps = read(K.evtReponses, []);
      reps.push({ evtId: id, membreId: me, reponse: "oui", createdAt: now() });
      write(K.evtReponses, reps);
      const e = read(K.evenements, []).find((x) => x.id === id);
      if (e) e.participants = 1;
      write(K.evenements, read(K.evenements, []).map((x) => (x.id === id ? { ...x, participants: 1 } : x)));
      touch();
      return id;
    },
    async modifier(id, patch) {
      write(K.evenements, read(K.evenements, []).map((e) => (e.id === id ? { ...e, ...patch } : e)));
      touch();
    },
    async supprimer(id) {
      write(K.evenements, read(K.evenements, []).filter((e) => e.id !== id));
      write(K.evtReponses, read(K.evtReponses, []).filter((r) => r.evtId !== id));
      touch();
    },
    subscribeParticipants(id, cb) {
      return onLocalChange(() => {
        const users = read(K.users, {});
        cb(
          read(K.evtReponses, [])
            .filter((r) => r.evtId === id)
            .map((r) => ({ ...sanitize(users[r.membreId] || { uid: r.membreId, nom: "Membre" }), reponse: r.reponse }))
        );
      });
    },
    /** `reponse` vaut "oui", "peutetre", ou null pour se retirer. */
    async repondre(id, reponse) {
      const me = read(K.session, null);
      const reps = read(K.evtReponses, []).filter((r) => !(r.evtId === id && r.membreId === me));
      if (reponse) reps.push({ evtId: id, membreId: me, reponse, createdAt: now() });
      write(K.evtReponses, reps);
      const combien = reps.filter((r) => r.evtId === id && r.reponse === "oui").length;
      write(K.evenements, read(K.evenements, []).map((e) => (e.id === id ? { ...e, participants: combien } : e)));
      touch();
    },
  },
  pages: {
    subscribe(cb) {
      return onLocalChange(() => cb(read(K.pages, []).slice().sort((a, b) => b.createdAt - a.createdAt)));
    },
    /** Les pages que j'administre ou que je suis. */
    subscribeMiennes(cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        const liens = read(K.pageAbonnes, []).filter((l) => l.membreId === me);
        const pages = read(K.pages, []);
        cb(liens.map((l) => ({ ...pages.find((x) => x.id === l.pageId), monRole: l.role })).filter((x) => x.id));
      });
    },
    async get(id) {
      return read(K.pages, []).find((x) => x.id === id) || null;
    },
    subscribeAbonnes(id, cb) {
      return onLocalChange(() => {
        const users = read(K.users, {});
        cb(
          read(K.pageAbonnes, [])
            .filter((l) => l.pageId === id)
            .map((l) => ({ ...sanitize(users[l.membreId] || { uid: l.membreId, nom: "Membre" }), role: l.role }))
        );
      });
    },
    async creer(data) {
      const me = read(K.session, null);
      const id = uid();
      const pages = read(K.pages, []);
      pages.push({ id, creeParId: me, createdAt: now(), ...data });
      write(K.pages, pages);
      const liens = read(K.pageAbonnes, []);
      liens.push({ pageId: id, membreId: me, role: "admin", createdAt: now() });
      write(K.pageAbonnes, liens);
      touch();
      return id;
    },
    async modifier(id, patch) {
      const pages = read(K.pages, []);
      const page = pages.find((x) => x.id === id);
      if (!page) throw new Error("Page introuvable");
      Object.assign(page, patch);
      write(K.pages, pages);
      // le nom et le logo sont recopiés sur les publications déjà écrites
      const posts = read(K.posts, []).map((x) =>
        x.pageId === id ? { ...x, pageNom: page.nom, pageImage: page.image } : x
      );
      write(K.posts, posts);
      touch();
    },
    async suivre(id) {
      const me = read(K.session, null);
      const liens = read(K.pageAbonnes, []);
      if (!liens.some((l) => l.pageId === id && l.membreId === me)) {
        liens.push({ pageId: id, membreId: me, role: "abonne", createdAt: now() });
        write(K.pageAbonnes, liens);
        touch();
      }
    },
    async nePlusSuivre(id) {
      const me = read(K.session, null);
      // un administrateur ne se retire pas par ce chemin : il resterait sans responsable
      write(K.pageAbonnes, read(K.pageAbonnes, []).filter((l) => !(l.pageId === id && l.membreId === me && l.role !== "admin")));
      touch();
    },
    async supprimer(id) {
      write(K.pages, read(K.pages, []).filter((x) => x.id !== id));
      write(K.pageAbonnes, read(K.pageAbonnes, []).filter((l) => l.pageId !== id));
      write(K.posts, read(K.posts, []).filter((x) => x.pageId !== id));
      touch();
    },
  },

  // ---------------------------------------------------------------- groupes
  groupes: {
    subscribe(cb) {
      return onLocalChange(() => cb(read(K.groupes, []).slice().sort((a, b) => b.createdAt - a.createdAt)));
    },
    /** Les groupes dont je suis membre, avec mon rôle dedans. */
    subscribeMiens(cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        const liens = read(K.groupeMembres, []).filter((l) => l.membreId === me);
        const groupes = read(K.groupes, []);
        cb(liens.map((l) => ({ ...groupes.find((g) => g.id === l.groupeId), monRole: l.role })).filter((g) => g.id));
      });
    },
    async get(id) {
      return read(K.groupes, []).find((g) => g.id === id) || null;
    },
    subscribeMembres(id, cb) {
      return onLocalChange(() => {
        const users = read(K.users, {});
        cb(
          read(K.groupeMembres, [])
            .filter((l) => l.groupeId === id)
            .map((l) => ({ ...sanitize(users[l.membreId] || { uid: l.membreId, nom: "Membre" }), role: l.role }))
        );
      });
    },
    async creer(data) {
      const me = read(K.session, null);
      const id = uid();
      const groupes = read(K.groupes, []);
      groupes.push({ id, creeParId: me, createdAt: now(), ...data });
      write(K.groupes, groupes);
      const liens = read(K.groupeMembres, []);
      liens.push({ groupeId: id, membreId: me, role: "responsable", createdAt: now() });
      write(K.groupeMembres, liens);
      touch();
      return id;
    },
    async rejoindre(id) {
      const me = read(K.session, null);
      const liens = read(K.groupeMembres, []);
      if (!liens.some((l) => l.groupeId === id && l.membreId === me)) {
        liens.push({ groupeId: id, membreId: me, role: "membre", createdAt: now() });
        write(K.groupeMembres, liens);
        touch();
      }
    },
    async quitter(id) {
      const me = read(K.session, null);
      write(K.groupeMembres, read(K.groupeMembres, []).filter((l) => !(l.groupeId === id && l.membreId === me)));
      touch();
    },
    async supprimer(id) {
      write(K.groupes, read(K.groupes, []).filter((g) => g.id !== id));
      write(K.groupeMembres, read(K.groupeMembres, []).filter((l) => l.groupeId !== id));
      write(K.posts, read(K.posts, []).filter((p) => p.groupeId !== id));
      touch();
    },
  },

  // ----------------------------------------------------------------- compte
  compte: {
    /** Tout ce que la personne a produit, dans un seul objet (droit d'accès). */
    async exporter() {
      const me = read(K.session, null);
      const u = read(K.users, {})[me];
      if (!u) throw new Error("Non connecté");
      const mien = (liste, champ) => liste.filter((x) => x[champ] === me);
      return {
        exporteLe: new Date().toISOString(),
        profil: sanitize(u),
        publications: mien(read(K.posts, []), "authorId"),
        commentaires: mien(read(K.comments, []), "authorId"),
        statuts: mien(read(K.stories, []), "authorId"),
        messages: mien(read(K.messages, []), "auteurId"),
        amities: read(K.amis, []).filter((a) => a.membres.includes(me)),
        blocages: read(K.blocages, []).filter((b) => b.parId === me),
        notifications: read(K.notifs, []).filter((n) => n.pourId === me),
      };
    },
    /**
     * Efface le compte et tout ce qui en dépend.
     * Les messages privés sont conservés — ils appartiennent aussi à la
     * personne d'en face — mais deviennent anonymes, le profil ayant disparu.
     */
    async supprimer() {
      const me = read(K.session, null);
      if (!me) throw new Error("Non connecté");
      const mesPosts = new Set(read(K.posts, []).filter((p) => p.authorId === me).map((p) => p.id));
      write(K.posts, read(K.posts, []).filter((p) => p.authorId !== me));
      write(K.comments, read(K.comments, []).filter((c) => c.authorId !== me && !mesPosts.has(c.postId)));
      write(K.stories, read(K.stories, []).filter((x) => x.authorId !== me));
      write(K.amis, read(K.amis, []).filter((a) => !a.membres.includes(me)));
      write(K.blocages, read(K.blocages, []).filter((b) => b.parId !== me && b.cibleId !== me));
      write(K.notifs, read(K.notifs, []).filter((n) => n.pourId !== me && n.deId !== me));
      const amens = read(K.amens, {});
      for (const cle of Object.keys(amens)) if (cle.startsWith(me + "|")) delete amens[cle];
      write(K.amens, amens);
      const presence = read(K.presence, {});
      delete presence[me];
      write(K.presence, presence);
      const users = read(K.users, {});
      delete users[me];
      write(K.users, users);
      localStorage.removeItem(K.session);
      touch();
    },
  },

  // --------------------------------------------------------------- blocages
  // Un document par sens : « qui a bloqué qui ». On s'abonne aux deux sens
  // pour masquer symétriquement, comme le fait Facebook.
  blocages: {
    subscribe(cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        const liste = read(K.blocages, []).filter((b) => b.parId === me || b.cibleId === me);
        cb(new Set(liste.map((b) => (b.parId === me ? b.cibleId : b.parId))));
      });
    },
    // ceux que J'AI bloqués (pour pouvoir les débloquer)
    subscribeMiens(cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        cb(read(K.blocages, []).filter((b) => b.parId === me).map((b) => b.cibleId));
      });
    },
    async bloquer(uid) {
      const me = read(K.session, null);
      if (!me || me === uid) return;
      const liste = read(K.blocages, []);
      if (!liste.some((b) => b.parId === me && b.cibleId === uid))
        liste.push({ id: `${me}__${uid}`, parId: me, cibleId: uid, membres: [me, uid], createdAt: now() });
      write(K.blocages, liste);
      // un blocage rompt l'amitié : garder le lien n'aurait pas de sens
      write(K.amis, read(K.amis, []).filter((a) => !(a.membres.includes(me) && a.membres.includes(uid))));
      touch();
    },
    async debloquer(uid) {
      const me = read(K.session, null);
      write(K.blocages, read(K.blocages, []).filter((b) => !(b.parId === me && b.cibleId === uid)));
      touch();
    },
  },

  // ------------------------------------------------------------- modération
  moderation: {
    /** Comptes dont l'attestation de baptême attend une décision. */
    subscribeAValider(cb) {
      return onLocalChange(() =>
        cb(
          Object.values(read(K.users, {}))
            .filter((u) => u.validation === "en_attente")
            .sort((a, b) => a.createdAt - b.createdAt)
            .map(sanitize)
        )
      );
    },
    /** Régularise un nom — miroir du nuage : format du nom réel, horloge intacte. */
    async renommer(uid, nom) {
      const propre = S.nomReel(nom);
      const users = read(K.users, {});
      if (users[uid]) { users[uid].nom = propre; write(K.users, users); }
    },
    /**
     * Décision sur une attestation. Acceptée ou refusée, la PHOTO EST EFFACÉE :
     * c'est une donnée sensible, on ne la garde pas une fois qu'elle a servi.
     */
    async deciderBapteme(uid, accepte, motif = "") {
      const users = read(K.users, {});
      const u = users[uid];
      if (!u) throw new Error("Membre introuvable");
      u.validation = accepte ? "valide" : "refuse";
      u.baptemeDecide = { par: read(K.session, null), le: now(), motif: motif || "" };
      if (u.bapteme) delete u.bapteme.photo;
      write(K.users, users);
      touch();
    },
    async suspendre(uid, { jours, motif }) {
      const users = read(K.users, {});
      if (!users[uid]) throw new Error("Membre introuvable");
      users[uid].suspendu = jours ? now() + jours * 864e5 : Number.MAX_SAFE_INTEGER;
      users[uid].suspenduMotif = motif || "";
      users[uid].suspenduPar = read(K.session, null);
      users[uid].suspenduLe = now();
      write(K.users, users);
      touch();
    },
    /**
     * Certification d'un membre — le petit sceau d'or à côté de son nom.
     *
     * Réservée à la modération : un membre qui pourrait se certifier lui-même
     * rendrait le sceau sans valeur. C'est aussi ce que disent les règles du
     * serveur, et non seulement cet écran.
     */
    /** Le même sceau, sur une page — une église, un ministère, une chorale. */
    async certifierPage(id, oui = true) {
      const pages = read(K.pages, []);
      const pg = pages.find((x) => x.id === id);
      if (!pg) throw new Error("Page introuvable");
      if (oui) {
        pg.certifie = true;
        pg.certifiePar = read(K.session, null);
        pg.certifieLe = now();
      } else {
        delete pg.certifie;
        delete pg.certifiePar;
        delete pg.certifieLe;
      }
      write(K.pages, pages);
      touch();
    },
    async certifier(uid, oui = true) {
      const users = read(K.users, {});
      if (!users[uid]) throw new Error("Membre introuvable");
      if (oui) {
        users[uid].certifie = true;
        users[uid].certifiePar = read(K.session, null);
        users[uid].certifieLe = now();
      } else {
        delete users[uid].certifie;
        delete users[uid].certifiePar;
        delete users[uid].certifieLe;
      }
      write(K.users, users);
      touch();
    },
    /** Le mode démo se comporte comme le serveur : voir le pilote Firebase. */
    async supprimerCompte(uid) {
      const moi = read(K.session, null);
      write(K.posts, read(K.posts, []).filter((p) => p.authorId !== uid));
      write(K.stories, read(K.stories, []).filter((x) => x.authorId !== uid));
      const presence = read(K.presence, {});
      delete presence[uid];
      write(K.presence, presence);
      const users = read(K.users, {});
      // la fiche est REMPLACÉE, pas retouchée : rien de personnel ne survit
      users[uid] = { uid, supprime: now(), supprimePar: moi };
      write(K.users, users);
      touch();
    },
    async reactiver(uid) {
      const users = read(K.users, {});
      if (!users[uid]) return;
      delete users[uid].suspendu;
      delete users[uid].suspenduMotif;
      delete users[uid].suspenduPar;
      delete users[uid].suspenduLe;
      write(K.users, users);
      touch();
    },
    subscribeSuspendus(cb) {
      return onLocalChange(() =>
        cb(Object.values(read(K.users, {})).filter((u) => u.suspendu > now()).map(sanitize))
      );
    },
  },

  // ---------------------------------------------------------------- amitiés
  amis: {
    subscribe(cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        cb(read(K.amis, []).filter((a) => a.membres.includes(me)));
      });
    },
    async demander(autre) {
      const me = read(K.session, null);
      if (!me || me === autre) return;
      const list = read(K.amis, []);
      const id = convoId(me, autre);
      if (list.some((a) => a.id === id)) return;
      list.push({ id, membres: [me, autre].sort(), de: me, vers: autre, etat: "attente", createdAt: now() });
      write(K.amis, list);
      touch();
    },
    async accepter(autre) {
      const me = read(K.session, null);
      const list = read(K.amis, []);
      const r = list.find((a) => a.id === convoId(me, autre));
      // seul le destinataire de la demande peut l'accepter
      if (!r || r.vers !== me) return;
      r.etat = "acceptee";
      r.repondueAt = now();
      write(K.amis, list);
      touch();
    },
    // sert à refuser, annuler sa demande, ou retirer un ami
    /** Le tiroir d'un membre. En local, on le déduit des amitiés : pas besoin
        de dupliquer ce qui tient déjà dans un seul tableau. */
    subscribeAmisDe(uid, cb) {
      return onLocalChange(() =>
        cb(
          read(K.amis, [])
            .filter((a) => a.etat === "acceptee" && a.membres.includes(uid))
            .map((a) => a.membres.find((m) => m !== uid))
            .filter(Boolean)
        )
      );
    },
    async supprimer(autre) {
      const me = read(K.session, null);
      write(K.amis, read(K.amis, []).filter((a) => a.id !== convoId(me, autre)));
      touch();
    },
  },

  // --------------------------------------------------------------- abonnés
  /* Suivre quelqu'un sans être son ami. À sens unique — on s'abonne, on n'est
     pas accepté — et sans limite de nombre : c'est une audience, pas un cercle.
     Ici, un simple tableau { cible, uid } ; en Firebase, un tiroir rangé sous
     le membre suivi, pour que la règle puisse lire son réglage. */
  abonnes: {
    subscribeSuivi(uid, cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        cb(read(K.abonnes, []).some((a) => a.cible === uid && a.uid === me));
      });
    },
    async compter(uid) {
      return read(K.abonnes, []).filter((a) => a.cible === uid).length;
    },
    async abonner(uid) {
      const me = read(K.session, null);
      if (!me || me === uid) return;
      const liste = read(K.abonnes, []);
      if (liste.some((a) => a.cible === uid && a.uid === me)) return;
      liste.push({ cible: uid, uid: me, depuis: now() });
      write(K.abonnes, liste);
      touch();
    },
    async desabonner(uid) {
      const me = read(K.session, null);
      write(K.abonnes, read(K.abonnes, []).filter((a) => !(a.cible === uid && a.uid === me)));
      touch();
    },
    subscribeMiens(cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        cb(read(K.abonnes, []).filter((a) => a.uid === me).map((a) => a.cible));
      });
    },
    async vider() {
      const me = read(K.session, null);
      write(K.abonnes, read(K.abonnes, []).filter((a) => a.cible !== me));
      touch();
    },
  },

  // ------------------------------------------------------------- voisinage
  // Ce que les algorithmes de suggestion ont besoin de savoir du cercle proche.
  // Volontairement borné au voisinage : jamais « tout le graphe ».
  signaux: {
    /** Les amis acceptés de chacun de ces membres. Map<uid, Set<uid>>. */
    async amisDe(ids) {
      const cible = new Set(ids || []);
      const carte = new Map([...cible].map((id) => [id, new Set()]));
      for (const a of read(K.amis, [])) {
        if (a.etat !== "acceptee") continue;
        const [x, y] = a.membres;
        if (carte.has(x)) carte.get(x).add(y);
        if (carte.has(y)) carte.get(y).add(x);
      }
      return carte;
    },
    /** Les membres qui partagent au moins un groupe ou une page avec moi. */
    async coMembres() {
      const me = read(K.session, null);
      const mesGroupes = new Set(read(K.groupeMembres, []).filter((l) => l.membreId === me).map((l) => l.groupeId));
      const mesPages = new Set(read(K.pageAbonnes, []).filter((l) => l.membreId === me).map((l) => l.pageId));
      const compte = new Map();
      const ajouter = (id) => id !== me && compte.set(id, (compte.get(id) || 0) + 1);
      for (const l of read(K.groupeMembres, [])) if (mesGroupes.has(l.groupeId)) ajouter(l.membreId);
      for (const l of read(K.pageAbonnes, [])) if (mesPages.has(l.pageId)) ajouter(l.membreId);
      return compte;
    },
    /** Qui compose ces groupes (ou ces pages). Map<espaceId, Set<uid>>. */
    async membresDesEspaces(type, ids) {
      const cible = new Set(ids || []);
      const carte = new Map([...cible].map((id) => [id, new Set()]));
      const liens = type === "page" ? read(K.pageAbonnes, []) : read(K.groupeMembres, []);
      for (const l of liens) {
        const espace = type === "page" ? l.pageId : l.groupeId;
        if (carte.has(espace)) carte.get(espace).add(l.membreId);
      }
      return carte;
    },
  },

  // --------------------------------------------------------- notifications
  notifs: {
    subscribe(cb) {
      return onLocalChange(() => {
        const me = read(K.session, null);
        cb(read(K.notifs, []).filter((n) => n.pourId === me).sort((a, b) => b.createdAt - a.createdAt).slice(0, 50));
      });
    },
    async add(pourId, data) {
      const me = read(K.session, null);
      if (!pourId || pourId === me) return; // on ne se notifie pas soi-même
      const list = read(K.notifs, []);
      list.push({ id: uid(), pourId, lu: false, createdAt: now(), ...data });
      write(K.notifs, list.slice(-300));
      touch();
    },
    async toutLire() {
      const me = read(K.session, null);
      write(K.notifs, read(K.notifs, []).map((n) => (n.pourId === me ? { ...n, lu: true } : n)));
      touch();
    },
  },

  async upload(file, type = "image") {
    if (type === "video" || type === "audio" || type === "document") {
      const dataURL = await fichierEnDataURL(file);
      return dataURL; // ni la vidéo, ni le son, ni un PDF ne se compressent ici
    }
    const { dataURL } = await compressImage(file);
    return dataURL; // stocké tel quel : suffisant pour la démo locale
  },
};

// En local on met à jour les publications/commentaires de l'auteur après édition du profil.
function propagateAuthorLocal(id, u) {
  const posts = read(K.posts, []).map((p) => (p.authorId === id ? { ...p, auteurNom: u.nom, auteurPhoto: u.photo } : p));
  write(K.posts, posts);
  const cs = read(K.comments, []).map((c) => (c.authorId === id ? { ...c, auteurNom: u.nom, auteurPhoto: u.photo } : c));
  write(K.comments, cs);
}

const sanitize = ({ mdp, sel, empreinte, ...rest }) => rest;

/**
 * Ce qui ne vit PAS dans la fiche publique d'un membre.
 *
 * Les règles Firestore protègent des DOCUMENTS, jamais des champs : dès qu'un
 * membre a le droit de lire la fiche d'un autre — et il en a besoin, ne serait-ce
 * que pour son nom — il reçoit le document ENTIER. L'application retirait ces
 * champs avant de les afficher, ce qui ne protège de rien : un attaquant charge
 * le SDK Firebase avec la clé publique et lit la fiche brute.
 *
 * Ils vivent donc dans `users/{uid}/prive/contact`, à côté de l'attestation de
 * baptême, où seuls l'intéressé et la modération peuvent aller.
 */
const CHAMPS_PRIVES = ["telephone", "dateNaissance", "genre", "email"];

/**
 * Le jour et le mois d'une date de naissance — « 08-19 » — et rien d'autre.
 *
 * ⚠️ L'ANNÉE EST VOLONTAIREMENT PERDUE. La date complète est une donnée privée :
 * l'audit du 17 août 2026 l'a sortie de la fiche publique avec le téléphone et
 * le genre, et elle y reste. Pour souhaiter un anniversaire il suffit du jour ;
 * l'âge ne regarde personne.
 *
 * ⚠️ ET C'EST UNE CHAÎNE, PAS UNE DATE. Comparer « 08-19 » à « 08-19 » ne
 * demande ni fuseau horaire ni calcul : à Parakou comme ailleurs, l'anniversaire
 * tombe le jour que le calendrier local affiche, et rien d'autre.
 */
const jourEtMois = (dateNaissance) => {
  const t = String(dateNaissance || "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(t);
  return m ? `${m[2]}-${m[3]}` : "";
};

/**
 * Délai minimal entre deux actions, en secondes.
 *
 * ⚠️ Ces chiffres DOUBLENT ceux de `firestore.rules` (`cadenceRespectee`).
 * C'est le serveur qui décide ; ceux-ci ne servent qu'à annoncer au membre
 * combien de temps il lui reste. S'ils divergent, l'attente annoncée sera
 * fausse — pas la limite elle-même.
 */
const DELAIS_CADENCE = { post: 10, ami: 3, depot: 5, commentaire: 3, story: 10, notif: 2 };

/**
 * Le plafond d'amis. Même remarque que ci-dessus : c'est `firestore.rules` qui
 * l'applique, en tenant lui-même le compte dans `users/{uid}/compteurs/amis`.
 * Celui-ci ne sert qu'à reconnaître le refus et à en parler au membre.
 */
const LIMITE_AMIS = 1000;

/**
 * Combien d'amitiés d'avant les tiroirs on reprend par ouverture de session.
 *
 * Chacune coûte deux lectures et un lot : la règle du compteur n'en laisse
 * passer qu'une par lot (voir `rangerMesDonnees`). Dix, c'est assez pour que
 * la liste se remplisse sous les yeux du membre, et assez peu pour que
 * l'ouverture ne s'arrête pas dessus. Le reste vient à la session suivante.
 */
const REPRISES_PAR_SESSION = 10;

/**
 * Range les données d'un membre à la nouvelle place — une fois, en silence.
 *
 * Deux choses ont changé de forme le jour où le serveur s'est mis à appliquer
 * lui-même la confidentialité :
 *   · les coordonnées ont quitté la fiche publique pour `prive/contact` ;
 *   · toute publication doit déclarer son audience, sinon la règle de lecture
 *     n'a plus rien à quoi se raccrocher et la publication devient invisible.
 *
 * Personne n'a d'identifiants d'administration sur ce projet, et il n'en faut
 * pas : chacun a le droit de ranger SES propres données. Le rangement se fait
 * donc à l'ouverture de session, pour soi seul, sans rien demander au membre.
 * Il est sans effet la deuxième fois.
 */
async function rangerMesDonnees(D, db, uid, nomDuJeton = "") {
  try {
    const ref = D.doc(db, "users", uid);
    const snap = await D.getDoc(ref);

    /* ── RATTRAPER UNE INSCRIPTION INTERROMPUE ───────────────────────────────
       Un compte peut exister SANS fiche. Cela s'est produit tant que l'e-mail
       de confirmation partait avant elle : sur une connexion hésitante, l'appel
       restait pendu et la ligne suivante n'était jamais atteinte. L'attestation
       était déposée, le compte créé — mais `users/{uid}` n'existait pas.

       Ces membres-là étaient INVISIBLES : l'écran des baptêmes cherche les
       fiches en attente, et ils n'en avaient aucune. Personne ne pouvait les
       valider, et eux ne comprenaient pas pourquoi rien n'avançait.

       La cause est corrigée dans `signUp`. Ceci répare les comptes déjà
       abîmés : il suffit à la personne d'ouvrir l'application une fois. Rien
       n'est inventé — le nom vient de son compte d'authentification, l'église
       et la date de son attestation. Ce qui manque reste vide. */
    if (!snap.exists() && nomDuJeton.trim()) {
      const att = await D.getDoc(D.doc(db, "users", uid, "prive", "attestation")).catch(() => null);
      const bapteme = att?.exists() ? att.data() : null;
      const nom = nomDuJeton.trim();
      const [prenom, ...suite] = nom.split(" ");
      await D.setDoc(ref, {
        uid,
        nom,
        prenom,
        nomFamille: suite.join(" "),
        bio: "",
        ville: "",
        eglise: bapteme?.eglise || "",
        photo: "",
        couverture: "",
        role: "membre",
        createdAt: now(),
        validation: "en_attente",
        // sans la photo : elle reste dans le dossier privé, où elle doit être
        bapteme: {
          eglise: bapteme?.eglise || "",
          date: bapteme?.date || "",
          depose: bapteme?.depose || now(),
        },
      });
      return; // la fiche vient de naître : il n'y a rien à ranger derrière
    }

    if (snap.exists()) {
      const fiche = snap.data();
      const aDeplacer = {};
      const aEffacer = {};
      for (const champ of CHAMPS_PRIVES) {
        if (fiche[champ] !== undefined) {
          if (champ !== "email") aDeplacer[champ] = fiche[champ]; // l'e-mail vient du jeton
          aEffacer[champ] = D.deleteField();
        }
      }
      if (Object.keys(aEffacer).length) {
        if (Object.keys(aDeplacer).length)
          await D.setDoc(D.doc(db, "users", uid, "prive", "contact"), { ...aDeplacer, maj: now() }, { merge: true });
        await D.updateDoc(ref, aEffacer);
      }
    }

    /* ⚠️ LE RATTRAPAGE DES ANCIENNES FICHES — 19 août 2026.
       `neLe` est né avec les anniversaires ; les membres inscrits avant ne
       l'ont pas. Leur date complète est dans leur dossier privé, que SEUL leur
       propre appareil peut lire — c'est donc ici, à leur prochaine ouverture,
       et nulle part ailleurs, que la miette publique peut être posée.

       ⚠️ ON NE LIT LE DOSSIER PRIVÉ QUE SI LA MIETTE MANQUE. Une lecture de
       plus à chaque ouverture de l'application, pour cent membres, c'est cent
       lectures par jour pour rien. */
    try {
      const fraiche = await D.getDoc(ref);
      if (fraiche.exists() && !fraiche.data().neLe) {
        const contact = await D.getDoc(D.doc(db, "users", uid, "prive", "contact"));
        const jm = jourEtMois(contact.exists() ? contact.data().dateNaissance : "");
        if (jm) await D.updateDoc(ref, { neLe: jm });
      }
    } catch {
      // Pas de date, pas de droit, pas de réseau : on s'en passe. Un
      // anniversaire manquant ne doit rien empêcher.
    }

    /* Les publications sans audience déclarée : on les ouvre, car c'est ce
       qu'elles étaient avant que l'audience existe.

       Et, pour un COMPTE D'ACCUEIL, celles qui avaient été réservées aux amis :
       son compte est public depuis, et un nouveau venu doit pouvoir remonter
       tout ce qui a été écrit — pas seulement ce qui l'a été après le
       changement. Sans ce rattrapage, son fil s'arrêterait net à une date sans
       raison visible. */
    const ouvrirTout = compteDAccueil(uid);
    const q = D.query(D.collection(db, "posts"), D.where("authorId", "==", uid), D.limit(300));
    const posts = await D.getDocs(q);
    const aOuvrir = posts.docs.filter((d) => {
      const v = d.data().visibilite;
      return v === undefined || (ouvrirTout && v !== "tous");
    });
    if (aOuvrir.length) {
      // Firestore refuse un lot de plus de 500 écritures ; on reste sous la
      // limite en découpant, car la requête en ramène jusqu'à 300.
      const lot = D.writeBatch(db);
      aOuvrir.forEach((d) => lot.update(d.ref, { visibilite: "tous" }));
      await lot.commit();
    }

    /* ── LES STATUTS PÉRIMÉS S'EFFACENT POUR DE BON ──────────────────────
       Ils étaient MASQUÉS après vingt-quatre heures, jamais supprimés : la
       photo restait dans la base et dans le dépôt, indéfiniment, pour une chose
       que son auteur croyait éphémère. C'est le contraire de ce qu'un statut
       promet.

       Le remède annoncé partout est une fonction planifiée — donc la
       facturation activée. Elle n'est pas nécessaire : chacun efface les SIENS
       à l'ouverture de sa session, ce que les règles autorisent déjà. Ce qui
       traîne encore appartient à quelqu'un qui ne s'est pas connecté depuis, et
       partira dès qu'il le fera.

       ⚠️ Le fichier et les vues D'ABORD, le statut ensuite. Firestore n'efface
       pas les sous-collections avec leur parent, et le droit de lire les vues
       DÉCOULE du statut : une fois celui-ci parti, plus personne ne peut
       atteindre ce qu'il laisse derrière. C'est la leçon du trou n° 3. */
    try {
      const miens = await D.getDocs(
        D.query(D.collection(db, "stories"), D.where("authorId", "==", uid), D.limit(100))
      );
      const perimes = miens.docs.filter((d) => (d.data().createdAt || 0) <= now() - DUREE_STORY);
      for (const d of perimes) {
        const media = d.data().media;
        await cloud.oublierFichiers({ image: media, video: media }).catch(() => {});
        const vues = await D.getDocs(D.query(D.collection(db, "stories", d.id, "vues"), D.limit(500)));
        if (!vues.empty) {
          const lot = D.writeBatch(db);
          vues.forEach((v) => lot.delete(v.ref));
          await lot.commit();
        }
        await D.deleteDoc(d.ref);
      }
    } catch {
      // un ménage qui échoue n'empêche jamais d'entrer : il se refera demain
    }

    /* La photo de profil posée, mais jamais annoncée dans le fil.
     *
     * L'application annonce ce geste depuis toujours — mais l'échec de cette
     * annonce était avalé en silence, et un compte pas encore validé n'a pas le
     * droit de publier. Résultat : des membres ont une photo, et le fil ne l'a
     * jamais montrée.
     *
     * On ne peut pas écrire à leur place — les règles l'interdisent, et c'est
     * ce qui empêche d'usurper un membre. Chacun répare donc le sien, depuis
     * son propre compte, à sa prochaine ouverture de session. Une seule fois :
     * la présence d'une annonce suffit à ne pas recommencer.
     *
     * La date est celle d'aujourd'hui, faute de savoir quand la photo a été
     * posée — c'est une apparition dans le fil, pas une réécriture du passé.
     */
    /* Les amitiés d'avant n'ont pas de tiroir : elles ont été conclues quand
       tout vivait dans la collection commune. On les y range, à l'ouverture de
       session — chacun pour les siennes, ce que les règles autorisent puisque
       l'amitié existe bel et bien.
       Sans cette reprise, un membre ayant dix amis verrait une liste vide et
       croirait à une panne.

       ─────────────────────────────────────────────────────────────────────
       ⚠️ CE RANGEMENT N'A JAMAIS RIEN RANGÉ, du jour où le compteur d'amis est
       apparu jusqu'au 15 août 2026. Il écrivait les deux tiroirs en un seul
       lot, SANS TOUCHER AUX COMPTEURS — et la règle exige les deux ensemble.
       Chaque ouverture de session se terminait donc par un `permission-denied`
       avalé par le `catch`, et pas une amitié n'était reprise. Trois choses
       l'ont fait échouer, et les trois sont corrigées ici :

       1. LE COMPTEUR NE BOUGEAIT PAS. La règle demande
          `amisApres(uid) == amisAvant(uid) + 1` : sans `compterAmi`, refus.

       2. ON RÉÉCRIVAIT DES ENTRÉES DÉJÀ LÀ. `allow update: if false` — et
          c'est voulu : réécrire ferait monter le compteur deux fois pour un
          seul ami. On regarde donc AVANT, et on ne pose que ce qui manque.
          C'est aussi ce qui fait taire le journal de ceux dont tout est déjà
          en ordre — la quasi-totalité des membres.

       3. TOUT PARTAIT EN UN SEUL LOT. La règle vérifie
          `getAfter(compteurAmis(uid)).data.avec == autreId` : le compteur ne
          peut nommer QU'UNE entrée à la fois. Deux amitiés dans le même lot
          se contredisaient. On range donc UNE amitié par lot.

       ⚠️ ET ON S'ARRÊTE À `REPRISES_PAR_SESSION`. Un membre revenant avec
       cinquante amitiés d'avant n'a pas à attendre cinquante allers-retours
       avant de voir son fil : on en reprend quelques-unes, et la session
       suivante continue. La réparation converge au lieu de retenir l'ouverture. */
    try {
      const mesLiens = await D.getDocs(
        D.query(D.collection(db, "amities"), D.where("membres", "array-contains", uid), D.limit(500))
      );
      const amis = mesLiens.docs
        .map((d) => d.data())
        .filter((a) => a.etat === "acceptee")
        .map((a) => (a.membres || []).find((m) => m !== uid))
        .filter(Boolean);

      let reprises = 0;
      for (const autre of amis) {
        if (reprises >= REPRISES_PAR_SESSION) break;

        const mien = D.doc(db, "users", uid, "amis", autre);
        const sien = D.doc(db, "users", autre, "amis", uid);
        /* Le tiroir de l'autre nous est lisible : nous sommes son ami accepté,
           et la règle de lecture le dit. */
        const [aMoi, aLui] = await Promise.all([
          D.getDoc(mien).catch(() => null),
          D.getDoc(sien).catch(() => null),
        ]);
        if (aMoi?.exists() && aLui?.exists()) continue;   // rien à faire

        const lot = D.writeBatch(db);
        if (!aMoi?.exists()) {
          lot.set(mien, { uid: autre, depuis: now() });
          cloud.compterAmi(D, db, lot, uid, autre, 1);
        }
        if (!aLui?.exists()) {
          lot.set(sien, { uid, depuis: now() });
          cloud.compterAmi(D, db, lot, autre, uid, 1);
        }
        /* Une amitié qui résiste ne doit pas emporter les suivantes : le
           tiroir de l'autre peut être plein, ou sa fiche avoir disparu. */
        await lot.commit().catch((e) => {
          console.warn("Amitié non reprise :", autre, e?.code || e);
        });
        reprises++;
      }
    } catch (e) {
      console.warn("Rangement des amis reporté :", e?.code || e?.message || e);
    }

    const fiche = snap.exists() ? snap.data() : null;
    const dejaAnnoncee = posts.docs.some((d) => d.data().type === "photo_profil");
    if (fiche?.photo && typeof fiche.photo === "string" && !dejaAnnoncee) {
      const lot = D.writeBatch(db);
      lot.set(D.doc(D.collection(db, "posts")), {
        type: "photo_profil",
        authorId: uid,
        auteurNom: fiche.nom || "Membre",
        auteurPhoto: fiche.photo,
        texte: "",
        image: fiche.photo,
        amens: 0,
        commentaires: 0,
        partages: 0,
        visibilite: fiche.confidentialite?.publications === "amis" ? "amis" : "tous",
        createdAt: now(),
      });
      lot.set(D.doc(db, "cadences", uid, "actions", "post"), { le: D.serverTimestamp() });
      await lot.commit();
    }
  } catch (e) {
    // Un rangement qui échoue ne doit jamais empêcher d'entrer : il se
    // retentera à la prochaine ouverture.
    console.warn("Rangement des données reporté :", e?.code || e?.message || e);
  }
}

/**
 * Mes amis, tenus à jour côté pilote.
 *
 * Depuis que le serveur applique lui-même l'audience des publications, le fil ne
 * peut plus poser une requête large : Firestore refuserait l'ENSEMBLE dès qu'une
 * publication réservée s'y trouve. L'application demande donc séparément ce qui
 * est public et ce que ses amis ont réservé — et pour cela il lui faut savoir
 * qui sont ses amis, au moment de composer la requête.
 *
 * Firestore plafonne `in` à 30 valeurs : au-delà, on découpe.
 */
/**
 * ⚠️ QUINZE, ET NON TRENTE — et ce n'est pas de la prudence.
 *
 * Firestore plafonne `in` à 30 valeurs, mais ce n'est pas cette limite-là qui
 * mord. La règle de lecture interroge la base pour CHAQUE auteur nommé
 * (`exists` sur le tiroir d'amis, puis sur celui des abonnés), et le nombre
 * d'appels qu'une requête supporte tombe entre vingt et trente CHEMINS
 * DISTINCTS. Mesuré, pas supposé : vingt auteurs passent, trente non — le banc
 * garde les deux essais.
 *
 * Deux publications du même auteur ne coûtent qu'un appel : le résultat est
 * gardé. C'est donc le nombre d'AUTEURS par requête qui compte, jamais celui
 * des publications rapportées.
 *
 * Si vous remontez ce chiffre, l'essai « un fil de trente auteurs tombe »
 * tombera avec lui.
 */
const PAQUET_IN = 15;
let mesAmis = [];
/* Ceux à qui je me suis abonné. Ils ouvrent leurs publications OUVERTES À TOUS,
   jamais celles qu'ils réservent à leurs amis — la règle le tient, et la
   requête des abonnements porte donc toujours `visibilite == 'tous'`. */
let mesAbonnements = [];
/* Ceux qui veulent être prévenus quand cette liste change. L'enveloppe s'en
   sert pour filtrer ce qui arrive, et le mode local pour se comporter comme le
   serveur — sans quoi la démonstration mentirait sur ce que voit un arrivant. */
const ecouteursAbonnements = new Set();
const poserAbonnements = (liste) => {
  mesAbonnements = liste;
  ecouteursAbonnements.forEach((f) => f(liste));
};
const paquets = (liste) => {
  const out = [];
  for (let i = 0; i < liste.length; i += PAQUET_IN) out.push(liste.slice(i, i + PAQUET_IN));
  return out;
};

/**
 * Les auteurs dont je peux voir les publications réservées : mes amis, ET MOI.
 *
 * S'oublier soi-même ici est une erreur qui ne se voit pas en relisant la règle
 * de lecture — laquelle autorise bien l'auteur à lire ce qu'il a écrit. Mais une
 * règle n'est pas une requête : la requête ne demandait que les publications de
 * mes amis, donc les miennes ne revenaient jamais, et le filtre côté application
 * n'avait rien à laisser passer.
 *
 * Conséquence, tant que ça a duré : un membre qui réservait ses publications à
 * ses amis ne les voyait pas dans son propre fil.
 */
const auteursReserves = () => (cloud._uid ? [cloud._uid, ...mesAmis] : mesAmis);

/**
 * Mon cercle : moi, mes amis, et ceux à qui je me suis abonné.
 *
 * ⚠️ C'EST LA SEULE PORTE D'ENTRÉE DU FIL depuis la cinquième passe. Koinonia
 * n'est plus une place publique : un membre qui arrive ne voit rien, et le
 * serveur le tient. La règle de lecture des publications n'est même pas
 * ÉVALUABLE sur une requête qui ne nomme pas ses auteurs — elle rend
 * « Property authorId is undefined » et refuse tout.
 *
 * Autrement dit : ce n'est pas un filtre de confort qu'on pourrait retirer pour
 * « voir plus ». Sans lui, il n'y a pas de fil du tout.
 */
const monCercle = () => {
  const vus = new Set();
  const sortie = [];
  for (const id of [cloud._uid, ...mesAmis, ...mesAbonnements]) {
    if (!id || vus.has(id)) continue;
    vus.add(id);
    sortie.push(id);
  }
  return sortie;
};

/** Ceux dont je ne vois QUE le public : mes abonnements qui ne sont pas amis.
    (Retiré pendant les quatre jours du fil ouvert, revenu avec la onzième
    passe : s'abonner est de nouveau la clé du fil.) */
const mesSuivis = () => mesAbonnements.filter((id) => id !== cloud._uid && !mesAmis.includes(id));

/** Moi et mes amis — ceux dont je vois tout ce qui m'est destiné. */
const mesProches = () => (cloud._uid ? [cloud._uid, ...mesAmis] : [...mesAmis]);

const separerPrive = (objet) => {
  const prive = {};
  const publique = {};
  for (const [cle, valeur] of Object.entries(objet || {})) {
    (CHAMPS_PRIVES.includes(cle) ? prive : publique)[cle] = valeur;
  }
  return { prive, publique };
};
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

/* ========================================================= IMPLÉMENTATION FIREBASE */

/**
 * Où renvoyer le membre après qu'il a cliqué dans son e-mail.
 *
 * Sans cette adresse, Firebase affiche sa propre page de confirmation — en
 * anglais, aux couleurs de Google — et le membre reste planté là, sans rien
 * pour revenir. Avec, il retrouve un bouton vers Koinonia ; et si l'URL d'action
 * est personnalisée dans la console (voir action.html), il y revient tout seul.
 */
const RETOUR_APRES_EMAIL = {
  url: (APP?.urlPublique || window.location.origin) + "/",
  handleCodeInApp: false,
};

const cloud = {
  _uid: null,

  auth: {
    /**
     * Suit l'authentification ET la fiche du membre, en direct.
     *
     * `onAuthStateChanged` seul ne se déclenche qu'à la connexion et à la
     * déconnexion. Lire la fiche une seule fois à ce moment-là fige le rôle et
     * l'état de validation pour toute la session : un membre validé par un
     * responsable restait bloqué jusqu'à ce qu'il se reconnecte, sans comprendre
     * pourquoi. On écoute donc aussi le document.
     */
    onChange(cb) {
      let stopAuth = () => {};
      let stopFiche = () => {};
      let stopAmis = () => {};
      FB.sdk().then(({ A, auth, D, db }) => {
        stopAuth = A.onAuthStateChanged(auth, (user) => {
          stopFiche();
          stopFiche = () => {};
          cloud._uid = user ? user.uid : null;
          if (!user) {
            stopAmis();
            stopAmis = () => {};
            mesAmis = [];
            poserAbonnements([]);
            return cb(null);
          }
          // Rangement silencieux, une fois par ouverture de session.
          rangerMesDonnees(D, db, user.uid, user.displayName || "");

          // Qui sont mes amis — nécessaire pour composer la requête du fil.
          stopAmis();
          stopAmis = cloud.amis.subscribe((rs) => {
            mesAmis = (rs || [])
              .filter((r) => r.etat === "acceptee")
              .map((r) => (r.membres || []).find((m) => m !== user.uid))
              .filter(Boolean);
          });

          /* Et à qui je me suis abonné. Une requête de GROUPE DE COLLECTIONS :
             mes abonnements sont éparpillés sous chacun de ceux que je suis
             (`users/{eux}/abonnes/{moi}`), il n'y a pas de liste à moi.
             Comme les amis, c'est nécessaire pour composer la requête du fil —
             sans cette liste, un abonnement n'apporterait rien à lire. */
          D.onSnapshot(
            D.query(D.collectionGroup(db, "abonnes"), D.where("uid", "==", user.uid), D.limit(500)),
            (snap) =>
              poserAbonnements(
                snap.docs.map((d) => d.ref.parent.parent?.id).filter((id) => id && id !== user.uid)
              ),
            () => poserAbonnements([])
          );

          // Le contact privé est lu UNE fois par ouverture de session : il ne
          // change presque jamais, et l'écouter en continu coûterait une lecture
          // permanente pour rien.
          let contact = null;
          D.getDoc(D.doc(db, "users", user.uid, "prive", "contact"))
            .then((s) => { contact = s.exists() ? s.data() : {}; })
            .catch(() => { contact = {}; });

          stopFiche = D.onSnapshot(
            D.doc(db, "users", user.uid),
            (snap) => {
              const base = snap.exists()
                ? { uid: user.uid, ...snap.data() }
                : { uid: user.uid, nom: user.displayName || "Membre" };
              // `email` vient du jeton d'authentification : il n'a jamais besoin
              // d'être recopié dans la base.
              cb({ ...base, ...(contact || {}), email: user.email, emailVerifie: user.emailVerified });
            },
            // fiche illisible (compte supprimé, réseau coupé) : on rend au moins
            // ce que le jeton d'authentification contient, plutôt que rien
            () => cb({ uid: user.uid, nom: user.displayName || "Membre", email: user.email, emailVerifie: user.emailVerified })
          );
        });
      });
      return () => {
        stopFiche();
        stopAmis();
        stopAuth();
      };
    },
    async signUp(donnees) {
      let { email, mdp, ville, eglise } = donnees;
      email = S.emailSur(email);
      S.motDePasseSur(mdp);
      const identite = validerIdentite(donnees);
      ville = S.texte(ville, S.LIMITES.ville, { champ: "La ville", requis: true });
      eglise = S.texte(eglise, S.LIMITES.eglise, { champ: "L'église", requis: true });
      S.cadence("inscription");
      const { A, auth, D, db } = await FB.sdk();
      const cred = await A.createUserWithEmailAndPassword(auth, email, mdp);

      /* ⚠️ LA FICHE AVANT TOUT LE RESTE — 20 août 2026.
         Elle partait après `updateProfile`, après le contact privé, après
         l'attestation. Chacune de ces attentes est une occasion, sur une 4G de
         Parakou, que l'appel reste pendu et que la suite ne soit jamais
         atteinte. Le compte existe alors dans l'authentification, et
         `users/{uid}` n'existe pas.

         Un tel membre est INVISIBLE : l'écran de modération cherche les fiches
         en attente, il n'en a aucune. Il n'a pas de nom, puisque le nom vit
         dans la fiche. Personne ne peut le valider, et lui ne comprend pas
         pourquoi rien n'avance.

         ⚠️ ET LE RATTRAPAGE NE LE SAUVAIT PAS. `rangerMesDonnees` sait
         reconstruire une fiche manquante — mais SEULEMENT à partir du nom posé
         par `updateProfile`. Si la coupure survient AVANT lui, il n'y a ni
         fiche ni nom, et rien à quoi se raccrocher. Le compte est perdu sans
         recours. C'est arrivé à une inscrite le 20 août 2026, repérée par
         Josaphat : présente dans l'authentification, absente de `users`.

         La fiche est donc désormais la PREMIÈRE écriture après la création du
         compte. Tout ce qui suit peut échouer sans rendre le membre invisible :
         il a un nom, il apparaît en modération, et sa prochaine ouverture de
         l'application range le reste. */
      const profilInitial = {
        uid: cred.user.uid,
        nom: identite.nom,
        prenom: identite.prenom,
        nomFamille: identite.nomFamille,
        bio: "", ville: ville || "", eglise: eglise || "", photo: "", couverture: "",
        role: "membre", createdAt: now(),
        /* ⚠️ UN TROISIÈME ÉTAT, ET IL CHANGE L'ORDRE DES CHOSES — 24 août 2026.
           La fiche naissait en `en_attente`, donc DANS LA FILE DU MODÉRATEUR,
           avant même que l'adresse soit confirmée. Josaphat pouvait ainsi
           approuver quelqu'un qui n'avait jamais ouvert son courriel — et sa
           file se remplissait de comptes dont nul ne savait s'ils étaient
           réels.

           Trois états désormais :
             · `email`      — inscrit, adresse non confirmée. Invisible du
                              modérateur ; le membre voit l'écran du code.
             · `en_attente` — adresse confirmée. C'est le WORKER qui l'y fait
                              passer, en acceptant le code, et c'est à cet
                              instant que le compte entre dans la file.
             · `valide`     — approuvé.

           ⚠️ ET PERSONNE NE PEUT SAUTER L'ÉTAPE. `validation` figure dans la
           liste des champs qu'un membre ne peut pas écrire sur sa propre fiche
           (voir `firestore.rules`) : seul un serveur de confiance la déplace. */
        validation: "email",
        neLe: jourEtMois(identite.dateNaissance),
        bapteme: identite.bapteme,
      };
      await D.setDoc(D.doc(db, "users", cred.user.uid), profilInitial);

      await A.updateProfile(cred.user, { displayName: identite.nom });
      /* ⚠️ IL N'Y A PLUS D'ATTESTATION À DÉPOSER — 18 août 2026.
         Elle s'écrivait ici, dans `users/{uid}/prive/attestation`. Le dossier
         d'admission tient désormais tout entier dans la fiche : la réponse au
         baptême et le nom de celui qui a fait connaître Koinonia. Rien de
         sensible ne transite plus, donc rien de sensible n'est conservé.

         ⚠️ LES ATTESTATIONS DÉJÀ DÉPOSÉES RESTENT EN BASE. Ce code ne les
         efface pas — un effacement en masse ne se déclenche pas depuis une
         inscription. Elles doivent être supprimées à part. */

      // La fiche publique et le contact privé partent séparés. Ce qui reste
      // dans `users/{uid}` est ce que tout membre a besoin de voir : un nom, une
      // photo, une église, une ville.
      /* ⚠️ LA FICHE EST DÉJÀ ÉCRITE, PLUS HAUT — voir le long commentaire au
         début de cette fonction. Il ne reste ici que le CONTACT PRIVÉ :
         téléphone, date de naissance, genre, adresse. `separerPrive` fait le
         tri, et la fiche publique n'en reçoit rien.

         ⚠️ ET SI CETTE ÉCRITURE ÉCHOUE, LE MEMBRE EXISTE QUAND MÊME. C'est
         tout l'intérêt du nouvel ordre : il a un nom, il apparaît en
         modération, et sa prochaine ouverture de l'application range ce qui
         manque (`rangerMesDonnees`). Avant, une coupure ici le rendait
         invisible pour toujours. */
      const { prive } = separerPrive({ email, ...identite });
      await D.setDoc(D.doc(db, "users", cred.user.uid, "prive", "contact"), { ...prive, maj: now() });
      /* ⚠️ PLUS D'E-MAIL À LIEN — 20 octobre 2026, à la suite de l'audit.
         Firebase envoyait ici son lien de confirmation, ALORS QUE seul le
         CODE fait entrer le dossier dans la file (`email` → `en_attente`,
         posé par le Worker en acceptant le code — jamais par le lien).

         Le membre qui cliquait le lien — le geste le plus naturel devant un
         e-mail — était donc confirmé chez Google, l'encart du code
         disparaissait (« il ne reste plus qu'à attendre »)… et son dossier
         n'arrivait JAMAIS chez le responsable, qui ne cherche que
         `en_attente`. Bloqué à vie, invisible. L'ironie : le commentaire de
         `salle-attente.js` documentait ce piège comme raison d'avoir retiré
         le bouton « Renvoyer le lien » — mais l'envoi initial était resté.

         La salle d'attente propose le code dès l'arrivée ; il n'y a rien
         d'autre à envoyer d'ici. (Elle sait aussi, depuis le même jour,
         rattraper un membre déjà piégé : tant que la fiche dit `email`,
         l'encart du code reste, quoi qu'en dise le jeton.) */
      return profilInitial;
    },
    async signIn({ email, mdp }) {
      S.cadence("connexion");
      const { A, auth } = await FB.sdk();
      await A.signInWithEmailAndPassword(auth, S.emailSur(email), mdp);
      S.oublier("connexion");
    },
    /**
     * Redemande une carte d'identité neuve, sans déconnecter personne.
     *
     * ⚠️ POURQUOI CETTE FONCTION EXISTE — 20 août 2026.
     * Un jeton Firebase est FIGÉ à la seconde où il est délivré, et il vit une
     * heure. Il porte `email_verified`. Quelqu'un qui se connecte, PUIS confirme
     * son adresse, garde donc pendant une heure un jeton qui dit « non
     * confirmée » — et le serveur le croit, parce que c'est le jeton qui fait
     * foi, jamais la base.
     *
     * Le membre, lui, a tout bien fait : il a cliqué dans son courriel, l'écran
     * lui dit que c'est confirmé, et pourtant réagir échoue. On lui répondait
     * « déconnectez-vous puis reconnectez-vous ». C'est une réponse d'aveu
     * d'impuissance, et Josaphat a signalé le 20 août 2026 qu'elle ne marchait
     * même pas toujours — une déconnexion qui laisse une session en cache rend
     * parfois le MÊME jeton.
     *
     * `reload()` relit le compte chez Google ; `getIdToken(true)` force la
     * délivrance d'une carte neuve. Deux appels, aucune déconnexion, et la
     * personne ne voit rien passer.
     */
    async rafraichirSession() {
      const { auth } = await FB.sdk();
      const user = auth.currentUser;
      if (!user) return false;
      await user.reload().catch(() => {});
      await user.getIdToken(true);
      return true;
    },
    async signOut() {
      const { A, auth } = await FB.sdk();
      await A.signOut(auth);
    },
    /**
     * « Mot de passe oublié ». Le lien reçu retombe sur action.html, qui demande
     * le nouveau mot de passe.
     *
     * On ne dit JAMAIS si l'adresse existe : sinon ce formulaire deviendrait un
     * moyen commode de savoir qui est membre de Koinonia — donc, ici, qui est
     * chrétien. Le message est le même dans tous les cas.
     */
    async reinitialiserMotDePasse(email) {
      const { A, auth } = await FB.sdk();
      const adresse = S.emailSur(email);
      try {
        await A.sendPasswordResetEmail(auth, adresse, RETOUR_APRES_EMAIL);
      } catch (e) {
        const code = e?.code || "";
        if (code.includes("user-not-found")) return; // silence volontaire
        if (code.includes("too-many-requests"))
          throw new S.ErreurValidation("Trop de demandes. Attendez quelques minutes.");
        throw e;
      }
    },
    async changerMotDePasse(ancien, nouveau) {
      const { A, auth } = await FB.sdk();
      const user = auth.currentUser;
      if (!user) throw new Error("Non connecté");
      // Firebase exige une authentification récente pour toucher au mot de passe :
      // on se sert de l'ancien mot de passe pour la refaire à l'instant.
      const identifiant = A.EmailAuthProvider.credential(user.email, ancien);
      try {
        await A.reauthenticateWithCredential(user, identifiant);
      } catch {
        throw new S.ErreurValidation("Mot de passe actuel incorrect.");
      }
      await A.updatePassword(user, nouveau);
    },
    async envoyerVerification() {
      const { A, auth } = await FB.sdk();
      if (!auth.currentUser) throw new Error("Non connecté");
      try {
        await A.sendEmailVerification(auth.currentUser, RETOUR_APRES_EMAIL);
      } catch (e) {
        const code = e?.code || "";
        if (code.includes("too-many-requests"))
          throw new S.ErreurValidation("Trop de demandes. Attendez quelques minutes avant de réessayer.");
        if (code.includes("unauthorized-continue-uri"))
          throw new S.ErreurValidation(
            "L'adresse de retour n'est pas autorisée dans Firebase (Authentication → Settings → Domaines autorisés)."
          );
        throw e;
      }
    },
    /**
     * Relit l'état côté serveur : le membre vient de cliquer dans son e-mail.
     *
     * `reload()` rafraîchit l'objet local, mais PAS le jeton envoyé à Firestore
     * et à Storage — or c'est lui qui porte `email_verified`, et les règles ne
     * regardent que lui. Sans le second appel, le membre voit son adresse
     * confirmée à l'écran et se fait pourtant refuser toutes ses écritures,
     * jusqu'à l'expiration naturelle du jeton (une heure).
     */
    async rafraichirVerification() {
      const { auth } = await FB.sdk();
      if (!auth.currentUser) return false;
      await auth.currentUser.reload();
      await auth.currentUser.getIdToken(true);
      return auth.currentUser.emailVerified;
    },

    /* ══════════════ LA VÉRIFICATION PAR CODE — 23 août 2026 ══════════════
       Demandée par Josaphat : « au lieu des liens de vérification, est-ce que
       c'est possible de recevoir un code ? Ça fait plus professionnel. »

       ⚠️ FIREBASE NE SAIT PAS LE FAIRE, et ce n'est pas un réglage oublié :
       il n'envoie que des liens. Sur ce projet, ses modèles sont même
       verrouillés — l'API répond `EMAIL_TEMPLATE_UPDATE_NOT_ALLOWED`, constaté
       le 21 août après trois heures perdues dans la console.

       ⚠️ TOUT LE TRAVAIL EST AILLEURS. Ces deux fonctions ne font que porter
       la voix : elles appellent le Worker `koinonia-pousser`, qui tire le
       code, l'envoie par Brevo, le vérifie, et pose `emailVerified`. Elles ne
       décident de rien.

       ⚠️ ET C'EST VOULU QUE LE JETON SEUL SERVE D'IDENTITÉ. On n'envoie ni
       l'adresse ni l'identifiant : le serveur les lit dans la signature de
       Google. Les envoyer, ce serait laisser l'appelant choisir à qui part le
       code — donc se faire vérifier le compte d'un autre. */

    /** Demande l'envoi d'un code à six chiffres, à l'adresse du compte. */
    async demanderCodeEmail() {
      const url = APP?.urlPousser;
      if (!url) throw new S.ErreurValidation("Service indisponible.");
      const { auth } = await FB.sdk();
      const jeton = await auth.currentUser?.getIdToken?.();
      if (!jeton) throw new S.ErreurValidation("Reconnectez-vous.");

      const reponse = await fetch(`${url}/code/demander`, {
        method: "POST",
        headers: { Authorization: `Bearer ${jeton}`, "Content-Type": "application/json" },
        body: "{}",
      });
      const dit = await reponse.json().catch(() => ({}));
      if (!reponse.ok) throw new S.ErreurValidation(dit.erreur || "Envoi impossible.");
      return dit;   // { envoye: true } ou { deja: true }
    },

    /**
     * Envoie le code saisi. Si le serveur l'accepte, il pose `emailVerified`.
     *
     * ⚠️ ON RAFRAÎCHIT LE JETON JUSTE APRÈS, ET C'EST INDISPENSABLE.
     * Le serveur a posé le drapeau chez Google, mais le jeton que porte cette
     * session date d'AVANT : il dit encore « adresse non confirmée », et les
     * règles ne regardent que lui. Sans ce rafraîchissement, le membre verrait
     * « c'est vérifié » et se ferait refuser sa première publication — la même
     * panne que celle du 20 août, dans l'autre sens.
     */
    async verifierCodeEmail(code) {
      const url = APP?.urlPousser;
      if (!url) throw new S.ErreurValidation("Service indisponible.");
      const propre = String(code || "").replace(/[^0-9]/g, "");
      if (propre.length !== 6) throw new S.ErreurValidation("Le code compte six chiffres.");

      const { auth } = await FB.sdk();
      const jeton = await auth.currentUser?.getIdToken?.();
      if (!jeton) throw new S.ErreurValidation("Reconnectez-vous.");

      const reponse = await fetch(`${url}/code/verifier`, {
        method: "POST",
        headers: { Authorization: `Bearer ${jeton}`, "Content-Type": "application/json" },
        body: JSON.stringify({ code: propre }),
      });
      const dit = await reponse.json().catch(() => ({}));
      if (!reponse.ok) throw new S.ErreurValidation(dit.erreur || "Code refusé.");

      await cloud.auth.rafraichirVerification();
      return true;
    },
  },

  users: {
    async get(id) {
      const { D, db } = await FB.sdk();
      const s = await D.getDoc(D.doc(db, "users", id));
      if (!s.exists()) return null;
      const fiche = { uid: id, ...s.data() };
      // Sa propre fiche est rendue complète ; celle d'un autre ne porte pas ses
      // coordonnées, et ce n'est pas l'application qui les retire — elles ne
      // sont tout simplement pas dans le document.
      if (id !== cloud._uid) return fiche;
      const p = await D.getDoc(D.doc(db, "users", id, "prive", "contact")).catch(() => null);
      return p?.exists() ? { ...fiche, ...p.data() } : fiche;
    },
    async update(patch) {
      const { D, db } = await FB.sdk();
      const id = cloud._uid;
      // Les coordonnées ne retournent jamais dans la fiche publique, même si un
      // écran les envoie par mégarde : l'aiguillage est ici, pas dans l'écran.
      const { prive, publique } = separerPrive(patch);
      if (Object.keys(prive).length)
        await D.setDoc(D.doc(db, "users", id, "prive", "contact"), { ...prive, maj: now() }, { merge: true });

      // 1. La fiche d'abord, seule. C'est elle qui compte : si la suite échoue,
      //    le membre a quand même sa nouvelle photo.
      if (Object.keys(publique).length) await D.updateDoc(D.doc(db, "users", id), publique);

      if (!patch.nom && patch.photo === undefined) return;

      // 2. Puis on recopie nom et photo sur ce qui a déjà été écrit. L'auteur
      //    est dénormalisé dans les publications, les commentaires et les
      //    statuts : sans cette reprise, une nouvelle photo n'apparaîtrait que
      //    sur ce qui sera publié ensuite.
      //
      //    Cette reprise ne doit JAMAIS faire échouer l'enregistrement du
      //    profil : elle est longue, plafonnée, et le membre n'y peut rien.
      try {
        const reprendre = async (collection, champAuteur) => {
          const q = D.query(D.collection(db, collection), D.where(champAuteur, "==", id), D.limit(300));
          const snap = await D.getDocs(q);
          if (snap.empty) return;
          const lot = D.writeBatch(db);
          snap.forEach((d) =>
            lot.update(d.ref, {
              auteurNom: patch.nom ?? d.data().auteurNom,
              auteurPhoto: patch.photo ?? d.data().auteurPhoto,
            })
          );
          await lot.commit();
        };
        await reprendre("posts", "authorId");
        await reprendre("stories", "authorId");
        // les commentaires vivent sous chaque publication : on passe par le groupe
        const q = D.query(D.collectionGroup(db, "commentaires"), D.where("authorId", "==", id), D.limit(300));
        const snap = await D.getDocs(q);
        if (!snap.empty) {
          const lot = D.writeBatch(db);
          snap.forEach((d) =>
            lot.update(d.ref, {
              auteurNom: patch.nom ?? d.data().auteurNom,
              auteurPhoto: patch.photo ?? d.data().auteurPhoto,
            })
          );
          await lot.commit();
        }
      } catch (e) {
        console.warn("Reprise de l'auteur incomplète :", e?.code || e);
      }
    },
    async search(q) {
      const { D, db } = await FB.sdk();
      const snap = await D.getDocs(D.query(D.collection(db, "users"), D.limit(200)));
      const s = norm(q);
      return snap.docs
        .map((d) => ({ uid: d.id, ...d.data() }))
        .filter((u) => !s || norm(u.nom).includes(s) || norm(u.ville || "").includes(s) || norm(u.eglise || "").includes(s))
        // ⚠️ `a.nom` peut manquer. Une fiche sans nom — un compte à moitié créé,
        // une inscription interrompue — faisait planter le TRI, donc l'écran
        // entier, pour tous les autres membres. Un annuaire ne doit pas tomber
        // parce qu'une de ses fiches est incomplète.
        .sort((a, b) => String(a.nom || "").localeCompare(String(b.nom || "")));
    },
  },

  posts: {
    /**
     * Le fil.
     *
     * ⚠️ IL N'Y A PLUS DE REQUÊTE LARGE, ET IL NE PEUT PLUS Y EN AVOIR.
     * Depuis la cinquième passe, Koinonia n'est pas une place publique : on ne
     * demande QUE des auteurs qu'on nomme. Ce n'est pas un choix d'affichage —
     * la règle de lecture n'est pas évaluable autrement (voir `monCercle`).
     *
     * Deux familles de requêtes, découpées en paquets de quinze auteurs :
     *   · MES PROCHES (moi et mes amis) — tout ce qu'ils m'ont destiné,
     *     réservé ou non ;
     *   · MES SUIVIS (ceux à qui je suis abonné sans être leur ami) — leurs
     *     publications ouvertes à tous, et rien d'autre.
     *
     * Un paquet qui échoue ne vide pas le fil : les autres continuent.
     */
    /* ⚠️ ONZIÈME PASSE — LE FIL EST REVENU AU CERCLE le 24 octobre 2026 (le
       fil ouvert de la dixième passe n'aura vécu que quatre jours : décision
       de Josaphat, « il faut un lien de proximité avant de voir »). Quatre
       familles d'écoutes, chacune adossée à une branche de la règle :
         · MES PROCHES — moi et mes amis, nommés : tout ce qu'ils m'ont
           destiné, réservé ou non ;
         · MES SUIVIS — ceux que je SUIS sans être leur ami : leur public
           seulement. S'abonner est la clé qui ouvre — et cette famille se
           RECOMPOSE quand un abonnement arrive, sinon le nouveau venu abonné
           d'office resterait devant un fil vide jusqu'au prochain passage ;
         · LE FONDATEUR — `APP.decouverte.membres`, lisible de TOUT membre
           (la porte de la onzième passe) : même sans un seul ami, un
           arrivant a cela à lire ;
         · LES PAGES D'ACCUEIL — `APP.decouverte.pages`, une page se lit
           parce que c'est une page. */
    subscribe(cb) {
      const arrets = [];
      const morceaux = new Map();

      const emettre = () => {
        const vus = new Set();
        const tout = [];
        // Une même publication peut revenir par deux familles : on la garde
        // une seule. Les publications de GROUPE ne sont jamais dans le fil.
        for (const p of [...morceaux.values()].flat()) {
          if (!p || vus.has(p.id) || p.groupeId) continue;
          vus.add(p.id);
          tout.push(p);
        }
        cb(tout.sort((a, b) => b.createdAt - a.createdAt).slice(0, TAILLE_PAGE));
      };

      FB.sdk().then(({ D, db }) => {
        const suivre = (cle, clauses, bac = arrets) =>
          bac.push(
            D.onSnapshot(
              D.query(D.collection(db, "posts"), ...clauses,
                      D.orderBy("createdAt", "desc"), D.limit(TAILLE_PAGE)),
              (snap) => { morceaux.set(cle, snap.docs.map((d) => ({ id: d.id, ...d.data() }))); emettre(); },
              () => { morceaux.set(cle, []); emettre(); }
            )
          );

        // Mes proches (+ mes propres publications, réservées comprises).
        paquets(mesProches()).forEach((groupe, i) =>
          suivre(`proches${i}`, [D.where("authorId", "in", groupe)])
        );
        // Le fondateur — pour tous, même sans lien.
        paquets(APP.decouverte?.membres || []).forEach((groupe, i) =>
          suivre(`fondateur${i}`, [D.where("authorId", "in", groupe)])
        );
        // Les pages d'accueil.
        paquets(APP.decouverte?.pages || []).forEach((groupe, i) =>
          suivre(`accueil${i}`, [D.where("pageId", "in", groupe)])
        );
        // Mes suivis — recomposés à chaque changement d'abonnement.
        let arretsSuivis = [];
        let derniersSuivis = null; // `null` : la toute première pose doit avoir lieu
        const composerSuivis = () => {
          const liste = mesSuivis();
          const signature = liste.join(",");
          if (signature === derniersSuivis) return;
          const premiere = derniersSuivis === null;
          derniersSuivis = signature;
          arretsSuivis.forEach((f) => f && f());
          arretsSuivis = [];
          for (const cle of [...morceaux.keys()]) if (cle.startsWith("suivis")) morceaux.delete(cle);
          paquets(liste).forEach((groupe, i) =>
            suivre(`suivis${i}`,
                   [D.where("authorId", "in", groupe), D.where("visibilite", "==", "tous")],
                   arretsSuivis)
          );
          // Pas au premier passage : les autres familles viennent d'être
          // posées — redessiner ferait clignoter « Aucune publication ».
          if (!premiere) emettre();
        };
        arrets.push(cloud.abonnes.subscribeMiens(composerSuivis));
        arrets.push(() => arretsSuivis.forEach((f) => f && f()));
      });
      return () => arrets.forEach((f) => f && f());
    },
    subscribePage(id, cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        const q = D.query(D.collection(db, "posts"), D.where("pageId", "==", id), D.limit(TAILLE_PAGE));
        stop = D.onSnapshot(
          q,
          (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt)),
          () => cb([])
        );
      });
      return () => stop();
    },
    subscribeGroupe(id, cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        const q = D.query(D.collection(db, "posts"), D.where("groupeId", "==", id), D.limit(100));
        stop = D.onSnapshot(
          q,
          (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt)),
          () => cb([])
        );
      });
      return () => stop();
    },
    /**
     * Les publications d'un membre.
     *
     * Sur SON propre profil, ou sur celui d'un ami, on peut tout demander. Sur
     * celui d'un inconnu, il faut se restreindre au public : sans cela, la
     * requête entière échouerait dès qu'il a une publication réservée, et sa
     * page paraîtrait vide alors qu'elle ne l'est pas.
     */
    subscribeByAuthor(id, cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        const toutVoir = id === cloud._uid || mesAmis.includes(id);
        const clauses = [D.collection(db, "posts"), D.where("authorId", "==", id)];
        if (!toutVoir) clauses.push(D.where("visibilite", "==", "tous"));
        clauses.push(D.limit(100));
        stop = D.onSnapshot(
          D.query(...clauses),
          (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt)),
          /* Un refus n'est plus une panne : depuis la cinquième passe, le profil
             d'un membre hors de mon cercle n'a rien à me montrer, et le serveur
             refuse la requête. Sans ce rattrapage, l'écran resterait sur
             « Chargement… » pour toujours. */
          () => cb([])
        );
      });
      return () => stop();
    },
    /**
     * La page suivante, au défilement.
     *
     * Comme pour l'abonnement, la requête doit se restreindre à ce que le
     * serveur autorisera — sinon elle échoue en bloc et le défilement s'arrête
     * sans rien dire. Le fil public prend donc deux pages, l'une ouverte et
     * l'autre réservée à ce que mes amis ont écrit, puis les fond.
     */
    async page({ avant, auteurId, groupeId, pageId } = {}) {
      const { D, db } = await FB.sdk();

      const demander = async (supplement = []) => {
        const clauses = [D.collection(db, "posts"), ...supplement];
        if (pageId) clauses.push(D.where("pageId", "==", pageId));
        if (groupeId) clauses.push(D.where("groupeId", "==", groupeId));
        if (auteurId) clauses.push(D.where("authorId", "==", auteurId));
        clauses.push(D.orderBy("createdAt", "desc"));
        if (avant) clauses.push(D.startAfter(avant));
        clauses.push(D.limit(TAILLE_PAGE));
        const snap = await D.getDocs(D.query(...clauses));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      };

      // Un groupe ou une page portent leur propre audience : leurs publications
      // sont toujours ouvertes à qui a le droit d'y être (voir proteger()).
      if (pageId || groupeId) return demander();

      if (auteurId) {
        const toutVoir = auteurId === cloud._uid || mesAmis.includes(auteurId);
        return demander(toutVoir ? [] : [D.where("visibilite", "==", "tous")]);
      }

      /* La page suivante du fil au cercle (onzième passe) — les mêmes
         familles que `subscribe` : proches, suivis (leur public), fondateur.
         Une requête large ne passerait pas plus ici qu'ailleurs. */
      const morceaux = await Promise.all([
        ...paquets(mesProches()).map((groupe) =>
          demander([D.where("authorId", "in", groupe)]).catch(() => [])
        ),
        ...paquets(mesSuivis()).map((groupe) =>
          demander([D.where("authorId", "in", groupe), D.where("visibilite", "==", "tous")]).catch(() => [])
        ),
        ...paquets(APP.decouverte?.membres || []).map((groupe) =>
          demander([D.where("authorId", "in", groupe)]).catch(() => [])
        ),
      ]);
      const vus = new Set();
      return morceaux
        .flat()
        .filter((p) => p && !p.groupeId && !vus.has(p.id) && vus.add(p.id))
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, TAILLE_PAGE);
    },
    async get(id) {
      const { D, db } = await FB.sdk();
      const s = await D.getDoc(D.doc(db, "posts", id));
      return s.exists() ? { id, ...s.data() } : null;
    },
    async add(data) {
      const { D, db } = await FB.sdk();
      const ref = D.doc(D.collection(db, "posts"));
      // Le compteur de cadence part dans le MÊME lot que la publication : la
      // règle l'exige, et c'est ce qui rend la limite incontournable. Une
      // publication envoyée seule est refusée par le serveur.
      await cloud.lotAvecCadence(D, db, "post", (lot) =>
        lot.set(ref, { amens: 0, commentaires: 0, partages: 0, createdAt: now(), ...data })
      );
      return ref.id;
    },
    /** Un partage de plus sur la publication d'ORIGINE, jamais sur le repartage. */
    async compterPartage(id) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "posts", id), { partages: D.increment(1) });
    },
    /**
     * Corriger le TEXTE d'une publication déjà écrite — 24 août 2026.
     *
     * Demandé par Josaphat. Le serveur l'autorisait depuis toujours
     * (`allow update: if … resource.data.authorId == moi()`), mais aucune
     * fonction ne l'appelait : une faute de frappe était définitive, et il
     * fallait effacer sa publication pour la réécrire — en perdant ses
     * réactions et ses commentaires.
     *
     * ⚠️ LE TEXTE, ET RIEN D'AUTRE. Ni l'auteur, ni la date, ni l'audience, ni
     * les compteurs. Une modification qui pourrait changer `authorId` ferait
     * d'une correction un vol de publication ; une qui toucherait `createdAt`
     * permettrait d'antidater. La façade n'accepte donc que `texte`, et cette
     * fonction n'écrit que lui.
     *
     * ⚠️ ET L'ON POSE `modifieLe`, comme un message corrigé dans la messagerie.
     * Une publication qu'on peut réécrire en silence après que dix personnes
     * l'ont approuvée, c'est une signature qu'on déplace sous les yeux de ceux
     * qui ont signé. La carte le dira.
     *
     * ⚠️ L'HEURE VIENT DU SERVEUR (`serverTimestamp`), jamais du téléphone :
     * une horloge locale se règle à la main.
     */
    async modifier(id, texte) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "posts", id), {
        texte,
        modifieLe: D.serverTimestamp(),
      });
    },
    async remove(id) {
      const { D, db } = await FB.sdk();
      // Les fichiers d'abord, tant qu'on sait encore où ils sont : une fois le
      // document effacé, plus rien ne dit quelles photos lui appartenaient.
      await cloud.oublierFichiers(await cloud.posts.get(id).catch(() => null));
      await D.deleteDoc(D.doc(db, "posts", id));
    },
  },

  amens: {
    /* Une CARTE `postId → réaction`, comme en local. Le tiroir n'a pas changé
       de place — `users/{uid}/amens/{postId}` — seul son contenu s'est enrichi
       d'un `type`. Un document d'avant n'en a pas : c'est un Amen, et il le
       reste sans qu'on ait à toucher un seul document. */
    subscribeMine(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        if (!cloud._uid) return cb(new Map());
        stop = D.onSnapshot(D.collection(db, "users", cloud._uid, "amens"), (snap) =>
          cb(new Map(snap.docs.map((d) => [d.id, R.reactionSure(d.data().type) || R.CLE_DEFAUT])))
        );
      });
      return () => stop();
    },
    /**
     * Pose, change ou retire une réaction. Reposer la même la retire.
     *
     * Tout part dans UN SEUL lot : le tiroir personnel et les compteurs de la
     * publication. Séparés, un échec au milieu laisserait un compteur qui ne
     * correspond à personne — et rien, ensuite, ne dirait lequel est faux.
     */
    async poser(postId, cle) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      if (!me) return;
      const mienne = D.doc(db, "users", me, "amens", postId);
      const snap = await D.getDoc(mienne);
      const avant = snap.exists() ? R.reactionSure(snap.data().type) || R.CLE_DEFAUT : null;
      const apres = avant === R.reactionSure(cle) ? null : R.reactionSure(cle);
      if (avant === apres) return;

      const lot = D.writeBatch(db);
      if (apres) lot.set(mienne, { type: apres, createdAt: now() });
      else lot.delete(mienne);

      /* ⚠️ LA MÊME RÉACTION EST ÉCRITE UNE SECONDE FOIS, SOUS LA PUBLICATION.
         Ajouté le 18 août 2026 ; rien de ce qui précède n'a changé.

         Le tiroir personnel `users/{uid}/amens/{postId}` répond à une seule
         question : « qu'ai-je mis sur cette publication ? ». Il ne peut pas
         répondre à « QUI a réagi à celle-ci ? » — il faudrait ouvrir le tiroir
         de chaque membre, ce qui ne se demande pas et coûterait tout.

         D'où ce second rangement, `posts/{postId}/amens/{uid}` : la même
         information, classée dans l'autre sens. C'est ce qui permet la liste
         des personnes qui ont réagi, demandée par Josaphat.

         ⚠️ DANS LE MÊME LOT, ET C'EST TOUT L'INTÉRÊT. Séparés, un échec au
         milieu laisserait les deux tiroirs en désaccord — et rien, ensuite, ne
         dirait lequel a raison. `writeBatch` ne connaît que deux issues : tout,
         ou rien.

         ⚠️ ON N'Y RECOPIE NI NOM NI PHOTO. Le membre écrit lui-même dans ce
         document : il pourrait s'y donner le nom d'un autre. Les noms sont
         relus sur les fiches au moment d'ouvrir la liste. Voir `firestore.rules`,
         section `posts/{postId}/amens`. */
      const sousLaPublication = D.doc(db, "posts", postId, "amens", me);
      if (apres) lot.set(sousLaPublication, { type: apres, createdAt: now() });
      else lot.delete(sousLaPublication);

      const maj = {};
      if (avant) maj[`reactions.${avant}`] = D.increment(-1);
      if (apres) maj[`reactions.${apres}`] = D.increment(1);
      // `amens` reste le TOTAL. Les publications d'avant les réactions n'ont
      // que lui, et c'est de lui que leur compte est repris.
      const delta = (apres ? 1 : 0) - (avant ? 1 : 0);
      if (delta) maj.amens = D.increment(delta);

      /* ⚠️ ET L'ON S'INSCRIT SUR LA PUBLICATION ELLE-MÊME — 18 août 2026.
         C'est ce qui permet au fil d'annoncer « Josaphat a aimé cette
         publication » au-dessus d'une carte.

         Pourquoi ici, et pas en interrogeant `posts/{id}/amens` : cette liste
         voyage AVEC la publication, qui est lue de toute façon. Interroger le
         tiroir pour chaque carte du fil, ce serait vingt requêtes à chaque
         ouverture — la mention ne coûte ainsi pas une lecture de plus.

         ⚠️ ON N'Y MET QUE SOI, et la règle le vérifie : sans cela, n'importe
         qui écrirait l'identifiant d'un autre et ferait dire au fil qu'il a
         aimé une publication qu'il n'a jamais vue. Voir `firestore.rules`,
         fonction `seulementMoiDans`.

         ⚠️ CHANGER DE RÉACTION NE CHANGE RIEN ICI : `arrayUnion` est
         idempotent, et l'on reste dans la liste tant qu'on soutient. On n'en
         sort qu'en retirant tout à fait. */
      if (apres) maj.derniersAmens = D.arrayUnion(me);
      else maj.derniersAmens = D.arrayRemove(me);

      lot.update(D.doc(db, "posts", postId), maj);
      await lot.commit();
    },
    /**
     * Qui a réagi à cette publication, et avec quoi. Ajouté le 18 août 2026.
     *
     * ⚠️ ON NE REND QUE DES IDENTIFIANTS ET DES TYPES. Les noms se relisent
     * ailleurs, sur les fiches — voir le commentaire de `poser`. L'écran qui
     * affiche la liste les demande une fois, à l'ouverture.
     *
     * ⚠️ ET L'ON EN DEMANDE UN NOMBRE BORNÉ. Une publication très soutenue ne
     * doit pas faire lire trois cents documents pour remplir un panneau qu'on
     * fait défiler. Cent suffit à tout ce que Koinonia verra avant longtemps.
     */
    async qui(postId, { combien = 100 } = {}) {
      const { D, db } = await FB.sdk();
      const snap = await D.getDocs(D.query(
        D.collection(db, "posts", postId, "amens"),
        D.limit(combien)
      ));
      return snap.docs.map((d) => ({
        uid: d.id,
        type: R.reactionSure(d.data().type) || R.CLE_DEFAUT,
      }));
    },

    /** L'appui court : l'Amen, comme avant les six autres. */
    async toggle(postId) {
      return cloud.amens.poser(postId, R.CLE_DEFAUT);
    },
  },

  vues: {
    /** @param {"posts"|"stories"} collection */
    async marquer(collection, id) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      if (!me) return;
      const ref = D.doc(db, collection, id, "vues", me);
      // l'identifiant du membre EST la clé : revoir n'écrit pas deux fois
      if ((await D.getDoc(ref)).exists()) return;
      const moi = await cloud.users.get(me);
      await D.setDoc(ref, { uid: me, nom: moi?.nom || "Membre", photo: moi?.photo || "", le: now() });
      // le compteur vit sur le document lui-même : tout le monde le lit sans
      // avoir à parcourir la liste, et sans droit de la lire
      await D.updateDoc(D.doc(db, collection, id), { vues: D.increment(1) }).catch(() => {});
    },
    async liste(collection, id) {
      const { D, db } = await FB.sdk();
      const snap = await D.getDocs(D.query(D.collection(db, collection, id, "vues"), D.limit(500)));
      return snap.docs.map((d) => d.data()).sort((a, b) => (b.le || 0) - (a.le || 0));
    },
  },

  comments: {
    subscribe(postId, cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        const q = D.query(D.collection(db, "posts", postId, "commentaires"), D.orderBy("createdAt", "asc"), D.limit(200));
        stop = D.onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, postId, ...d.data() }))));
      });
      return () => stop();
    },
    async add(postId, data) {
      const { D, db } = await FB.sdk();
      /* ⚠️ LE COMPTEUR DE CADENCE VOYAGE AVEC LE COMMENTAIRE — 20 octobre
         2026, comme pour une publication : la règle l'exige désormais, et un
         commentaire envoyé seul est refusé par le serveur. */
      const ref = D.doc(D.collection(db, "posts", postId, "commentaires"));
      await cloud.lotAvecCadence(D, db, "commentaire", (lot) =>
        lot.set(ref, { createdAt: now(), ...data })
      );
      /* Même raison que pour les réactions : la liste voyage avec la
         publication, et l'on n'y inscrit que soi. Elle sert à « Josaphat a
         commenté cette publication ». */
      await D.updateDoc(D.doc(db, "posts", postId), {
        commentaires: D.increment(1),
        derniersCommentateurs: D.arrayUnion(cloud._uid),
      });
    },
    async remove(postId, id) {
      const { D, db } = await FB.sdk();
      await D.deleteDoc(D.doc(db, "posts", postId, "commentaires", id));
      await D.updateDoc(D.doc(db, "posts", postId), { commentaires: D.increment(-1) });
    },

    /* ══════════ RÉAGIR À UN COMMENTAIRE — 24 août 2026 ══════════
       Demandé par Josaphat. Il a fallu lever `allow update: if false` sur les
       commentaires, qui les protégeait de TOUTE modification — mais seulement
       pour le compteur : le texte reste intouchable par tout le monde, son
       auteur compris. Onze attaques au banc le vérifient.

       ⚠️ MÊME ARCHITECTURE QUE LES PUBLICATIONS, et pour la même raison. Deux
       écritures dans un seul lot :
         · le tiroir personnel `users/{uid}/amensCommentaires/{cid}` — c'est lui
           qui FAIT FOI, et qui décide de ce que chacun voit sur son bouton ;
         · le compteur sur le commentaire — que n'importe qui peut gonfler, et
           qui ne trompe alors que l'affichage.
       Séparées, un échec au milieu laisserait un compteur qui ne correspond à
       personne, et plus rien ensuite ne dirait lequel est faux. */
    async reagir(postId, cid) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      if (!me) return;

      const mienne = D.doc(db, "users", me, "amensCommentaires", cid);
      const snap = await D.getDoc(mienne);
      const avait = snap.exists();

      const lot = D.writeBatch(db);
      if (avait) lot.delete(mienne);
      else lot.set(mienne, { type: "amen", postId, createdAt: now() });
      lot.update(D.doc(db, "posts", postId, "commentaires", cid), {
        amens: D.increment(avait ? -1 : 1),
      });
      await lot.commit();
      return !avait;   // vrai si l'on vient de poser
    },

    /**
     * Toutes MES réactions aux commentaires, d'un seul coup.
     *
     * ⚠️ UNE ÉCOUTE, PAS UNE LECTURE PAR COMMENTAIRE. Un écran de publication
     * en affiche parfois trente : les interroger un par un, ce serait trente
     * lectures à chaque ouverture, pour savoir ce qu'on a soi-même fait. Le
     * tiroir se lit en entier, et il tient en mémoire.
     */
    subscribeMesAmens(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        if (!cloud._uid) return cb(new Set());
        stop = D.onSnapshot(
          D.collection(db, "users", cloud._uid, "amensCommentaires"),
          (snap) => cb(new Set(snap.docs.map((d) => d.id)))
        );
      });
      return () => stop();
    },
  },

  convos: {
    subscribe(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        if (!cloud._uid) return cb([]);
        const q = D.query(D.collection(db, "conversations"), D.where("membres", "array-contains", cloud._uid), D.limit(100));
        stop = D.onSnapshot(q, (snap) =>
          cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            /* Une conversation SUPPRIMÉE POUR MOI (voir `supprimer`) reste
               masquée tant que rien de plus récent que ma date n'y a été
               écrit ; un nouveau message la fait revenir tout seul. */
            .filter((c) => (c.dernierAt || c.createdAt || 0) > (c.cacheAvant?.[cloud._uid] || 0))
            .sort((a, b) => (b.dernierAt || 0) - (a.dernierAt || 0)))
        );
      });
      return () => stop();
    },
    /* ⚠️ SUPPRIMER = POUR MOI SEULEMENT — 24 octobre 2026, demandé par
       Josaphat. Rien n'est détruit : je pose MA date dans `cacheAvant`, ma
       liste la masque, et l'autre ne voit aucune différence. La règle du
       serveur ne me laisse toucher que ma propre clé. */
    async supprimer(id) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "conversations", id), { [`cacheAvant.${cloud._uid}`]: now() });
    },
    async ensure(otherUid) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      const id = convoId(me, otherUid);
      const ref = D.doc(db, "conversations", id);
      if (!(await D.getDoc(ref)).exists()) {
        await D.setDoc(ref, { membres: [me, otherUid], dernier: "", dernierAt: now(), lu: { [me]: true, [otherUid]: true } });
      }
      return id;
    },
    /** Voir le pilote local : l'identifiant n'est pas déduit des membres. */
    /* ⚠️ CELUI QUI CRÉE UN GROUPE EN EST LA PREMIÈRE SENTINELLE.
       « Sentinelle » est le mot de Koinonia pour celui qui veille sur un
       groupe — ailleurs on dirait « administrateur ». La règle du serveur
       n'accepte QUE `[moi]` à la création : on ne se donne pas de complices en
       chemin. */
    async creerGroupe(uids, nom) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      const membres = [...new Set([me, ...(uids || [])])];
      if (membres.length < 3) throw new Error("Choisissez au moins deux personnes.");
      const ref = await D.addDoc(D.collection(db, "conversations"), {
        membres, nom: nom || "", groupe: true, creeParId: me,
        sentinelles: [me],
        dernier: "", dernierAt: now(),
        lu: Object.fromEntries(membres.map((m) => [m, m === me])),
      });
      return ref.id;
    },
    async get(id) {
      const { D, db } = await FB.sdk();
      const s = await D.getDoc(D.doc(db, "conversations", id));
      return s.exists() ? { id, ...s.data() } : null;
    },
    /**
     * Renommer une conversation de groupe.
     *
     * ⚠️ AUCUNE VÉRIFICATION DE DROIT ICI, et ce n'est pas un oubli : la règle
     * du serveur dit `allow update: if membre()`. N'importe quel membre du
     * groupe peut donc renommer — comme sur WhatsApp. Redire la condition dans
     * l'application ne protégerait rien et finirait par diverger.
     */
    async renommer(id, nom) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "conversations", id), {
        nom: String(nom || "").trim().slice(0, 60),
      });
    },

    /**
     * Ajouter des membres à une conversation.
     *
     * ⚠️ ON RELIT LA LISTE AVANT D'ÉCRIRE. Deux personnes qui ajoutent
     * quelqu'un en même temps écriraient chacune SA liste, et la seconde
     * effacerait le travail de la première. On part donc de ce qui est en base,
     * pas de ce qu'on avait à l'écran.
     *
     * ⚠️ ET ON PLAFONNE À TRENTE, comme la règle du serveur : au-delà, elle
     * refuserait l'écriture entière, et l'ajout échouerait sans qu'on sache
     * pourquoi. Mieux vaut s'arrêter avant qu'être refusé.
     *
     * ⚠️ `lu` REÇOIT LES NOUVEAUX À `false` : sans cela ils n'auraient aucune
     * pastille, et le groupe leur paraîtrait déjà lu.
     */
    async ajouterMembres(id, uids) {
      const { D, db } = await FB.sdk();
      const ref = D.doc(db, "conversations", id);
      const snap = await D.getDoc(ref);
      if (!snap.exists()) return;
      const avant = snap.data().membres || [];
      const membres = [...new Set([...avant, ...(uids || [])])].slice(0, 30);
      const lu = { ...(snap.data().lu || {}) };
      for (const u of membres) if (!(u in lu)) lu[u] = false;
      await D.updateDoc(ref, { membres, groupe: membres.length > 2, lu });
    },
    /* ⚠️ RETIRER UN MEMBRE — 24 octobre 2026, demandé par Josaphat, comme sur
       Messenger. Réservé à la SENTINELLE (la règle du serveur le tient), qui
       ne peut pas s'expulser elle-même. Même prudence qu'à l'ajout : on
       relit la liste avant d'écrire. Ses messages restent — on retire une
       personne, pas sa parole. */
    async retirerMembre(id, uid) {
      const { D, db } = await FB.sdk();
      const ref = D.doc(db, "conversations", id);
      const snap = await D.getDoc(ref);
      if (!snap.exists()) return;
      const membres = (snap.data().membres || []).filter((m) => m !== uid);
      const sentinelles = (snap.data().sentinelles || []).filter((m) => m !== uid);
      await D.updateDoc(ref, { membres, sentinelles });
    },

    /** La photo d'un groupe. Réservée à la sentinelle, par la règle. */
    async photoGroupe(id, url) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "conversations", id), { photo: S.lienSur(url) });
    },

    /**
     * Confier la veille à quelqu'un d'autre.
     *
     * ⚠️ ON N'EN RETIRE PAS. Ôter son titre à une sentinelle demande de
     * décider qui en a le droit — la plus ancienne ? un vote ? — et c'est une
     * question à trancher, pas à inventer ici. La liste ne fait que grandir,
     * comme celle des membres.
     */
    async nommerSentinelle(id, uid) {
      const { D, db } = await FB.sdk();
      const ref = D.doc(db, "conversations", id);
      const snap = await D.getDoc(ref);
      if (!snap.exists()) return;
      const avant = snap.data().sentinelles || [];
      if (avant.includes(uid)) return;
      await D.updateDoc(ref, { sentinelles: [...avant, uid] });
    },

    /** Voir le pilote local : `lu` allume la pastille, `luA` dessine les traits. */
    async markRead(id) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "conversations", id), {
        [`lu.${cloud._uid}`]: true,
        [`luA.${cloud._uid}`]: now(),
      });
    },
    /** L'instant de la dernière frappe. L'appelant l'espace : chaque appel est une écriture facturée. */
    async frappe(id, actif) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "conversations", id), {
        [`frappe.${cloud._uid}`]: actif ? now() : 0,
      });
    },
  },

  messages: {
    subscribe(id, cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        /* ⚠️ ON DEMANDE LES 300 DERNIERS, PAS LES 300 PREMIERS.
           C'était `orderBy("createdAt", "asc")` avec `limit(300)` — ce qui
           rend les 300 messages LES PLUS ANCIENS. Au-delà de trois cents
           messages, une conversation se figeait donc à son passé : les
           nouveaux n'arrivaient jamais, tandis que la LISTE des conversations,
           elle, continuait d'annoncer le dernier — le champ `dernier` vit sur
           la conversation, pas dans la page de messages.

           Le symptôme exact rapporté par Josaphat le 18 août 2026 : « il y a
           des mentions d'audio quand on ne rentre pas dans les messages, mais
           quand on entre, il n'y a pas les audios ». Ce n'étaient pas les
           vocaux qui manquaient — c'était toute la fin de la conversation.

           ⚠️ ET L'ON REMET DANS L'ORDRE AVANT DE RENDRE. Les deux applications
           attendent du plus ancien au plus récent ; le tri décroissant ne sert
           qu'à choisir QUELS messages, pas dans quel ordre les donner. Sans ce
           `reverse()`, le site afficherait tout à l'envers. */
        const q = D.query(
          D.collection(db, "conversations", id, "messages"),
          D.orderBy("createdAt", "desc"),
          D.limit(300)
        );
        stop = D.onSnapshot(q, (snap) =>
          cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse())
        );
      });
      return () => stop();
    },
    /**
     * ⚠️ ON N'ATTEND PLUS LA CONFIRMATION DU SERVEUR POUR RENDRE LA MAIN.
     *
     * Firestore affiche un message écrit AUSSITÔT, localement, avant même
     * d'avoir parlé au serveur — et la promesse d'`addDoc`, elle, n'aboutit
     * qu'à la confirmation. Sur un réseau hésitant, cela peut prendre de longues
     * secondes, parfois davantage. Pendant tout ce temps, l'écran d'envoi
     * attendait : le message était visible dans le fil, et le tourniquet
     * tournait encore.
     *
     * Le symptôme rapporté par Josaphat le 25 août 2026 : « le vocal part, mais
     * le truc tourne toujours là où on clique pour envoyer ». Le vocal était
     * bien parti ; c'est l'attente qui ne finissait pas.
     *
     * ⚠️ CE N'EST PAS UNE ÉCRITURE « À L'AVEUGLE ». Firestore garde l'écriture
     * et la rejoue tant qu'elle n'a pas abouti, même après une coupure ou une
     * fermeture de l'application. Rien n'est perdu par ce raccourci.
     *
     * ⚠️ ET UN REFUS RESTE VISIBLE, sans qu'on ait à le dire. Si les règles du
     * serveur rejettent l'écriture, Firestore retire le message du cache local :
     * il DISPARAÎT du fil sous les yeux de celui qui l'a écrit. C'est un signal
     * plus clair qu'un message d'erreur affiché trois minutes plus tard, quand
     * on est passé à autre chose.
     *
     * ⚠️ ON LAISSE TOUT DE MÊME UNE SECONDE ET DEMIE. Sur un bon réseau, la
     * confirmation arrive en quelques dizaines de millisecondes : autant la
     * recevoir, pour que les erreurs immédiates — texte trop long, compte
     * suspendu — remontent comme avant à celui qui écrit. Ce délai n'attend
     * que ce qui est sur le point d'arriver ; il n'invente rien.
     */
    async send(id, texte, extra = {}) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;

      const ecrire = (async () => {
        await D.addDoc(D.collection(db, "conversations", id, "messages"), {
          auteurId: me, texte, createdAt: now(), ...extra,
        });
        const convo = await cloud.convos.get(id);
        const lu = Object.fromEntries((convo?.membres || [me]).map((m) => [m, m === me]));
        // `lu` est réécrit en entier (tout le monde a du neuf à lire, sauf moi) ;
        // `luA` et `frappe` ne touchent que ma propre case.
        await D.updateDoc(D.doc(db, "conversations", id), {
          dernier: texte || (extra.audio ? "🎤 Message vocal" : ""),
          dernierAt: now(),
          lu,
          [`luA.${me}`]: now(),
          [`frappe.${me}`]: 0,
        });
      })();

      /* ⚠️ LE `catch` EST POSÉ TOUT DE SUITE, et il n'est pas décoratif : une
         promesse rejetée que personne n'attend fait tomber l'application
         entière sur certaines plateformes (« unhandled rejection »). On
         l'attrape ici, et l'on garde la vraie promesse pour la course. */
      ecrire.catch(() => {});

      await Promise.race([ecrire, new Promise((r) => setTimeout(r, 1500))]);
    },

    /* Corriger, retirer, réagir. Les règles n'autorisaient RIEN de tout cela —
       `allow update, delete: if false` — et elles ne le font toujours pas à
       l'aveugle : chacun de ces trois gestes a sa branche, et l'une d'elles
       n'est ouverte qu'à l'auteur. Voir `firestore.rules`, section messages. */
    async modifier(convoId, id, texte) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "conversations", convoId, "messages", id), {
        texte,
        // `serverTimestamp` et non `Date.now()` : la règle n'accepte que
        // l'heure du serveur, sinon on pourrait antidater une correction.
        modifieLe: D.serverTimestamp(),
      });
    },

    /**
     * Retirer un message pour tout le monde.
     *
     * Il n'est pas EFFACÉ : il reste, vidé, marqué « Message supprimé ». Un
     * vrai effacement ouvrirait un trou dans la conversation, et l'autre verrait
     * un fil dont il manque un morceau sans savoir lequel — ni que c'est vous
     * qui l'avez retiré.
     *
     * ⚠️ Les pièces jointes partent AVANT le document. Après, plus rien ne dit
     * quels fichiers lui appartenaient — c'est la leçon du trou n° 3 : retirer
     * un message dont la photo reste lisible par quiconque en a l'adresse, ce
     * n'est pas le retirer.
     */
    async supprimer(convoId, id) {
      const { D, db } = await FB.sdk();
      const ref = D.doc(db, "conversations", convoId, "messages", id);
      const snap = await D.getDoc(ref);
      if (snap.exists()) await cloud.oublierFichiers(snap.data()).catch(() => {});
      await D.updateDoc(ref, {
        supprime: true,
        supprimeLe: D.serverTimestamp(),
        texte: "",
        image: D.deleteField(),
        audio: D.deleteField(),
        duree: D.deleteField(),
        fichier: D.deleteField(),
      });

      /* ⚠️ ET L'APERÇU DOIT SUIVRE, SINON IL MENT INDÉFINIMENT.
         Le champ `dernier` vit sur la CONVERSATION, pas sur le message : le
         retrait ne l'atteignait donc pas. Retirer un vocal laissait la liste
         annoncer « 🎤 Message vocal » à jamais, tandis que la conversation
         ouverte n'en montrait aucun. Signalé par Josaphat le 18 août 2026 :
         « il y a des mentions d'audio quand on ne rentre pas dans les
         messages, mais quand on entre, il n'y a pas les audios ».

         ⚠️ ON REPART DU DERNIER MESSAGE ENCORE DEBOUT, et non du message
         retiré : c'est lui que la liste doit annoncer. On en demande dix pour
         franchir une salve de retraits successifs — dix lectures au retrait
         d'un message, geste rare, contre un aperçu faux pour toujours.

         ⚠️ LA MÊME FORMULE QUE `send`, mot pour mot. Deux façons d'écrire cet
         aperçu finiraient par diverger, et c'est la liste qui mentirait. */
      const recents = await D.getDocs(D.query(
        D.collection(db, "conversations", convoId, "messages"),
        D.orderBy("createdAt", "desc"),
        D.limit(10)
      ));
      const vivant = recents.docs.map((d) => d.data()).find((m) => !m.supprime);
      await D.updateDoc(D.doc(db, "conversations", convoId), {
        dernier: vivant ? (vivant.texte || (vivant.audio ? "🎤 Message vocal" : "")) : "",
        dernierAt: vivant?.createdAt || now(),
      });
    },

    async reagir(convoId, id, cle) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      if (!me) return;
      const ref = D.doc(db, "conversations", convoId, "messages", id);
      // On relit la sienne : reposer la même réaction la retire, comme partout.
      const snap = await D.getDoc(ref);
      const avant = R.reactionSure(snap.data()?.reactions?.[me]);
      const voulue = R.reactionSure(cle);
      const apres = avant === voulue ? null : voulue;
      if (avant === apres) return;
      await D.updateDoc(ref, { [`reactions.${me}`]: apres || D.deleteField() });
    },
  },

  reports: {
    async add(data) {
      const { D, db } = await FB.sdk();
      await D.addDoc(D.collection(db, "signalements"), { createdAt: now(), parId: cloud._uid, traite: false, ...data });
    },
    subscribe(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        const q = D.query(D.collection(db, "signalements"), D.orderBy("createdAt", "desc"), D.limit(100));
        stop = D.onSnapshot(
          q,
          (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
          () => cb([]) // non modérateur : les règles refusent la lecture
        );
      });
      return () => stop();
    },
    async traiter(id, decision) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "signalements", id), {
        traite: true, decision, traiteLe: now(), traitePar: cloud._uid,
      });
    },
  },

  // ------------------------------------------------------------- présence
  /**
   * Les appareils d'un membre : un document par téléphone, nommé par son jeton.
   *
   * ⚠️ ON DÉPOSE, ON NE LIT JAMAIS. La règle refuse la lecture à TOUT LE MONDE,
   * y compris à l'intéressé et à la modération — le seul qui en a besoin est le
   * serveur d'envoi, et un compte de service ne passe pas par les règles. Le
   * banc d'essai éprouve ce refus, précisément pour que personne ne l'ouvre un
   * jour « pour déboguer ».
   *
   * ⚠️ LE NOM DU DOCUMENT EST LE JETON, et la règle l'exige. Sans cette
   * égalité, on pourrait déposer n'importe quoi sous n'importe quel nom.
   */
  appareils: {
    /**
     * ⚠️ `bulles` DIT CE QUE CET APPAREIL SAIT FAIRE, et le serveur d'envoi
     * s'en sert pour choisir la FORME de l'alerte — voir `koinonia-pousser`.
     * Un appareil qui l'a déposé reçoit les messages « en données seules » et
     * fabrique lui-même la notification de discussion ; les autres reçoivent
     * le bandeau tout fait, comme avant. Le mentir ne prive que soi-même.
     *
     * ⚠️ LE SITE NE LE POSE PAS, et c'est voulu : un navigateur ne sait pas
     * faire de bulle. Seule l'application du téléphone passe `true`.
     */
    async deposer(uid, jeton, plateforme, bulles) {
      const { D, db } = await FB.sdk();
      if (!uid || !jeton) return;
      await D.setDoc(D.doc(db, "users", uid, "appareils", jeton), {
        jeton,
        plateforme: plateforme || "android",
        le: now(),
        bulles: bulles === true,
      });
    },
    async retirer(uid, jeton) {
      const { D, db } = await FB.sdk();
      if (!uid || !jeton) return;
      await D.deleteDoc(D.doc(db, "users", uid, "appareils", jeton));
    },
  },

  presence: {
    async battre() {
      if (!cloud._uid) return;
      const { D, db } = await FB.sdk();
      await D.setDoc(D.doc(db, "presence", cloud._uid), { at: now() });
    },
    /**
     * Qui est en ligne, et depuis quand.
     *
     * ⚠️ ON N'ÉCOUTE QUE LES SIENS, ET C'EST UNE QUESTION DE COÛT AUTANT QUE
     * DE PUDEUR. Cette écoute portait sur TOUTE la collection. Or chacun y
     * écrit son battement toutes les quarante secondes — et Firestore livre
     * chaque changement à CHAQUE application ouverte. Dix personnes
     * connectées, ce n'est donc pas dix fois le coût d'une seule : c'est cent
     * fois. Le coût grandit au CARRÉ du nombre de présents, et c'est lui qui
     * faisait franchir la limite gratuite vers la centième inscription.
     *
     * En ne nommant que ses amis, la même écoute redevient proportionnelle :
     * on ne paie que ce qu'on regarde. À l'écran, rien ne change — la façade
     * écartait déjà les autres, mais après les avoir payés.
     *
     * ⚠️ PAR PAQUETS DE QUINZE, comme le fil : `in` n'accepte pas une liste
     * illimitée, et les mêmes quinze qui valent pour les publications valent
     * ici. Quelqu'un qui a cent amis tient donc sept écoutes — c'est
     * beaucoup moins que cent lectures toutes les quarante secondes.
     *
     * @param {string[]} [qui] les identifiants à suivre. Sans liste, on
     *   retombe sur l'ancien comportement : mieux vaut une présence chère
     *   qu'une présence muette si un appelant oublie de la donner.
     */
    subscribe(cb, qui) {
      const arrets = [];
      const parLot = new Map();      // lot → { uid: at }

      FB.sdk().then(({ D, db }) => {
        const emettre = () => {
          const limite = now() - FENETRE_PRESENCE;
          const vuA = new Map();
          const enLigne = new Set();
          for (const lot of parLot.values())
            for (const [uid, at] of Object.entries(lot)) {
              vuA.set(uid, at);
              if (at > limite) enLigne.add(uid);
            }
          cb({ enLigne, vuA });
        };

        const suivre = (requete, cle) =>
          D.onSnapshot(requete, (snap) => {
            const lot = {};
            snap.docs.forEach((d) => { lot[d.id] = d.data().at || 0; });
            parLot.set(cle, lot);
            emettre();
          }, () => {});

        const liste = [...new Set(qui || [])].filter(Boolean);
        if (!liste.length) {
          arrets.push(suivre(D.query(D.collection(db, "presence"), D.limit(300)), "tous"));
          return;
        }
        for (let i = 0; i < liste.length; i += PAQUET_IN) {
          const paquet = liste.slice(i, i + PAQUET_IN);
          arrets.push(
            suivre(
              D.query(D.collection(db, "presence"), D.where(D.documentId(), "in", paquet)),
              `lot${i}`
            )
          );
        }
      });

      return () => arrets.forEach((a) => a?.());
    },
    async partir() {
      if (!cloud._uid) return;
      const { D, db } = await FB.sdk();
      await D.setDoc(D.doc(db, "presence", cloud._uid), { at: 0 });
    },
  },

  // --------------------------------------------------------------- stories
  stories: {
    /* Les statuts suivent exactement la règle des publications : on ne voit que
       ceux de son cercle, et la requête doit NOMMER ses auteurs — sinon la
       règle n'a pas d'`authorId` à évaluer et refuse tout. D'où les mêmes
       paquets de quinze qu'au fil.
       Ici pas de distinction entre proches et suivis : un statut n'a pas
       d'audience réservée, il est ouvert à ceux qui peuvent le voir. */
    subscribe(cb) {
      const arrets = [];
      const morceaux = new Map();

      const emettre = () => {
        const limite = now() - DUREE_STORY;
        const vus = new Set();
        cb(
          [...morceaux.values()]
            .flat()
            .filter((s) => s && s.createdAt > limite && !vus.has(s.id) && vus.add(s.id))
            .sort((a, b) => a.createdAt - b.createdAt)
        );
      };

      FB.sdk().then(({ D, db }) => {
        paquets(monCercle()).forEach((groupe, i) => {
          arrets.push(
            D.onSnapshot(
              D.query(D.collection(db, "stories"), D.where("authorId", "in", groupe),
                      D.orderBy("createdAt", "desc"), D.limit(150)),
              (snap) => { morceaux.set(i, snap.docs.map((d) => ({ id: d.id, ...d.data() }))); emettre(); },
              () => { morceaux.set(i, []); emettre(); }
            )
          );
        });
        if (!arrets.length) cb([]);
      });
      return () => arrets.forEach((f) => f && f());
    },
    async add(data) {
      const { D, db } = await FB.sdk();
      /* ⚠️ LE COMPTEUR DE CADENCE VOYAGE AVEC LE STATUT — 20 octobre 2026,
         comme pour une publication : la règle l'exige désormais. */
      const ref = D.doc(D.collection(db, "stories"));
      await cloud.lotAvecCadence(D, db, "story", (lot) =>
        lot.set(ref, { createdAt: now(), ...data })
      );
    },
    async remove(id) {
      const { D, db } = await FB.sdk();
      await D.deleteDoc(D.doc(db, "stories", id));
    },
    /* La carte `uid → réaction` vit SUR le statut, et non dans une
       sous-collection comme les vues. Un statut est lu par tout le monde et
       n'existe que vingt-quatre heures : une carte se lit avec lui, sans une
       requête de plus, et disparaît avec lui. */
    async reagir(id, cle) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      if (!me) return;
      const ref = D.doc(db, "stories", id);
      const snap = await D.getDoc(ref);
      const avant = R.reactionSure(snap.data()?.reactions?.[me]);
      const voulue = R.reactionSure(cle);
      const apres = avant === voulue ? null : voulue;
      if (avant === apres) return;
      await D.updateDoc(ref, { [`reactions.${me}`]: apres || D.deleteField() });
    },
  },

  // ------------------------------------------------------------------ pages
  /* ---------------------------------------------------------- événements
     Voir le pilote local pour le raisonnement. Ici la seule différence tient au
     compteur : Firestore l'incrémente à part, sans relire la liste entière. */
  evenements: {
    subscribe(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        const q = D.query(D.collection(db, "evenements"), D.orderBy("quand", "asc"), D.limit(200));
        stop = D.onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => cb([]));
      });
      return () => stop();
    },
    async get(id) {
      const { D, db } = await FB.sdk();
      const s = await D.getDoc(D.doc(db, "evenements", id));
      return s.exists() ? { id, ...s.data() } : null;
    },
    async creer(data) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      const ref = await D.addDoc(D.collection(db, "evenements"), {
        creeParId: me, participants: 1, createdAt: now(), ...data,
      });
      // celui qui organise participe
      await D.setDoc(D.doc(db, "evenements", ref.id, "participants", me), {
        membreId: me, reponse: "oui", createdAt: now(),
      });
      return ref.id;
    },
    async modifier(id, patch) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "evenements", id), patch);
    },
    async supprimer(id) {
      const { D, db } = await FB.sdk();
      await D.deleteDoc(D.doc(db, "evenements", id));
    },
    subscribeParticipants(id, cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        stop = D.onSnapshot(
          D.collection(db, "evenements", id, "participants"),
          async (snap) => {
            const gens = await Promise.all(
              snap.docs.map(async (d) => {
                const u = await cloud.users.get(d.id).catch(() => null);
                return { ...(u || { uid: d.id, nom: "Membre" }), reponse: d.data().reponse };
              })
            );
            cb(gens);
          },
          () => cb([])
        );
      });
      return () => stop();
    },
    async repondre(id, reponse) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      const ref = D.doc(db, "evenements", id, "participants", me);
      const avant = await D.getDoc(ref);
      const avantOui = avant.exists() && avant.data().reponse === "oui";
      if (reponse) await D.setDoc(ref, { membreId: me, reponse, createdAt: now() });
      else await D.deleteDoc(ref);
      const apresOui = reponse === "oui";
      if (avantOui !== apresOui)
        await D.updateDoc(D.doc(db, "evenements", id), { participants: D.increment(apresOui ? 1 : -1) });
    },
  },
  pages: {
    subscribe(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        const q = D.query(D.collection(db, "pages"), D.orderBy("createdAt", "desc"), D.limit(100));
        stop = D.onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
      });
      return () => stop();
    },
    subscribeMiennes(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        if (!cloud._uid) return cb([]);
        const q = D.query(D.collectionGroup(db, "abonnes"), D.where("membreId", "==", cloud._uid), D.limit(100));
        stop = D.onSnapshot(q, async (snap) => {
          const liens = snap.docs.map((d) => ({ pageId: d.ref.parent.parent.id, role: d.data().role }));
          const pages = await Promise.all(
            liens.map(async (l) => {
              const x = await D.getDoc(D.doc(db, "pages", l.pageId));
              return x.exists() ? { id: x.id, ...x.data(), monRole: l.role } : null;
            })
          );
          cb(pages.filter(Boolean));
        }, (e) => {
          console.warn("Mes pages : requête refusée —", e?.code || e);
          cb([]);
        });
      });
      return () => stop();
    },
    async get(id) {
      const { D, db } = await FB.sdk();
      const x = await D.getDoc(D.doc(db, "pages", id));
      return x.exists() ? { id, ...x.data() } : null;
    },
    subscribeAbonnes(id, cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        stop = D.onSnapshot(D.query(D.collection(db, "pages", id, "abonnes"), D.limit(500)), async (snap) => {
          const gens = await Promise.all(
            snap.docs.map(async (d) => {
              const u = await cloud.users.get(d.data().membreId);
              return u ? { ...u, role: d.data().role } : null;
            })
          );
          cb(gens.filter(Boolean));
        });
      });
      return () => stop();
    },
    async creer(data) {
      const { D, db } = await FB.sdk();
      const ref = await D.addDoc(D.collection(db, "pages"), { creeParId: cloud._uid, createdAt: now(), ...data });
      await D.setDoc(D.doc(db, "pages", ref.id, "abonnes", cloud._uid), {
        membreId: cloud._uid, role: "admin", createdAt: now(),
      });
      return ref.id;
    },
    async modifier(id, patch) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "pages", id), patch);
      if (patch.nom || patch.image !== undefined) {
        const snap = await D.getDocs(D.query(D.collection(db, "posts"), D.where("pageId", "==", id), D.limit(300)));
        const lot = D.writeBatch(db);
        snap.forEach((d) => lot.update(d.ref, { pageNom: patch.nom ?? d.data().pageNom, pageImage: patch.image ?? d.data().pageImage }));
        await lot.commit();
      }
    },
    async suivre(id) {
      const { D, db } = await FB.sdk();
      await D.setDoc(D.doc(db, "pages", id, "abonnes", cloud._uid), {
        membreId: cloud._uid, role: "abonne", createdAt: now(),
      });
    },
    async nePlusSuivre(id) {
      const { D, db } = await FB.sdk();
      await D.deleteDoc(D.doc(db, "pages", id, "abonnes", cloud._uid));
    },
    async supprimer(id) {
      const { D, db } = await FB.sdk();
      await D.deleteDoc(D.doc(db, "pages", id));
    },
  },

  // ---------------------------------------------------------------- groupes
  groupes: {
    subscribe(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        const q = D.query(D.collection(db, "groupes"), D.orderBy("createdAt", "desc"), D.limit(100));
        stop = D.onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
      });
      return () => stop();
    },
    subscribeMiens(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        if (!cloud._uid) return cb([]);
        // ⚠️ Requête « groupe de collections » : elle exige un index déclaré avec
        // la portée COLLECTION_GROUP (voir firestore.indexes.json). Sans lui,
        // elle échoue — et sans le gestionnaire d'erreur ci-dessous, en silence.
        const q = D.query(D.collectionGroup(db, "membres"), D.where("membreId", "==", cloud._uid), D.limit(100));
        stop = D.onSnapshot(q, async (snap) => {
          const liens = snap.docs.map((d) => ({ groupeId: d.ref.parent.parent.id, role: d.data().role }));
          const groupes = await Promise.all(
            liens.map(async (l) => {
              const g = await D.getDoc(D.doc(db, "groupes", l.groupeId));
              return g.exists() ? { id: g.id, ...g.data(), monRole: l.role } : null;
            })
          );
          cb(groupes.filter(Boolean));
        }, (e) => {
          console.warn("Mes groupes : requête refusée —", e?.code || e);
          cb([]);
        });
      });
      return () => stop();
    },
    async get(id) {
      const { D, db } = await FB.sdk();
      const g = await D.getDoc(D.doc(db, "groupes", id));
      return g.exists() ? { id, ...g.data() } : null;
    },
    subscribeMembres(id, cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        stop = D.onSnapshot(D.query(D.collection(db, "groupes", id, "membres"), D.limit(500)), async (snap) => {
          const membres = await Promise.all(
            snap.docs.map(async (d) => {
              const u = await cloud.users.get(d.data().membreId);
              return u ? { ...u, role: d.data().role } : null;
            })
          );
          cb(membres.filter(Boolean));
        });
      });
      return () => stop();
    },
    async creer(data) {
      const { D, db } = await FB.sdk();
      const ref = await D.addDoc(D.collection(db, "groupes"), { creeParId: cloud._uid, createdAt: now(), ...data });
      await D.setDoc(D.doc(db, "groupes", ref.id, "membres", cloud._uid), {
        membreId: cloud._uid, role: "responsable", createdAt: now(),
      });
      return ref.id;
    },
    async rejoindre(id) {
      const { D, db } = await FB.sdk();
      await D.setDoc(D.doc(db, "groupes", id, "membres", cloud._uid), {
        membreId: cloud._uid, role: "membre", createdAt: now(),
      });
    },
    async quitter(id) {
      const { D, db } = await FB.sdk();
      await D.deleteDoc(D.doc(db, "groupes", id, "membres", cloud._uid));
    },
    async supprimer(id) {
      const { D, db } = await FB.sdk();
      await D.deleteDoc(D.doc(db, "groupes", id));
    },
  },

  // ----------------------------------------------------------------- compte
  compte: {
    async exporter() {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      const lire = async (chemin, champ) => {
        const snap = await D.getDocs(D.query(D.collection(db, chemin), D.where(champ, "==", me), D.limit(1000)));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      };
      const profil = await cloud.users.get(me);
      // ⚠️ Une requête doit être formulée comme la règle qui l'autorise, sinon
      // Firestore refuse la requête ENTIÈRE — les règles ne filtrent pas, elles
      // valident. `amities` et `blocages` s'autorisent sur `membres`, donc on
      // interroge `membres`, puis on trie côté client.
      const lireMien = async (chemin) => {
        const snap = await D.getDocs(
          D.query(D.collection(db, chemin), D.where("membres", "array-contains", me), D.limit(1000))
        );
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      };
      const [publications, statuts, amities, blocages] = await Promise.all([
        lire("posts", "authorId"),
        lire("stories", "authorId"),
        lireMien("amities"),
        lireMien("blocages"),
      ]);
      const notifs = await D.getDocs(D.query(D.collection(db, "users", me, "notifications"), D.limit(1000)));
      return {
        exporteLe: new Date().toISOString(),
        profil,
        publications,
        statuts,
        amities,
        blocages,
        notifications: notifs.docs.map((d) => ({ id: d.id, ...d.data() })),
        note: "Les commentaires et messages privés ne figurent pas ici : ils sont imbriqués dans les contenus d'autres membres.",
      };
    },
    async supprimer() {
      const { A, auth, D, db } = await FB.sdk();
      const me = cloud._uid;
      const effacer = async (chemin, champ) => {
        const snap = await D.getDocs(D.query(D.collection(db, chemin), D.where(champ, "==", me), D.limit(500)));
        if (snap.empty) return;
        /* ⚠️ LES FICHIERS D'ABORD — 20 octobre 2026. Ce lot effaçait les
           documents sans toucher au stockage : après « Supprimer mon compte »,
           chaque photo restait en ligne, lisible de qui en avait l'adresse,
           et plus rien ne disait à qui elle avait appartenu. Le trou n° 3,
           pour la troisième fois — `remove()` d'une seule publication, lui,
           faisait le ménage depuis le début. */
        for (const d of snap.docs) await cloud.oublierFichiers(d.data()).catch(() => {});
        const lot = D.writeBatch(db);
        snap.forEach((d) => lot.delete(d.ref));
        await lot.commit();
      };
      /**
       * Même piège qu'à l'export : la requête doit épouser la règle. `amities`
       * et `blocages` s'autorisent sur `membres`, pas sur `de` ni `parId`.
       * `garder` sert ensuite à n'effacer que ce qui est réellement à moi —
       * un blocage posé CONTRE moi ne m'appartient pas.
       */
      const effacerMien = async (chemin, garder = () => true) => {
        const snap = await D.getDocs(
          D.query(D.collection(db, chemin), D.where("membres", "array-contains", me), D.limit(500))
        );
        const aEffacer = snap.docs.filter((d) => garder(d.data()));
        if (!aEffacer.length) return;
        const lot = D.writeBatch(db);
        aEffacer.forEach((d) => lot.delete(d.ref));
        await lot.commit();
      };
      await effacer("posts", "authorId");
      await effacer("stories", "authorId");
      await effacerMien("amities");
      await effacerMien("blocages", (b) => b.parId === me);
      await D.deleteDoc(D.doc(db, "presence", me)).catch(() => {});
      /* ⚠️ LES APPAREILS AUSSI — 20 octobre 2026. Le serveur d'envoi lit cette
         liste avec sa propre autorité : des jetons laissés là feraient encore
         sonner un téléphone pour un compte qui n'existe plus. Seul le membre
         peut les effacer — c'est maintenant, ou jamais. */
      try {
        const appareils = await D.getDocs(D.query(D.collection(db, "users", me, "appareils"), D.limit(50)));
        if (!appareils.empty) {
          const lot = D.writeBatch(db);
          appareils.forEach((d) => lot.delete(d.ref));
          await lot.commit();
        }
      } catch {
        // une liste illisible ne doit pas empêcher l'effacement du compte
      }
      // Firestore n'efface pas les sous-collections avec le document parent :
      // sans cette ligne, l'attestation survivrait à la suppression du compte.
      await D.deleteDoc(D.doc(db, "users", me, "prive", "attestation")).catch(() => {});
      // Celui-là ne doit PAS être avalé : si la fiche survit, le membre croirait
      // son compte effacé alors que son profil resterait visible de tous.
      await D.deleteDoc(D.doc(db, "users", me));
      // en dernier : le compte d'authentification lui-même
      await A.deleteUser(auth.currentUser);
    },
  },

  // --------------------------------------------------------------- blocages
  blocages: {
    subscribe(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        if (!cloud._uid) return cb(new Set());
        const q = D.query(D.collection(db, "blocages"), D.where("membres", "array-contains", cloud._uid), D.limit(500));
        stop = D.onSnapshot(
          q,
          (snap) =>
            cb(new Set(snap.docs.map((d) => {
              const b = d.data();
              return b.parId === cloud._uid ? b.cibleId : b.parId;
            }))),
          () => cb(new Set())
        );
      });
      return () => stop();
    },
    /**
     * Les membres que J'AI bloqués.
     *
     * ⚠️ La requête doit épouser la règle : `blocages` s'autorise sur `membres`,
     * pas sur `parId`. Interroger `parId` faisait rejeter la requête ENTIÈRE —
     * et comme `onSnapshot` sans gestionnaire d'erreur ne rappelle jamais, la
     * page profil restait bloquée sur « Chargement du profil… » pour toujours.
     * On filtre donc côté client, après coup.
     */
    subscribeMiens(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        if (!cloud._uid) return cb([]);
        const q = D.query(D.collection(db, "blocages"), D.where("membres", "array-contains", cloud._uid), D.limit(500));
        stop = D.onSnapshot(
          q,
          (snap) =>
            cb(
              snap.docs
                .map((d) => d.data())
                .filter((b) => b.parId === cloud._uid)
                .map((b) => b.cibleId)
            ),
          () => cb([])
        );
      });
      return () => stop();
    },
    async bloquer(uid) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      if (!me || me === uid) return;
      await D.setDoc(D.doc(db, "blocages", `${me}__${uid}`), { parId: me, cibleId: uid, membres: [me, uid], createdAt: now() });
      await D.deleteDoc(D.doc(db, "amities", convoId(me, uid))).catch(() => {});
    },
    async debloquer(uid) {
      const { D, db } = await FB.sdk();
      await D.deleteDoc(D.doc(db, "blocages", `${cloud._uid}__${uid}`));
    },
  },

  // ------------------------------------------------------------- modération
  moderation: {
    /* ⚠️ RENOMMER, C'EST RÉGULARISER — 24 octobre 2026, demandé par Josaphat.
       Le format du nom réel est validé ICI comme au serveur (la règle de la
       modération l'exige aussi), et l'horloge du membre (`nomChangeLe`) n'est
       pas touchée : son droit de changement semestriel lui reste entier. */
    async renommer(uid, nom) {
      const propre = S.nomReel(nom);
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "users", uid), { nom: propre });
    },
    subscribeAValider(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        const q = D.query(D.collection(db, "users"), D.where("validation", "==", "en_attente"), D.limit(100));
        stop = D.onSnapshot(
          q,
          async (snap) => {
            // l'image vit à part : on va la chercher fiche par fiche
            const liste = await Promise.all(
              snap.docs.map(async (d) => {
                const prive = await D.getDoc(D.doc(db, "users", d.id, "prive", "attestation")).catch(() => null);
                const att = prive?.exists() ? prive.data() : {};
                return { uid: d.id, ...d.data(), bapteme: { ...(d.data().bapteme || {}), ...att } };
              })
            );
            cb(liste);
          },
          () => cb([])
        );
      });
      return () => stop();
    },
    async deciderBapteme(uid, accepte, motif = "") {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "users", uid), {
        validation: accepte ? "valide" : "refuse",
        baptemeDecide: { par: cloud._uid, le: now(), motif: motif || "" },
      });
      // La donnée sensible disparaît vraiment : on supprime le document, pas un
      // champ qui laisserait un fichier orphelin derrière lui.
      await D.deleteDoc(D.doc(db, "users", uid, "prive", "attestation"));
    },
    async suspendre(uid, { jours, motif }) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "users", uid), {
        suspendu: jours ? now() + jours * 864e5 : Number.MAX_SAFE_INTEGER,
        suspenduMotif: motif || "",
        suspenduPar: cloud._uid,
        suspenduLe: now(),
      });
    },
    /** Voir le pilote local : le sceau d'or, réservé à la modération. */
    /** Le même sceau, sur une page. */
    async certifierPage(id, oui = true) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(
        D.doc(db, "pages", id),
        oui
          ? { certifie: true, certifiePar: cloud._uid, certifieLe: now() }
          : { certifie: D.deleteField(), certifiePar: D.deleteField(), certifieLe: D.deleteField() }
      );
    },
    async certifier(uid, oui = true) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(
        D.doc(db, "users", uid),
        oui
          ? { certifie: true, certifiePar: cloud._uid, certifieLe: now() }
          : { certifie: D.deleteField(), certifiePar: D.deleteField(), certifieLe: D.deleteField() }
      );
    },
    async reactiver(uid) {
      const { D, db } = await FB.sdk();
      await D.updateDoc(D.doc(db, "users", uid), {
        suspendu: D.deleteField(), suspenduMotif: D.deleteField(),
        suspenduPar: D.deleteField(), suspenduLe: D.deleteField(),
      });
    },

    /**
     * SUPPRIMER DÉFINITIVEMENT un compte — pas le restreindre.
     *
     * Efface tout ce qui appartient au membre, puis remplace sa fiche par une
     * PIERRE TOMBALE de trois champs : son identifiant, la date, et le nom de
     * qui a décidé. La règle du serveur interdit tout autre champ — aucun nom,
     * aucune photo, aucune ville ne peut survivre à cette écriture.
     *
     * ⚠️ CE QUE CECI N'EFFACE PAS, ET QUE PERSONNE NE PEUT EFFACER D'ICI :
     * le compte d'AUTHENTIFICATION lui-même. Firebase ne laisse un compte être
     * supprimé que par son propre titulaire ; un responsable ne le peut que
     * depuis la console, ou avec un serveur d'administration que Koinonia n'a
     * pas. La pierre tombale est précisément ce qui rend cela sans danger :
     * l'adresse existe encore, mais l'application referme la porte devant elle.
     *
     * ⚠️ CE QUI RESTE AILLEURS. Les amitiés (`amities`) et les tiroirs d'amis
     * appartiennent aux DEUX personnes : la règle ne laisse que l'une des deux
     * les défaire, et un responsable n'en est aucune. Le supprimé disparaît donc
     * des écrans — sa fiche n'a plus de nom — mais son identifiant reste dans le
     * tiroir de ses anciens amis. C'est du sable, pas une fuite : il n'y a plus
     * rien à lire derrière.
     *
     * Les messages privés sont conservés, comme pour une suppression volontaire :
     * ils appartiennent aussi à celui qui les a reçus.
     */
    async supprimerCompte(uid) {
      const { D, db } = await FB.sdk();
      const moi = cloud._uid;

      const effacer = async (chemin, champ) => {
        const snap = await D.getDocs(D.query(D.collection(db, chemin), D.where(champ, "==", uid), D.limit(500)));
        if (snap.empty) return;
        /* ⚠️ LES FICHIERS D'ABORD — 20 octobre 2026, comme pour la
           suppression volontaire : une « suppression définitive » qui laisse
           les photos en ligne n'est pas définitive. C'est précisément pour la
           modération que le trou n° 3 avait été bouché — et il s'était rouvert
           ici. (La règle Storage reconnaît le rôle du modérateur : il a le
           droit d'effacer ces fichiers, depuis la neuvième passe même en
           étant `admin`.) */
        for (const d of snap.docs) await cloud.oublierFichiers(d.data()).catch(() => {});
        const lot = D.writeBatch(db);
        snap.forEach((d) => lot.delete(d.ref));
        await lot.commit();
      };

      // Le contenu d'abord : si la pierre tombale partait la première, elle
      // effacerait le nom de l'auteur et l'on ne saurait plus quoi ramasser.
      await effacer("posts", "authorId");
      await effacer("stories", "authorId");
      // Les sous-collections ne partent pas avec leur parent : le dossier privé
      // — attestation et coordonnées — doit être nommé une à une.
      await D.deleteDoc(D.doc(db, "users", uid, "prive", "attestation")).catch(() => {});
      await D.deleteDoc(D.doc(db, "users", uid, "prive", "contact")).catch(() => {});
      await D.deleteDoc(D.doc(db, "presence", uid)).catch(() => {});

      // Et la pierre : `setDoc` sans fusion, pour REMPLACER la fiche entière.
      // Avec `{ merge: true }`, tous les anciens champs survivraient — et la
      // règle refuserait d'ailleurs l'écriture, ce qui est sa raison d'être.
      await D.setDoc(D.doc(db, "users", uid), {
        uid,
        supprime: D.serverTimestamp(),
        supprimePar: moi,
      });
    },
    subscribeSuspendus(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        const q = D.query(D.collection(db, "users"), D.where("suspendu", ">", now()), D.limit(100));
        stop = D.onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ uid: d.id, ...d.data() }))), () => cb([]));
      });
      return () => stop();
    },
  },

  // ---------------------------------------------------------------- amitiés
  amis: {
    subscribe(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        if (!cloud._uid) return cb([]);
        const q = D.query(D.collection(db, "amities"), D.where("membres", "array-contains", cloud._uid), D.limit(500));
        stop = D.onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => cb([]));
      });
      return () => stop();
    },
    async demander(autre) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      if (!me || me === autre) return;
      const id = convoId(me, autre);
      const ref = D.doc(db, "amities", id);
      if ((await D.getDoc(ref)).exists()) return;
      // Comme pour une publication : le compteur voyage avec la demande.
      await cloud.lotAvecCadence(D, db, "ami", (lot) =>
        lot.set(ref, { membres: [me, autre].sort(), de: me, vers: autre, etat: "attente", createdAt: now() })
      );
    },
    /* Accepter une amitié remplit LES DEUX TIROIRS — `users/{moi}/amis/{autre}`
       et `users/{autre}/amis/{moi}`.
 
       Pourquoi ce doublon plutôt que la seule collection `amities` : une règle
       de LISTE ne sait pas de qui l'on consulte la liste, ce nom étant dans la
       question et non dans le document. Rangés sous leur propriétaire, le nom
       est dans le CHEMIN — et la règle peut enfin vérifier qui a le droit de
       regarder. Voir `firestore.rules`, section « le tiroir d'amis ».
 
       L'acceptation et les deux tiroirs partent dans le MÊME lot : ou tout
       réussit, ou rien. Une amitié acceptée dont le tiroir resterait vide ne
       s'afficherait nulle part, sans que personne comprenne pourquoi.

       ⚠️ Les DEUX COMPTEURS voyagent dans ce même lot, et ce n'est pas
       facultatif : les règles refusent une entrée de tiroir dont le compteur ne
       bouge pas. C'est ainsi que la limite de mille tient sans Cloud Function.
       Voir `cloud.compterAmi` juste en dessous. */
    async accepter(autre) {
      const { D, db } = await FB.sdk();
      const moi = cloud._uid;
      const lot = D.writeBatch(db);
      lot.update(D.doc(db, "amities", convoId(moi, autre)), { etat: "acceptee", repondueAt: now() });
      lot.set(D.doc(db, "users", moi, "amis", autre), { uid: autre, depuis: now() });
      lot.set(D.doc(db, "users", autre, "amis", moi), { uid: moi, depuis: now() });
      cloud.compterAmi(D, db, lot, moi, autre, 1);
      cloud.compterAmi(D, db, lot, autre, moi, 1);
      await cloud.commitAmitie(D, db, lot);
    },
    /* Le même geste sert à trois choses : retirer un ami, annuler sa demande,
       refuser celle d'un autre. Les deux dernières n'ont AUCUN tiroir à vider —
       et depuis que le compteur existe, y toucher quand même ferait échouer le
       lot entier : les règles refusent de faire descendre un compteur pour une
       entrée qui n'était pas là. On regarde donc avant, plutôt que d'effacer à
       l'aveugle comme on pouvait le faire quand un effacement inutile ne coûtait
       rien. Cela couvre aussi les amitiés d'avant les tiroirs, dont l'entrée
       n'a jamais existé. */
    async supprimer(autre) {
      const { D, db } = await FB.sdk();
      const moi = cloud._uid;
      const mien = D.doc(db, "users", moi, "amis", autre);
      const sien = D.doc(db, "users", autre, "amis", moi);
      const [aMoi, aLui] = await Promise.all([
        D.getDoc(mien).catch(() => null),
        D.getDoc(sien).catch(() => null),
      ]);

      const lot = D.writeBatch(db);
      lot.delete(D.doc(db, "amities", convoId(moi, autre)));
      // Les deux tiroirs se vident avec elle : une amitié rompue qui laisserait
      // sa trace dans la liste de l'autre serait pire que pas de liste du tout.
      // Et les compteurs redescendent — la place rendue doit se reprendre.
      if (aMoi?.exists()) {
        lot.delete(mien);
        cloud.compterAmi(D, db, lot, moi, autre, -1);
      }
      if (aLui?.exists()) {
        lot.delete(sien);
        cloud.compterAmi(D, db, lot, autre, moi, -1);
      }
      await cloud.commitAmitie(D, db, lot);
    },
    /** Le tiroir d'un membre — sa liste d'amis, telle que la règle l'autorise. */
    subscribeAmisDe(uid, cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        stop = D.onSnapshot(
          D.query(D.collection(db, "users", uid, "amis"), D.limit(500)),
          (snap) => cb(snap.docs.map((d) => d.id)),
          // Un refus n'est pas une panne : c'est la confidentialité qui joue.
          () => cb(null)
        );
      });
      return () => stop();
    },
  },

  // --------------------------------------------------------------- abonnés
  /* Suivre quelqu'un sans être son ami. Le tiroir est rangé SOUS le membre
     suivi — `users/{uid}/abonnes/{moi}` — pour la même raison que le tiroir
     d'amis : son nom est alors dans le CHEMIN, et la règle peut aller lire son
     réglage avant de laisser écrire.

     ⚠️ C'est le SERVEUR qui décide. Tant que « Autoriser les abonnés » n'est
     pas coché sur sa fiche, l'écriture est refusée quoi qu'en pense cette
     page — la clé Firebase est publique, une vérification faite ici ne
     protégerait personne. */
  abonnes: {
    /** Suis-je abonné à ce membre ? Un seul document écouté, pas tout le tiroir. */
    subscribeSuivi(uid, cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        if (!cloud._uid) return cb(false);
        stop = D.onSnapshot(
          D.doc(db, "users", uid, "abonnes", cloud._uid),
          (s) => cb(s.exists()),
          () => cb(false)
        );
      });
      return () => stop();
    },
    /* Le NOMBRE, et non la liste. Un tiroir sans limite ne se télécharge pas
       pour être compté : `getCountFromServer` compte côté serveur et se facture
       une lecture par millier au lieu d'une par abonné. */
    async compter(uid) {
      const { D, db } = await FB.sdk();
      const r = await D.getCountFromServer(D.collection(db, "users", uid, "abonnes"));
      return r.data().count;
    },
    async abonner(uid) {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      if (!me || me === uid) return;
      await D.setDoc(D.doc(db, "users", uid, "abonnes", me), { uid: me, depuis: now() });
    },
    async desabonner(uid) {
      const { D, db } = await FB.sdk();
      if (!cloud._uid) return;
      await D.deleteDoc(D.doc(db, "users", uid, "abonnes", cloud._uid));
    },
    /** À qui je suis abonné — la liste que le fil et l'enveloppe interrogent. */
    subscribeMiens(cb) {
      ecouteursAbonnements.add(cb);
      cb(mesAbonnements);
      return () => ecouteursAbonnements.delete(cb);
    },
    /* Refermer le réglage doit VRAIMENT retirer les abonnés. La règle n'arrête
       que les nouveaux ; ceux déjà là, c'est au membre de les effacer — et lui
       seul en a le droit, avec eux-mêmes. Par paquets, la limite d'un lot
       Firestore étant de 500 écritures. */
    async vider() {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      if (!me) return;
      const PAQUET = 400;
      // borné : mieux vaut en laisser derrière soi que tourner sans fin
      for (let tour = 0; tour < 25; tour++) {
        const snap = await D.getDocs(
          D.query(D.collection(db, "users", me, "abonnes"), D.limit(PAQUET))
        );
        if (snap.empty) return;
        const lot = D.writeBatch(db);
        snap.forEach((d) => lot.delete(d.ref));
        await lot.commit();
        if (snap.size < PAQUET) return;
      }
    },
  },

  // ------------------------------------------------------------- voisinage
  signaux: {
    /**
     * Les amis de chacun des membres nommés — ce qui permet de compter les
     * AMIS EN COMMUN.
     *
     * ⚠️ ON LIT LES TIROIRS, PAS LA COLLECTION `amities`. Ce fut longtemps
     * l'inverse : une seule requête « toutes les amitiés acceptées où figure
     * l'un de mes amis ». Elle était élégante, et elle n'a JAMAIS fonctionné en
     * production — la règle de `amities` n'ouvre un document qu'à ceux qui y
     * figurent, et cette requête en demandait justement d'autres. Firestore
     * refuse alors la requête entière. L'échec était avalé par un `catch`, le
     * voisinage restait vide, et « 3 amis en commun » ne s'affichait nulle part
     * — ni dans l'annuaire, ni dans les suggestions, ni sur un profil. Le mode
     * local n'a pas de règles : tout y marchait, ce qui a caché la panne
     * pendant des semaines.
     * Le tiroir `users/{ami}/amis`, lui, s'ouvre à ses amis — le banc l'éprouve
     * (« un ami lit le tiroir de son ami », et la requête refusée juste après).
     *
     * ⚠️ UN TIROIR PAR AMI, DONC UNE REQUÊTE PAR AMI. C'est le prix de la
     * règle, et il monte avec le nombre d'amis : on s'arrête à quarante. Au
     * delà, le décompte est incomplet plutôt que ruineux — mieux vaut annoncer
     * « 12 amis en commun » quand il y en a 14 que faire payer mille lectures
     * à chaque ouverture d'écran.
     */
    async amisDe(ids) {
      const { D, db } = await FB.sdk();
      const carte = new Map((ids || []).map((id) => [id, new Set()]));
      await Promise.all(
        (ids || []).slice(0, PLAFOND_VOISINAGE).map(async (id) => {
          try {
            const snap = await D.getDocs(D.query(D.collection(db, "users", id, "amis"), D.limit(500)));
            // le nom du document EST l'identifiant de l'ami
            carte.set(id, new Set(snap.docs.map((d) => d.id)));
          } catch {
            // Un tiroir refusé — une amitié défaite entre-temps — ne doit pas
            // vider les autres. C'était le défaut de la version précédente :
            // tout ou rien, et c'était toujours rien.
          }
        })
      );
      return carte;
    },
    async coMembres() {
      const { D, db } = await FB.sdk();
      const me = cloud._uid;
      const compte = new Map();
      if (!me) return compte;
      const parcourir = async (racine, sous, champId) => {
        const miens = await D.getDocs(
          D.query(D.collectionGroup(db, sous), D.where("membreId", "==", me), D.limit(100))
        );
        for (const lien of miens.docs) {
          const espace = lien.ref.parent.parent.id;
          const autres = await D.getDocs(D.query(D.collection(db, racine, espace, sous), D.limit(500)));
          autres.forEach((d) => {
            const id = d.data().membreId;
            if (id && id !== me) compte.set(id, (compte.get(id) || 0) + 1);
          });
        }
      };
      await parcourir("groupes", "membres");
      await parcourir("pages", "abonnes");
      return compte;
    },
    async membresDesEspaces(type, ids) {
      const { D, db } = await FB.sdk();
      const racine = type === "page" ? "pages" : "groupes";
      const sous = type === "page" ? "abonnes" : "membres";
      const carte = new Map();
      // une lecture par espace : la liste vient d'un écran, elle est courte
      for (const id of (ids || []).slice(0, 40)) {
        const snap = await D.getDocs(D.query(D.collection(db, racine, id, sous), D.limit(500)));
        carte.set(id, new Set(snap.docs.map((d) => d.data().membreId).filter(Boolean)));
      }
      return carte;
    },
  },

  // --------------------------------------------------------- notifications
  notifs: {
    subscribe(cb) {
      let stop = () => {};
      FB.sdk().then(({ D, db }) => {
        if (!cloud._uid) return cb([]);
        const q = D.query(D.collection(db, "users", cloud._uid, "notifications"), D.orderBy("createdAt", "desc"), D.limit(50));
        stop = D.onSnapshot(q, (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
      });
      return () => stop();
    },
    async add(pourId, data) {
      if (!pourId || pourId === cloud._uid) return;
      const { D, db } = await FB.sdk();
      /* ⚠️ LE COMPTEUR DE CADENCE VOYAGE AVEC L'ALERTE — 20 octobre 2026.
         C'était le vecteur n° 7 d'ARCHITECTURE.md : on écrivait SANS LIMITE
         dans la boîte de n'importe qui. Deux secondes par alerte émise,
         tenues par la règle.

         ⚠️ ET LE REFUS EST AVALÉ, comme une sonnerie manquée : un membre qui
         réagit en défilant plus vite que la cadence perd l'ANNONCE, jamais le
         geste — l'Amen ou le commentaire, lui, est déjà parti. Faire échouer
         le geste pour son annonce serait pire que l'inondation qu'on ferme. */
      const lot = D.writeBatch(db);
      lot.set(D.doc(D.collection(db, "users", pourId, "notifications")), { lu: false, createdAt: now(), ...data });
      lot.set(D.doc(db, "cadences", cloud._uid, "actions", "notif"), { le: D.serverTimestamp() });
      await lot.commit().catch(() => {});
    },
    async toutLire() {
      const { D, db } = await FB.sdk();
      const q = D.query(D.collection(db, "users", cloud._uid, "notifications"), D.where("lu", "==", false), D.limit(50));
      const snap = await D.getDocs(q);
      if (snap.empty) return;
      const batch = D.writeBatch(db);
      snap.forEach((d) => batch.update(d.ref, { lu: true }));
      await batch.commit();
    },
  },

  /**
   * Écrit un lot en y joignant le compteur de cadence.
   *
   * Les règles imposent que toute action limitée pose l'instant présent dans
   * `cadences/{moi}/actions/{action}` — dans le même lot, sinon rien ne serait
   * jamais compté. `serverTimestamp()` est ce que la règle lit sous le nom de
   * `request.time` : c'est l'horloge du serveur, pas celle du téléphone, et un
   * client qui mentirait sur l'heure ne gagnerait rien.
   *
   * Le refus qui remonte quand on va trop vite est un `permission-denied` ;
   * `proteger()` le traduit en message lisible plus haut.
   */
  async lotAvecCadence(D, db, action, remplir) {
    const lot = D.writeBatch(db);
    remplir(lot);
    lot.set(D.doc(db, "cadences", cloud._uid, "actions", action), { le: D.serverTimestamp() });
    try {
      await lot.commit();
    } catch (e) {
      if (e?.code !== "permission-denied") throw e;
      // Un refus peut venir de la cadence OU d'autre chose (session périmée,
      // compte non validé). On ne devine pas : on relit le compteur — le membre
      // a le droit de lire le sien — et on ne parle de cadence que si c'est
      // vraiment elle. Un message faux vaut moins qu'un message général.
      const attente = await cloud.attenteRestante(D, db, action).catch(() => 0);
      if (attente <= 0) throw e;
      throw new S.ErreurValidation(
        action === "ami"
          ? `Vous envoyez des demandes trop vite. Patientez ${attente} seconde${attente > 1 ? "s" : ""}.`
          : action === "commentaire"
          ? `Vous commentez trop vite. Patientez ${attente} seconde${attente > 1 ? "s" : ""}.`
          : action === "story"
          ? `Vous publiez des statuts trop vite. Patientez ${attente} seconde${attente > 1 ? "s" : ""}.`
          : `Vous publiez trop vite. Patientez ${attente} seconde${attente > 1 ? "s" : ""} avant la prochaine publication.`
      );
    }
  },

  /**
   * Fait bouger d'un cran le compteur d'amis de `qui`, dans le lot qui remue
   * son tiroir. Les règles exigent les deux ensemble : un tiroir qui grossit
   * sans compteur est refusé, un compteur qui bouge sans tiroir aussi.
   *
   * ⚠️ `{ merge: true }` n'est PAS un détail de style. Un `set` ordinaire
   * REMPLACE le document, et l'incrément y repart alors de zéro : mille
   * deviendrait un, et la limite se rouvrirait toute seule. Seule la fusion
   * cumule. (Les règles rattrapent l'erreur — le banc l'éprouve — mais elles la
   * rattraperaient en refusant, c'est-à-dire en cassant l'amitié.)
   *
   * Pourquoi `increment` plutôt qu'un nombre calculé ici : le compteur de
   * l'AUTRE ne nous est pas lisible tant que l'amitié n'est pas conclue, et
   * deux acceptations simultanées se marcheraient dessus. Le serveur, lui,
   * additionne sans se tromper.
   */
  compterAmi(D, db, lot, qui, avec, pas) {
    lot.set(
      D.doc(db, "users", qui, "compteurs", "amis"),
      { n: D.increment(pas), avec },
      { merge: true }
    );
  },

  /**
   * Envoie un lot d'amitié, et met un nom sur le refus le plus probable.
   *
   * Comme pour la cadence : un `permission-denied` peut venir de la limite de
   * mille OU d'autre chose. On ne devine pas — on relit son propre compteur,
   * qu'on a le droit de lire, et on ne parle de la limite que si c'est vraiment
   * elle. Celui de l'autre nous est fermé : quand c'est LUI qui est plein, le
   * message reste général, et c'est plus honnête qu'une phrase inventée.
   */
  async commitAmitie(D, db, lot) {
    try {
      await lot.commit();
    } catch (e) {
      if (e?.code !== "permission-denied") throw e;
      const miens = await cloud.nombreAmis(D, db).catch(() => 0);
      if (miens < LIMITE_AMIS) throw e;
      throw new S.ErreurValidation(
        `Vous avez atteint la limite de ${LIMITE_AMIS} amis. Retirez-en un avant d'en ajouter un autre.`
      );
    }
  },

  /** Ce que le serveur compte comme mon nombre d'amis. */
  async nombreAmis(D, db) {
    const s = await D.getDoc(D.doc(db, "users", cloud._uid, "compteurs", "amis"));
    return s.exists() ? Number(s.data().n) || 0 : 0;
  },

  /** Secondes restantes avant que l'action redevienne permise, 0 si elle l'est. */
  async attenteRestante(D, db, action) {
    const s = await D.getDoc(D.doc(db, "cadences", cloud._uid, "actions", action));
    if (!s.exists()) return 0;
    const le = s.data().le?.toMillis?.();
    if (!le) return 0;
    return Math.max(0, Math.ceil((le + DELAIS_CADENCE[action] * 1000 - Date.now()) / 1000));
  },

  /**
   * Supprime les fichiers portés par une publication.
   *
   * Sans cela, effacer une publication n'efface que son texte : la photo reste
   * dans le stockage, et reste lisible par n'importe quel membre qui en a
   * l'adresse. Pour une publication supprimée par la modération — c'est-à-dire
   * précisément celles qu'il fallait faire disparaître — le retrait n'était
   * qu'apparent.
   *
   * L'adresse de téléchargement porte le chemin du fichier, encodé, entre
   * « /o/ » et le « ? ». C'est ce qu'on en extrait ; une adresse qui ne vient
   * pas du stockage Firebase est ignorée.
   */
  async oublierFichiers(post) {
    if (!post) return;
    const liens = [
      post.image, post.video, post.audio,
      // `fichier.url` : les documents joints à un MESSAGE passent par ici aussi
      // depuis qu'un message peut être retiré. Sans cette ligne, le PDF d'un
      // message supprimé restait dans le dépôt, lisible de qui en avait l'adresse.
      post.fichier?.url,
      ...(Array.isArray(post.images) ? post.images : []),
    ].filter((l) => typeof l === "string" && l.includes("/o/"));
    if (!liens.length) return;

    const { S, storage } = await FB.sdk();
    await Promise.all(
      liens.map(async (lien) => {
        try {
          const chemin = decodeURIComponent(lien.split("/o/")[1].split("?")[0]);
          await S.deleteObject(S.ref(storage, chemin));
        } catch {
          // Un fichier déjà absent, ou une adresse qu'on ne sait pas lire, ne
          // doit jamais empêcher la suppression de la publication elle-même.
        }
      })
    );
  },
  /**
   * Un jeton de dépôt : le droit de poser UN fichier, à UN chemin.
   *
   * Sa création est limitée en cadence — même mécanisme que publier — et le
   * fichier ne pourra être déposé qu'à un chemin portant son nom. C'est ce qui
   * ferme le dernier trou connu : rien ne limitait le NOMBRE de dépôts.
   *
   * ⚠️ Pourquoi ce détour plutôt qu'une cadence dans les règles de dépôt : ces
   * règles-là n'ont pas d'équivalent de `getAfter`, donc rien n'y relie un
   * compteur à un fichier. Le lien est donc géométrique et non temporel — deux
   * fichiers, deux chemins, deux jetons. Voir `storage.rules`.
   */
  async jetonDeDepot(D, db, moi) {
    /* ⚠️ ON ATTEND, ON N'ÉCHOUE PAS — et c'est tout le sujet.
       Le composeur envoie les photos d'un album UNE PAR UNE, en boucle. Avec un
       jeton par fichier, choisir trois photos faisait échouer la deuxième avant
       même qu'elle parte : le membre lisait « le serveur a refusé cette action »
       pour avoir voulu publier trois photos. C'est un défaut introduit avec les
       jetons, et il aurait accueilli les nouveaux membres dès le premier album.

       La limite ne bouge pas — c'est elle qui protège le stockage, et c'est le
       serveur qui la tient. C'est l'APPLICATION qui devient patiente : elle
       relit son compteur, dort le temps qu'il reste, et redemande. Deux
       secondes invisibles valent mieux qu'une erreur incompréhensible.

       Une seule reprise, et bornée par le délai lui-même : si le refus n'est
       pas une histoire de cadence, il faut le dire, pas tourner en rond. */
    const demander = async () => {
      const jeton = uid();
      const lot = D.writeBatch(db);
      lot.set(D.doc(db, "depots", moi, "jetons", jeton), { le: D.serverTimestamp() });
      // le compteur de cadence voyage dans le MÊME lot : la règle l'exige
      lot.set(D.doc(db, "cadences", moi, "actions", "depot"), { le: D.serverTimestamp() });
      await lot.commit();
      return jeton;
    };

    try {
      return await demander();
    } catch (e) {
      if (e?.code !== "permission-denied") throw e;
      // On ne devine pas la cause : on relit son compteur — chacun a le droit
      // de lire le sien — et on n'attend que si c'est vraiment la cadence.
      const attente = await cloud.attenteRestante(D, db, "depot").catch(() => 0);
      if (attente <= 0 || attente > DELAIS_CADENCE.depot) throw e;
      await new Promise((suite) => setTimeout(suite, attente * 1000 + 300));
      return await demander();
    }
  },

  async upload(file, type = "image") {
    const { S: Depot, storage, auth, D, db } = await FB.sdk();
    // juste après une inscription, `cloud._uid` n'est pas encore posé
    // (onAuthStateChanged arrive au tour suivant) : `auth.currentUser` l'est.
    const moi = auth.currentUser?.uid || cloud._uid;
    // Le jeton D'ABORD : sans lui le dépôt sera refusé, et mieux vaut échouer
    // avant d'avoir fait monter trois mégaoctets sur une connexion mobile.
    const jeton = await cloud.jetonDeDepot(D, db, moi);

    if (type === "video" || type === "audio" || type === "document") {
      // Chaque famille a son dossier, et chaque dossier sa règle : la taille et
      // le type y sont imposés séparément. Un PDF ne doit pas pouvoir se
      // glisser dans « images/ » pour profiter d'une règle qui n'est pas la
      // sienne — d'où un dossier « fichiers/ » à part.
      const dossier = { video: "videos", audio: "audios", document: "fichiers" }[type];
      const contenu = type === "document" ? "application/pdf" : file.type || `${type}/webm`;
      const ref = Depot.ref(storage, `${dossier}/${moi}/${jeton}`);
      await Depot.uploadBytes(ref, file, { contentType: contenu });
      return await Depot.getDownloadURL(ref);
    }
    /* ⚠️ UNE IMAGE DÉJÀ COMPRESSÉE NE REPASSE PAS PAR LE CANEVAS.
       `compressImage` dessine dans un `<canvas>` et fabrique un `new Image()` :
       deux objets du NAVIGATEUR. Sur le téléphone il n'y en a aucun, et le
       défaut est pire qu'une erreur — l'exception part dans un rappel de
       `FileReader`, donc la promesse ne se résout NI ne se rejette JAMAIS.
       L'envoi tournait indéfiniment, sans un mot : c'est ce que Josaphat a vu
       le 15 août 2026, après avoir choisi sa photo.

       L'application native compresse déjà, avec les outils du téléphone
       (`pont/images.js` : 1280 pixels, qualité 82 — les mêmes réglages
       exactement). Elle pose ce drapeau, et l'on saute simplement l'étape.

       ⚠️ RIEN NE CHANGE POUR LE SITE : un fichier venu d'un navigateur ne
       porte pas ce drapeau, et passe par le canevas comme avant. */
    const { blob } = file.dejaCompresse
      ? { blob: file }
      : await compressImage(file);
    /* ⚠️ Plus de « .jpg » à la fin. Le nom du fichier EST le jeton, et rien
       d'autre : la règle compare le nom au document en base, une extension
       collée derrière ne correspondrait à aucun jeton. Le type reste porté par
       `contentType`, qui est ce que le navigateur lit — et `nosniff` interdit
       de toute façon de deviner autrement. */
    const ref = Depot.ref(storage, `images/${moi}/${jeton}`);
    await Depot.uploadBytes(ref, blob, { contentType: "image/jpeg" });
    return await Depot.getDownloadURL(ref);
  },
};

/* ------------------------------------------------------------------------ export */

// Le pilote Supabase vit dans son propre fichier et n'est chargé que s'il sert :
// aucun octet inutile pour ceux qui restent en mode local ou Firebase.
/**
 * Enveloppe de sécurité appliquée AU PILOTE CHOISI, quel qu'il soit.
 * C'est ce qui garantit qu'un pilote ajouté demain (Supabase, ou un autre)
 * ne peut pas contourner la validation : elle est au-dessus, pas dedans.
 */
function proteger(p) {
  // Object.create conserve la chaîne du pilote (dont les accesseurs comme _uid)
  const enveloppe = Object.create(p);

  // L'enveloppe suit le membre connecté pour pouvoir refuser toute écriture
  // à un compte suspendu — quel que soit l'écran d'où vient la demande.
  let moiCourant = null;
  enveloppe.auth = Object.create(p.auth);
  enveloppe.auth.onChange = (cb) =>
    p.auth.onChange((u) => {
      moiCourant = u;
      // Rebrancher AVANT de prévenir l'écran : sinon le premier rendu se fait
      // sur des listes vides, et l'écran croit que le membre n'a ni ami ni
      // abonnement. Voir le long commentaire de `suivreMaSession`.
      rebrancher(u);
      cb(u);
    });

  function verifierActif() {
    // e-mail non confirmé : on lit, on ne publie pas encore
    if (moiCourant && moiCourant.emailVerifie === false)
      throw new S.ErreurValidation("Confirmez votre adresse e-mail pour pouvoir publier.");
    /* ⚠️ CES DEUX TEXTES PARLAIENT ENCORE DE L'ATTESTATION DE BAPTÊME, retirée
       de l'inscription le 19 août 2026. Un membre lisait donc qu'on vérifiait
       un document qu'on ne lui avait jamais demandé. */
    /* L'adresse n'est pas encore confirmée : c'est la première marche, et elle
       ne dépend que du membre. On le dit avant de parler du responsable, qui
       n'a même pas encore vu son dossier. */
    if (moiCourant && moiCourant.validation === "email")
      throw new S.ErreurValidation(
        "Confirmez d'abord votre adresse e-mail avec le code que nous vous avons envoyé."
      );
    if (moiCourant && moiCourant.validation === "en_attente")
      throw new S.ErreurValidation(
        "Votre inscription est en cours d'examen. Vous pourrez publier dès qu'un responsable l'aura validée."
      );
    if (moiCourant && moiCourant.validation === "refuse")
      throw new S.ErreurValidation(
        "Votre inscription n'a pas été validée. Écrivez-nous si vous pensez que c'est une erreur."
      );
    const fin = moiCourant?.suspendu;
    if (!fin || fin <= Date.now()) return;
    const definitive = fin > Date.now() + 3650 * 864e5;
    throw new S.ErreurValidation(
      definitive
        ? "Votre compte est suspendu. Écrivez-nous si vous pensez que c'est une erreur."
        : `Votre compte est suspendu jusqu'au ${new Date(fin).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}.`
    );
  }
  enveloppe.verifierActif = verifierActif;

  // Un mot de passe changé doit être aussi exigeant qu'un mot de passe créé :
  // la règle est celle de securite.js, pas celle du pilote.
  if (p.auth.changerMotDePasse)
    enveloppe.auth.changerMotDePasse = (ancien, nouveau) =>
      p.auth.changerMotDePasse(ancien, S.motDePasseSur(nouveau));

  const remplacer = (groupe, methodes) => {
    if (!Object.prototype.hasOwnProperty.call(enveloppe, groupe)) enveloppe[groupe] = Object.create(p[groupe]);
    for (const [nom, fn] of Object.entries(methodes)) enveloppe[groupe][nom] = fn;
  };

  /**
   * Traduit les refus du serveur en français.
   *
   * Quand une règle refuse une écriture, Firebase répond « Missing or
   * insufficient permissions » — incompréhensible pour un membre, et trompeur :
   * il croit à une panne alors qu'il manque une confirmation. Le cas le plus
   * fréquent est un jeton d'authentification plus ancien que la confirmation
   * de l'adresse e-mail.
   */
  function traduireRefus(e, quoi = "") {
    const code = e?.code || "";
    if (code === "permission-denied" || code === "storage/unauthorized") {
      /* ⚠️ LE REFUS DOIT SE NOMMER.
         Cette phrase était rigoureusement la même pour tout : publier, envoyer
         une photo, réagir, ajouter un ami. Un membre la voyait plusieurs fois
         sans jamais apprendre ce qui avait été refusé — et nous non plus, ce
         qui rendait chaque signalement inexploitable : « ça a affiché l'erreur
         rouge » ne désigne rien.
         Le geste est donc nommé dans le message, et le détail technique va dans
         la console pour qui sait la lire. */
      console.error("Refus du serveur —", quoi || "action non nommée", e);

      /* ⚠️ ON NOMME LA CAUSE, ON NE CONSEILLE PLUS UNE DÉCONNEXION — 20 août 2026.
         Cette phrase disait à tout le monde de se déconnecter et de se
         reconnecter. C'était le bon conseil pour UN cas — le jeton périmé — et
         un mauvais conseil pour tous les autres : celui qui attend une
         validation pouvait se reconnecter cent fois sans rien changer, et
         croyait l'application cassée. Josaphat l'a constaté le 20 août 2026 sur
         des comptes qu'il venait lui-même de valider.

         Le jeton, lui, est désormais rafraîchi AUTOMATIQUEMENT par `garde`
         avant qu'on arrive ici. Si l'on y arrive quand même, c'est que la cause
         est ailleurs — et la fiche du membre sait laquelle. */
      const tete = quoi ? `${quoi} : le serveur a refusé.` : "Le serveur a refusé cette action.";

      if (moiCourant?.validation === "email")
        return new S.ErreurValidation(
          `${tete} Confirmez d'abord votre adresse e-mail avec le code reçu.`
        );
      if (moiCourant?.validation === "en_attente")
        return new S.ErreurValidation(
          `${tete} Votre inscription attend encore la validation d'un responsable —` +
            " vous pourrez réagir et écrire dès qu'elle sera faite."
        );
      if (moiCourant?.validation === "refuse")
        return new S.ErreurValidation(
          `${tete} Votre inscription n'a pas été validée. Écrivez-nous si vous pensez que c'est une erreur.`
        );
      if (moiCourant?.emailVerifie === false)
        return new S.ErreurValidation(
          `${tete} Confirmez d'abord votre adresse e-mail : le lien est dans le message que nous vous avons envoyé.`
        );
      if (moiCourant?.suspendu && moiCourant.suspendu > Date.now())
        return new S.ErreurValidation(`${tete} Votre compte est suspendu.`);

      return new S.ErreurValidation(
        `${tete} Réessayez dans un instant. Si cela se reproduit, signalez-le : ce refus n'a pas de cause connue.`
      );
    }
    if (code === "unavailable" || code === "storage/retry-limit-exceeded")
      return new S.ErreurValidation("Connexion interrompue. Réessayez dans un instant.");

    /* Un index manquant. Firestore répond alors une phrase en anglais qui
       contient l'adresse de la console d'administration — et cette phrase
       s'affichait telle quelle au milieu du fil, sous les yeux des membres.
       Le défaut est chez nous : un index qui manque se déclare dans
       `firestore.indexes.json` et se déploie. Le membre n'a pas à le savoir. */
    if (code === "failed-precondition") {
      console.error("INDEX MANQUANT — à ajouter dans firestore.indexes.json :", e?.message || e);
      return new S.ErreurValidation("Contenu momentanément indisponible. Réessayez dans un instant.");
    }
    if (code === "resource-exhausted")
      return new S.ErreurValidation("Le service est très sollicité. Réessayez dans un instant.");

    /* Le garde-fou général. Toute erreur venant de Firebase porte un `code` ;
       nos propres refus, eux, sont des `ErreurValidation` écrites en français
       pour être lues. Ce qui vient de Firebase et qu'on n'a pas prévu ne doit
       JAMAIS arriver jusqu'à l'écran : on garde le détail pour le diagnostic
       et on rend une phrase lisible. */
    if (code && !(e instanceof S.ErreurValidation)) {
      console.error("Erreur Firebase non traduite —", code, e?.message || e);
      return new S.ErreurValidation("Une erreur est survenue. Réessayez dans un instant.");
    }
    return e;
  }

  /* ⚠️ UN SEUL RAFRAÎCHISSEMENT À LA FOIS. Vingt cartes du fil qui se voient
     toutes refuser en même temps ne doivent pas demander vingt jetons : elles
     attendent le même. */
  let jetonEnVol = null;

  const garde = (fn, quoi = "") => async (...args) => {
    verifierActif();
    try {
      return await fn(...args);
    } catch (e) {
      /* ⚠️ ON RÉESSAIE UNE FOIS, AVEC UNE CARTE D'IDENTITÉ NEUVE — 20 août 2026.
         Le cas est celui-ci, et il touchait TOUS les nouveaux : on s'inscrit,
         on se connecte, PUIS on confirme son adresse. Le jeton délivré à la
         connexion dit encore « adresse non confirmée », et il vit une heure.
         Le serveur le croit — c'est le jeton qui fait foi, jamais la base. Le
         membre a pourtant tout bien fait : il a cliqué dans son courriel, et
         l'application lui refuse le moindre Amen.

         La réponse était « déconnectez-vous puis reconnectez-vous ». C'était un
         aveu d'impuissance, et Josaphat a montré le 20 août 2026 que cela ne
         suffisait même pas toujours.

         Désormais l'application va chercher la carte neuve elle-même et refait
         le geste. Le membre ne voit rien : son Amen part, simplement.

         ⚠️ UNE SEULE FOIS. Si le second essai échoue aussi, la cause n'est pas
         le jeton — c'est une validation qui n'est pas venue, une suspension, ou
         un vrai interdit. Réessayer en boucle ne ferait que masquer cela. */
      const refusDeDroit = e?.code === "permission-denied" || e?.code === "storage/unauthorized";
      if (refusDeDroit && p.auth?.rafraichirSession) {
        try {
          jetonEnVol = jetonEnVol || p.auth.rafraichirSession().finally(() => { jetonEnVol = null; });
          const rafraichi = await jetonEnVol;
          if (rafraichi) {
            console.warn("Refus du serveur — jeton rafraîchi, on refait :", quoi || "action");
            return await fn(...args);
          }
        } catch (e2) {
          throw traduireRefus(e2, quoi);
        }
      }
      throw traduireRefus(e, quoi);
    }
  };

  /* Le même enrobage, MOINS `verifierActif` — pour les gestes de LECTURE.
     ⚠️ Pourquoi il existe. Les règles du serveur ont été assouplies le 14 août
     2026 : s'abonner ne demande plus qu'être connecté, parce qu'un nouveau
     venu — e-mail pas encore confirmé, attestation pas encore examinée — doit
     pouvoir se composer un fil, sans quoi il arrive devant un écran vide.
     Mais `garde` refusait TOUJOURS avant même d'appeler le serveur : la
     permission était accordée en haut et reprise en bas. Le nouveau venu
     n'atteignait jamais Firestore, et rien ne s'affichait pour l'expliquer.
     Ce qui protège est intact : le serveur reste seul juge, et il refuse
     toujours de s'abonner chez qui n'a pas ouvert son tiroir. */
  const gardeLecture = (fn, quoi = "") => async (...args) => {
    try {
      return await fn(...args);
    } catch (e) {
      throw traduireRefus(e, quoi);
    }
  };

  /* ------------------------------------------------------------- blocages */
  // Le masquage se fait ICI, au-dessus du pilote : tous les écrans en profitent
  // sans qu'aucun n'ait à y penser, et un écran futur sera couvert d'office.
  let bloques = new Set();
  const reemettre = new Set();
  if (p.blocages?.subscribe)
    p.blocages.subscribe((set) => {
      bloques = set || new Set();
      reemettre.forEach((f) => f());
    });

  /** Enveloppe un abonnement pour retirer ce qui provient d'un membre bloqué. */
  function filtrer(groupe, methode, auteurDe, garder = () => true) {
    const origine = p[groupe]?.[methode];
    if (typeof origine !== "function") return;
    remplacer(groupe, {
      [methode]: (...args) => {
        const cb = args.pop();
        let dernier = [];
        const emettre = () =>
          cb(dernier.filter((x) => {
            const a = auteurDe(x);
            if (a && bloques.has(a)) return false;
            return garder(x);
          }));
        reemettre.add(emettre);
        const stop = origine.call(p[groupe], ...args, (donnees) => {
          dernier = Array.isArray(donnees) ? donnees : [];
          emettre();
        });
        return () => {
          reemettre.delete(emettre);
          stop && stop();
        };
      },
    });
  }

  /**
   * Le même masquage, pour une lecture qui ne rend qu'UNE FOIS.
   *
   * ⚠️ `filtrer` ci-dessus n'enveloppe que les ABONNEMENTS. Tout ce qui se lit
   * d'un coup — une page de fil, la liste de ceux qui ont réagi, celle de ceux
   * qui ont vu — passait à côté du blocage. Un membre bloqué restait donc
   * visible dans « Qui a réagi » et dans « Qui a vu », et ses publications
   * reparaissaient dès qu'on chargeait la page suivante du fil.
   *
   * Trouvé le 20 août 2026 en cherchant où les blocages n'étaient PAS
   * appliqués — deux de ces trois listes venaient d'être écrites la veille.
   *
   * ⚠️ ON NE FILTRE PAS `users.search` ICI, ET C'EST VOLONTAIRE. Le téléphone
   * en garde le résultat vingt-quatre heures (`pont/annuaire.js`) : filtrer à
   * la lecture figerait la liste des bloqués au moment du cache, et quelqu'un
   * bloqué ce matin resterait visible jusqu'au lendemain. Les écrans le
   * filtrent au moment de dessiner, ce qui est toujours juste.
   */
  function filtrerUneFois(groupe, methode, auteurDe) {
    const origine = p[groupe]?.[methode];
    if (typeof origine !== "function") return;
    remplacer(groupe, {
      [methode]: async (...args) => {
        const resultat = await origine.call(p[groupe], ...args);
        if (!Array.isArray(resultat)) return resultat;
        return resultat.filter((x) => {
          const a = auteurDe(x);
          return !(a && bloques.has(a));
        });
      },
    });
  }

  /* --------------------------------------------------- confidentialité */
  // Comme pour les blocages, les réglages s'appliquent ICI, au-dessus du pilote :
  // un écran écrit demain en profitera sans avoir à y penser.
  //
  // ⚠️ Ce sont des règles d'application, pas des barrières. Tant que les règles
  // Supabase ne sont pas déployées, quelqu'un qui contourne cette page peut
  // encore lire ce qui est en base. Voir SECURITE.md.
  /* ⚠️ CES DEUX LISTES SE RÉABONNENT À CHAQUE OUVERTURE DE SESSION, et ce n'est
     pas une précaution : c'est la correction d'un défaut qui a éteint « En
     ligne » dans toute l'application.

     L'enveloppe se construit au CHARGEMENT DU FICHIER, bien avant que
     quiconque soit identifié. Or le pilote abandonne sans bruit quand il ne
     connaît pas encore le membre :

         subscribe(cb) { … if (!cloud._uid) return cb([]); … }

     Il rend une liste vide et n'installe AUCUNE écoute — donc il ne rappellera
     jamais. Un seul abonnement posé trop tôt, et `mesAmis` restait vide pour
     toute la durée de la session.

     Tant que ces listes ne servaient qu'à trier des publications, cela ne se
     voyait pas : le fil est tenu par le serveur, qui, lui, connaît les amis.
     Mais la présence est filtrée ICI, dans l'application (voir `presence`
     plus bas). Ensemble vide veut dire « aucun ami » : plus personne n'était
     jamais montré en ligne, ni dans le profil, ni dans la messagerie. Josaphat
     l'a vu le 14 août 2026, sur la messagerie.

     La leçon vaut au-delà de ce cas : dans cette enveloppe, tout abonnement
     qui dépend de QUI EST CONNECTÉ doit être (re)posé quand la session change,
     jamais une seule fois à la construction. */
  let mesAmis = new Set();
  let mesSuivis = new Set();

  const suivreMaSession = () => {
    let arreterAmis = () => {};
    let arreterSuivis = () => {};
    let dernier = null;

    return (u) => {
      // La fiche du membre se rafraîchit souvent (photo, réglages…) ; seul un
      // CHANGEMENT DE COMPTE demande de tout rebrancher.
      if (u?.uid === dernier) return;
      dernier = u?.uid || null;

      arreterAmis();
      arreterSuivis();
      arreterAmis = () => {};
      arreterSuivis = () => {};
      mesAmis = new Set();
      mesSuivis = new Set();

      if (!u) return reemettre.forEach((f) => f());

      if (p.amis?.subscribe)
        arreterAmis =
          p.amis.subscribe((relations) => {
            mesAmis = new Set(
              (relations || [])
                .filter((r) => r.etat === "acceptee")
                .map((r) => (r.membres || []).find((m) => m !== u.uid))
                .filter(Boolean)
            );
            reemettre.forEach((f) => f());
          }) || (() => {});

      if (p.abonnes?.subscribeMiens)
        arreterSuivis =
          p.abonnes.subscribeMiens((liste) => {
            mesSuivis = new Set(liste || []);
            reemettre.forEach((f) => f());
          }) || (() => {});

      reemettre.forEach((f) => f());
    };
  };
  const rebrancher = suivreMaSession();

  const mesReglages = () => S.confidentialiteDe(moiCourant);
  const estAmi = (uid) => mesAmis.has(uid);
  const estMoi = (uid) => uid === moiCourant?.uid;

  /**
   * Cette publication m'est-elle destinée ?
   *
   * ⚠️ CE N'EST PLUS UN FILTRE DE CONFORT. Depuis la cinquième passe, Koinonia
   * n'est pas une place publique : chacun ne voit que son cercle — lui-même,
   * ses amis, et ceux à qui il s'est abonné. Le serveur le tient déjà, et
   * fermement : une requête qui ne nomme pas ses auteurs n'est même pas
   * évaluable par la règle de lecture.
   *
   * Ceci le tient une SECONDE fois, ici, pour que le mode local se comporte
   * exactement comme le serveur. Sans quoi la démonstration mentirait sur ce
   * que voit un arrivant — et c'est précisément ce qu'on veut pouvoir montrer.
   */
  /* ⚠️ ONZIÈME PASSE (24 octobre 2026) : le fil est revenu au CERCLE. Ce
     miroir local dit la même chose que la règle serveur, pour que la
     démonstration ne mente pas : les proches tout, les suivis leur public,
     le fondateur pour tous, le lieu (groupe/page) pour qui y est. */
  const postVisible = (x) => {
    if (!x) return false;
    // Le LIEU fait l'audience : dans un groupe ou sur une page, on lit parce
    // qu'on y est, pas parce qu'on connaît celui qui écrit.
    if (x.groupeId || x.pageId) return true;
    if (estMoi(x.authorId) || estAmi(x.authorId)) return true;
    // La porte du fondateur : lisible de tous, même sans lien.
    if ((APP.decouverte?.membres || []).includes(x.authorId)) return x.visibilite !== "amis";
    // Un abonné ne voit que le public de celui qu'il suit.
    return x.visibilite !== "amis" && mesSuivis.has(x.authorId);
  };

  /* Les STATUTS, eux, restent au cercle (leur règle serveur n'a pas changé) :
     on ne voit que ceux de ses amis et de ceux qu'on suit. */
  const storyVisible = (x) => {
    if (!x) return false;
    if (estMoi(x.authorId) || estAmi(x.authorId)) return true;
    return x.visibilite !== "amis" && mesSuivis.has(x.authorId);
  };

  /** Vérifie qu'un membre accepte bien ce geste, d'après SES réglages à lui. */
  async function autoriseParLautre(uid, cle, message) {
    if (!uid || estMoi(uid) || estAmi(uid)) return; // un ami passe toujours
    const autre = await p.users.get(uid).catch(() => null);
    const reglage = S.confidentialiteDe(autre)[cle];
    if (reglage === "tous") return;
    if (reglage === "amisDAmis") {
      // au moins un ami en commun suffit
      const carte = await p.signaux.amisDe([uid]).catch(() => new Map());
      const siens = carte.get(uid) || new Set();
      if ([...siens].some((x) => mesAmis.has(x))) return;
    }
    throw new S.ErreurValidation(message);
  }

  const autreMembre = (x) => (x.membres || []).find((m) => m !== moiCourant?.uid);

  filtrer("posts", "subscribe", (x) => x.authorId, postVisible);
  filtrer("posts", "subscribeByAuthor", (x) => x.authorId, postVisible);
  filtrer("posts", "subscribeGroupe", (x) => x.authorId); // un groupe a déjà ses membres
  filtrer("posts", "subscribePage", (x) => x.authorId);   // une page est publique par nature
  filtrer("stories", "subscribe", (x) => x.authorId, storyVisible);
  filtrer("comments", "subscribe", (x) => x.authorId);
  filtrer("convos", "subscribe", autreMembre);
  filtrer("amis", "subscribe", autreMembre);
  filtrer("notifs", "subscribe", (x) => x.deId);
  filtrer("reports", "subscribe", () => null); // la modération voit tout

  /* Les lectures d'un seul coup — voir `filtrerUneFois` pour le pourquoi. */
  filtrerUneFois("posts", "page", (x) => x.authorId);
  filtrerUneFois("amens", "qui", (x) => x.uid);
  filtrerUneFois("vues", "liste", (x) => x.uid);

  // une page chargée au défilement doit être filtrée comme le reste
  //
  // ⚠️ Et ses erreurs doivent être TRADUITES. Cette méthode ne passe pas par
  // `garde` — à dessein : lire ne demande pas d'être un compte actif, et un
  // membre suspendu doit continuer à voir le fil. Mais sans traduction, une
  // erreur de Firestore remontait telle quelle jusqu'au bas du fil, en anglais,
  // adresse de la console d'administration comprise. C'est par ici qu'elle est
  // passée.
  remplacer("posts", {
    page: async (options) => {
      try {
        return (await p.posts.page(options)).filter(
          (x) => !bloques.has(x.authorId) && (options?.groupeId || options?.pageId || postVisible(x))
        );
      } catch (e) {
        throw traduireRefus(e, "Chargement du fil");
      }
    },
  });

  /**
   * Ce qu'on ne montre JAMAIS d'un autre membre, quel que soit l'écran.
   *
   * Ces champs sont demandés à l'inscription et servent aux responsables ; ils
   * n'ont aucune raison de circuler dans l'annuaire. La photo de l'attestation
   * surtout : c'est une donnée sur la religion, la plus protégée qui soit.
   * La modération, elle, passe par `moderation.subscribeAValider`, pas par ici.
   */
  const PRIVES = ["telephone", "email", "dateNaissance", "genre", "bapteme", "baptemeDecide"];
  const alleger = (u) => {
    if (!u || estMoi(u.uid)) return u;
    const copie = { ...u };
    for (const champ of PRIVES) delete copie[champ];
    return copie;
  };

  // l'annuaire ne propose ni un membre bloqué, ni quelqu'un qui a demandé à ne
  // pas y figurer — sauf ses amis, qui doivent toujours pouvoir le retrouver.
  remplacer("users", {
    search: async (q) =>
      (await p.users.search(q))
        .filter(
          (u) =>
            !bloques.has(u.uid) &&
            (S.confidentialiteDe(u).annuaire || estMoi(u.uid) || estAmi(u.uid))
        )
        .map(alleger),
    get: async (uid) => alleger(await p.users.get(uid)),
  });

  // ... et le voisinage qui nourrit les suggestions non plus : un membre bloqué
  // ne doit ressortir ni comme ami commun, ni comme co-membre d'un groupe.
  remplacer("signaux", {
    amisDe: async (ids) => {
      const carte = await p.signaux.amisDe(ids);
      for (const [, amis] of carte) for (const x of [...amis]) if (bloques.has(x)) amis.delete(x);
      return carte;
    },
    coMembres: async () => {
      const compte = await p.signaux.coMembres();
      for (const id of [...compte.keys()]) if (bloques.has(id)) compte.delete(id);
      return compte;
    },
  });

  /* ══════════════════════════ FAIRE SONNER LE TÉLÉPHONE D'EN FACE ══
     Écrire une alerte en base la rend visible À L'OUVERTURE de l'application.
     Pour qu'un téléphone sonne alors qu'elle est FERMÉE, il faut un serveur —
     `koinonia-pousser`, qui garde la clé d'envoi et vérifie qui l'appelle.

     ⚠️ ON NE FAIT SONNER QUE POUR CE QUI ATTEND UNE RÉPONSE : un message, une
     demande d'amitié. PAS pour les réactions ni les commentaires. Une réaction
     qui réveille un téléphone à 23 h use plus qu'elle n'apporte, et c'est
     exactement ce qui fait désinstaller une application. Le choix est de
     Josaphat, le 17 août 2026 ; il se change en ajoutant un appel, jamais en
     retirant une condition.

     ⚠️ CELA NE DOIT JAMAIS FAIRE ÉCHOUER LE GESTE. On n'attend pas la réponse
     et l'on avale toute erreur : un message part même si le serveur d'envoi est
     éteint, injoignable, ou si le destinataire n'a aucun téléphone déclaré.
     Une sonnerie manquée n'est pas une panne ; un message perdu, si. */
  /**
   * ⚠️ LE QUATRIÈME ARGUMENT — `donnees` — N'EST PAS DÉCORATIF.
   * Ajouté le 24 août 2026 avec les bulles de discussion. Le titre et le corps
   * font le bandeau ; `donnees` dit à l'application CE QU'ELLE REÇOIT — quelle
   * conversation, de qui, avec quelle photo. Sans lui, le téléphone ne peut pas
   * fabriquer une notification de discussion, et sans notification de
   * discussion, Android refuse la bulle.
   *
   * ⚠️ ET LE TEXTE DU MESSAGE Y VOYAGE, ce qui est nouveau. Il ne s'affiche
   * PAS sur un écran verrouillé : la notification est marquée « privée » côté
   * Android (voir `natif/ServiceBulles.kt`), qui montre alors le nom sans la
   * phrase. La règle de ce projet — rien de lisible par-dessus l'épaule —
   * tient toujours ; c'est Android qui la fait respecter, une fois qu'on le
   * lui a demandé.
   */
  async function faireSonner(pourId, titre, corps, donnees) {
    const url = APP?.urlPousser;
    if (!url || !pourId || pourId === moiCourant?.uid) return;
    try {
      const { auth } = await FB.sdk();
      const jeton = await auth.currentUser?.getIdToken?.();
      if (!jeton) return;
      await fetch(`${url}/pousser`, {
        method: "POST",
        headers: { Authorization: `Bearer ${jeton}`, "Content-Type": "application/json" },
        body: JSON.stringify({ pourId, titre, corps, donnees }),
      });
    } catch {
      // volontairement muet : voir l'avertissement ci-dessus
    }
  }

  // et l'on n'écrit pas à quelqu'un que l'on a bloqué (ou qui nous a bloqués)
  remplacer("messages", {
    send: garde(async (id, t, extra = {}) => {
      const convo = await p.convos.get(id);
      const autres = (convo?.membres || []).filter((m) => m && m !== moiCourant?.uid);
      /* ⚠️ LE BLOCAGE NE FERME QUE LE TÊTE-À-TÊTE — 20 octobre 2026. Ce
         contrôle visait « le premier membre qui n'est pas moi » : dans un
         groupe, écrire à dix personnes passait ou échouait selon la place
         d'UN bloqué dans la liste — et le message d'erreur ne disait pas qui.
         Trouvé à l'audit. En tête-à-tête on refuse, comme avant ; en groupe
         on écrit au groupe, et l'on cessera simplement de faire sonner les
         bloqués, plus bas. */
      const enGroupe = Boolean(convo?.groupe) || autres.length > 1;
      if (!enGroupe && autres[0] && bloques.has(autres[0]))
        throw new S.ErreurValidation("Vous ne pouvez plus échanger de messages avec ce membre.");
      /* Un message porte un texte, OU une pièce jointe, OU les deux. Exiger le
         texte dans tous les cas revenait à refuser une photo envoyée seule —
         c'était déjà le cas des vocaux, réglé par une exception nommée. Une
         exception par sorte de pièce jointe finit toujours par en oublier une :
         on nomme donc la règle plutôt que ses exceptions. */
      const pieceJointe = Boolean(extra.audio || extra.image || extra.fichier?.url);

      // Les adresses des pièces jointes passent par le même filtre que tout
      // lien affiché : `lienSur` n'accepte que http(s), blob: et data: d'image
      // ou de vidéo. Un `javascript:` glissé là deviendrait un piège au clic.
      const propre = { ...extra };
      /* Répondre à un message précis : on RECOPIE l'aperçu de celui qu'on cite,
         plutôt que de garder son seul identifiant. Un aperçu recopié coûte
         quelques octets et se lit sans rien demander de plus ; l'identifiant
         seul obligerait chaque bulle à aller relire le message cité — trente
         lectures pour afficher une conversation.
         Et il est TAILLÉ ET NETTOYÉ ici : ce texte vient d'un autre membre, et
         il n'y a aucune raison qu'il traverse la base sans être borné. */
      if (propre.repondA)
        propre.repondA = {
          id: String(propre.repondA.id || "").slice(0, 60),
          auteurId: String(propre.repondA.auteurId || "").slice(0, 60),
          apercu: String(propre.repondA.apercu || "").slice(0, 140),
        };
      /* Répondre à un STATUT. Même principe que ci-dessus, et pour la même
         raison de coût : on recopie de quoi redessiner la vignette, plutôt que
         de garder l'identifiant d'un statut qui aura disparu dans vingt-quatre
         heures. La conversation, elle, reste — et la réponse doit continuer de
         se comprendre longtemps après que le statut se soit effacé.
         L'image peut donc mourir avant la bulle : l'écran s'y attend et n'en
         affiche alors que le libellé. */
      if (propre.repondAStatut)
        propre.repondAStatut = {
          id: String(propre.repondAStatut.id || "").slice(0, 60),
          type: propre.repondAStatut.type === "video" ? "video"
              : propre.repondAStatut.type === "image" ? "image"
              : "texte",
          image: propre.repondAStatut.image ? S.lienSur(propre.repondAStatut.image) : "",
          apercu: String(propre.repondAStatut.apercu || "").slice(0, 140),
          fond: String(propre.repondAStatut.fond || "").slice(0, 160),
        };
      if (propre.image) propre.image = S.lienSur(propre.image);
      if (propre.fichier?.url)
        propre.fichier = {
          url: S.lienSur(propre.fichier.url),
          nom: S.texte(propre.fichier.nom, 120, { champ: "Le nom du fichier" }) || "Document",
          taille: Number(propre.fichier.taille) || 0,
        };

      const sortie = await p.messages.send(id, pieceJointe ? (t || "") : validerMessage(t), propre);

      /* Après l'envoi, jamais avant : on ne fait pas sonner pour un message qui
         n'est pas parti. Sans `await` — la sonnerie ne retient pas la main.

         ⚠️ CHEZ TOUS LES MEMBRES — 20 octobre 2026. `faireSonner` ne visait
         que le premier : dans un groupe de dix, huit n'étaient jamais prévenus
         téléphone fermé. Un envoi par destinataire, jamais chez un bloqué.
         (Le serveur d'envoi plafonne à trente appels par minute et par
         expéditeur : au rythme d'une conversation humaine, même un groupe de
         trente y tient.) */
      const annonce = pieceJointe && !t ? "vous a envoyé une pièce jointe" : "vous a écrit";
      const donneesDeBulle = {
        /* ⚠️ CES NOMS SONT UN CONTRAT AVEC `natif/ServiceBulles.kt`, qui les
           relit un par un. En renommer un ici sans le renommer là-bas fait
           retomber l'alerte dans le chemin ordinaire — sans bulle, et sans
           que rien ne le dise. (Depuis le 20 octobre, le serveur d'envoi
           réécrit lui-même `deId`, `nom` et `photo` depuis la fiche de
           l'appelant — on les envoie encore pour dire ce qu'on est.) */
        type: "message",
        convoId: id,
        deId: moiCourant?.uid || "",
        nom: moiCourant?.nom || "Koinonia",
        /* ⚠️ UNE ADRESSE, JAMAIS UNE IMAGE. Une photo de profil peut être une
           « data:URL » — l'image entière, encodée, plusieurs dizaines de
           kilo-octets. Or une alerte Firebase est plafonnée à 4 ko : au-delà,
           l'envoi est refusé et le destinataire ne reçoit RIEN. On ne
           transmet donc que ce qui est un lien ; le téléphone ira chercher
           l'image lui-même. Sans photo, il met l'icône de Koinonia. */
        photo: /^https?:\/\//.test(moiCourant?.photo || "") ? moiCourant.photo : "",
        /* Le texte, ou l'annonce de la pièce jointe : la bulle montre l'échange
           comme Messenger, pas une ligne vide. Coupé court — une notification
           n'est pas un écran de lecture. */
        texte: (t || "").trim().slice(0, 200) || annonce,
        quand: String(Date.now()),
      };
      for (const destinataire of autres) {
        if (bloques.has(destinataire)) continue;
        faireSonner(destinataire, moiCourant?.nom || "Koinonia", annonce, donneesDeBulle);
      }
      return sortie;
    }),
  });

  // L'audience est recopiée SUR la publication au moment de l'écrire. Une
  // publication garde donc l'audience qu'elle avait ce jour-là : refermer son
  // compte demain ne réécrit pas ce qu'on a déjà partagé largement.
  remplacer("posts", {
    add: garde((d) => {
      const valide = validerPost(d);
      // Dans un groupe ou sur une page, l'audience est celle du lieu : en être
      // membre suffit. Y appliquer « mes amis seulement » n'aurait aucun sens —
      // et ferait échouer la lecture du groupe pour tous les autres membres.
      //
      // Un COMPTE D'ACCUEIL publie toujours ouvert, comme une page : c'est ce
      // que le nouveau venu vient lire, et cela ne doit dépendre ni d'un
      // réglage ni du fait qu'on y ait pensé au moment d'écrire.
      const visibilite =
        valide.groupeId || valide.pageId || compteDAccueil(valide.authorId)
          ? "tous"
          : mesReglages().publications;
      return p.posts.add({ ...valide, visibilite });
    }, "Publication"),

    /* ⚠️ LA FAÇADE N'ACCEPTE QUE LE TEXTE, et c'est ici que cela se joue.
       `modifier` reçoit une CHAÎNE, pas un objet : il n'existe donc aucun
       chemin par lequel un écran — présent ou futur — pourrait glisser un
       `authorId`, un `createdAt` ou une `visibilite` dans une correction. Ce
       qu'on ne peut pas exprimer ne peut pas être exploité.

       ⚠️ ET `garde` S'APPLIQUE, comme à toute écriture : un compte suspendu ou
       non validé ne corrige pas plus qu'il ne publie. Sans cela, on aurait
       laissé là une porte ouverte à ceux à qui l'on a fermé les autres. */
    modifier: garde(
      (id, texte) =>
        p.posts.modifier(
          id,
          S.texte(texte, S.LIMITES.post, { champ: "La publication" })
        ),
      "Correction de la publication"
    ),
  });
  remplacer("stories", {
    add: garde((d) => p.stories.add({ ...validerStory(d), visibilite: mesReglages().publications }), "Statut"),
  });
  remplacer("comments", {
    add: garde((id, d) => p.comments.add(id, validerCommentaire(d)), "Commentaire"),
    /* Réagir est une écriture : elle passe par `garde`, comme publier et
       commenter. Un compte suspendu ou non validé ne réagit pas plus à un
       commentaire qu'à une publication — sans cela on lui aurait laissé là une
       porte ouverte pendant qu'on lui fermait les autres. */
    reagir: garde((postId, cid) => p.comments.reagir(postId, cid), "Réaction au commentaire"),
  });
  // Après une mise à jour de profil, l'enveloppe rafraîchit sa propre copie :
  // Firebase, lui, ne rejoue `onChange` que sur un changement d'authentification,
  // et un réglage de confidentialité doit s'appliquer tout de suite.
  remplacer("users", {
    update: garde(async (patch) => {
      const valide = validerProfil(patch);
      /* ⚠️ LE NOM SE CHANGE UNE FOIS PAR SEMESTRE — 24 octobre 2026, demandé
         par Josaphat, comme sur Facebook. Le PREMIER changement est libre
         (corriger une faute, se mettre en règle) ; chaque changement pose la
         date, et la règle du serveur tient le même compte. La modération,
         elle, renomme sans attendre — c'est son rôle de régularisation. */
      if ("nom" in valide) {
        if (valide.nom === (moiCourant?.nom || "")) {
          delete valide.nom; // rien ne change : on ne touche ni au nom ni à l'horloge
        } else {
          const SEMESTRE = 180 * 24 * 3600 * 1000;
          const dernier = Number(moiCourant?.nomChangeLe || 0);
          if (dernier && now() - dernier < SEMESTRE) {
            const quand = new Date(dernier + SEMESTRE);
            throw new S.ErreurValidation(
              `Le nom ne se change qu'une fois tous les six mois. Prochain changement possible le ${quand.toLocaleDateString("fr-FR")}.`
            );
          }
          valide.nomChangeLe = now();
        }
      }
      const suivaisAvant = S.confidentialiteDe(moiCourant).abonnes;
      const r = await p.users.update(valide);
      if (moiCourant) moiCourant = { ...moiCourant, ...valide };
      /* Refermer « Autoriser les abonnés » doit VRAIMENT les retirer. La règle
         du serveur n'arrête que les nouveaux : sans ce ménage, ceux d'hier
         resteraient inscrits derrière un réglage qui dit non — et
         reparaîtraient le jour où le membre le rouvre.
         En arrière-plan, et sans faire échouer l'enregistrement : le réglage,
         lui, est déjà pris. Ce qui resterait partirait au prochain essai. */
      if (suivaisAvant && valide.confidentialite?.abonnes === false)
        p.abonnes?.vider?.().catch(() => {});
      return r;
    }),
  });
  remplacer("reports", { add: (d) => p.reports.add(validerSignalement(d)) }); // signaler reste permis
  remplacer("amens", {
    toggle: garde((id) => p.amens.toggle(id), "Amen"),
    poser: garde((id, cle) => p.amens.poser(id, cle), "Réaction"),
  });
  /* Réagir et corriger sont des écritures : elles passent par `garde`, comme
     publier. Un compte suspendu ne réagit pas plus qu'il ne publie — et sans
     cela, il aurait trouvé là une porte laissée ouverte. */
  remplacer("messages", {
    modifier: garde((cid, id, t) => p.messages.modifier(cid, id, validerMessage(t)), "Correction du message"),
    supprimer: garde((cid, id) => p.messages.supprimer(cid, id), "Retrait du message"),
    reagir: garde((cid, id, cle) => p.messages.reagir(cid, id, cle), "Réaction"),
  });
  remplacer("stories", { reagir: garde((id, cle) => p.stories.reagir(id, cle), "Réaction au statut") });
  remplacer("amis", {
    demander: garde(async (uid) => {
      await autoriseParLautre(uid, "demandes", "Ce membre n'accepte les demandes que d'amis d'amis.");
      const sortie = await p.amis.demander(uid);
      faireSonner(uid, moiCourant?.nom || "Koinonia", "vous a envoyé une demande d'amitié");
      return sortie;
    }, "Demande d'amitié"),
    /* Ces deux-là ne passent PAS par `garde` : lire son courrier d'amitié et y
       répondre ne demande pas d'être un compte pleinement actif, et un membre
       en attente d'attestation pouvait déjà le faire. Mais leurs refus, eux,
       doivent être traduits — depuis la limite de mille, il y a une vraie
       raison d'échouer, et « FirebaseError: permission-denied » n'en est pas
       l'énoncé. */
    accepter: async (uid) => {
      try {
        return await p.amis.accepter(uid);
      } catch (e) {
        throw traduireRefus(e, "Acceptation de l'amitié");
      }
    },
    supprimer: async (uid) => {
      try {
        return await p.amis.supprimer(uid);
      } catch (e) {
        throw traduireRefus(e, "Retrait de l'ami");
      }
    },
  });

  /* Les abonnés. Le serveur a le dernier mot — il relit « Autoriser les
     abonnés » sur la fiche de l'intéressé — mais on ne propose pas de suivre
     quelqu'un qu'on a bloqué, ni quelqu'un qui nous a bloqués. */
  if (p.abonnes)
    remplacer("abonnes", {
      abonner: gardeLecture(async (uid) => {
        if (bloques.has(uid))
          throw new S.ErreurValidation("Vous ne pouvez pas suivre ce membre.");
        return p.abonnes.abonner(uid);
      }, "Abonnement"),
    });

  // Ouvrir une conversation est déjà un geste : on vérifie là, et pas seulement
  // à l'envoi, pour ne pas laisser une discussion vide s'ouvrir dans le vide.
  remplacer("convos", {
    ensure: garde(async (uid) => {
      await autoriseParLautre(uid, "messages", "Ce membre ne reçoit de messages que de ses amis.");
      return p.convos.ensure(uid);
    }),
  });

  // Qui ne veut pas apparaître « en ligne » n'émet tout simplement pas de signal :
  // c'est plus sûr que de masquer à l'affichage, où il suffirait de regarder ailleurs.
  remplacer("presence", {
    battre: () => (mesReglages().presence ? p.presence.battre() : undefined),

    /**
     * On ne voit en ligne QUE ses amis.
     *
     * Un membre qui arrive dans une communauté de cinquante personnes voyait la
     * liste entière de qui était connecté — des gens qu'il ne connaît pas, dont
     * il apprenait les habitudes avant d'avoir échangé un mot.
     *
     * ⚠️ HONNÊTETÉ SUR CE QUE CECI EST. Le filtre est ici, dans l'application,
     * et non dans les règles — contrairement au fil, qui lui est tenu par le
     * serveur. La raison est mesurée : la collection `presence` se lit d'un
     * bloc, et une règle qui vérifierait l'amitié DOCUMENT PAR DOCUMENT
     * dépasserait le plafond d'appels dès la vingtième personne, coupant la
     * présence pour tout le monde. Ce qui reste lisible pour qui contourne
     * l'application est un horodatage par identifiant — « untel était actif à
     * telle heure ». C'est le moins sensible de ce que porte cette base, et
     * c'est le seul endroit où nous en restons là.
     */
    /* ⚠️ LA FAÇADE NOMME QUI SUIVRE. Elle seule connaît les amis du moment ;
       le pilote ne fait qu'obéir. Le filtre qui suit reste en place — il ne
       coûte plus rien, et il protège d'un pilote qui rendrait trop.

       ⚠️ ET L'ÉCOUTE SE REBRANCHE QUAND LE CERCLE CHANGE — 20 octobre 2026.
       La liste des amis était figée à l'instant de l'abonnement. Or l'écoute
       de session (`signaux.js`) s'abonne AVANT que la liste d'amis n'arrive
       du serveur : elle ne suivait donc que soi-même, pour toute la session —
       et un ami accepté en cours de route n'apparaissait jamais « en ligne »
       sans recharger. Trouvé à l'audit. `reemettre` est prévenu à chaque
       changement du cercle (voir `suivreMaSession`) : on s'y accroche, et l'on
       ne rebranche que si la liste a VRAIMENT changé — rebrancher pour rien
       jetterait l'écoute à chaque blocage ou déblocage. */
    subscribe: (cb) => {
      let arreterPilote = () => {};
      let signature = null;

      const transmettre = (signaux) => {
        if (!signaux) return cb(signaux);
        const garder = (uid) => estMoi(uid) || estAmi(uid);
        const enLigne = new Set([...(signaux.enLigne || [])].filter(garder));
        const vuA = new Map();
        for (const [uid, quand] of signaux.vuA || []) if (garder(uid)) vuA.set(uid, quand);
        cb({ ...signaux, enLigne, vuA });
      };

      const brancher = () => {
        const liste = [moiCourant?.uid, ...mesAmis].filter(Boolean);
        const sig = [...liste].sort().join(",");
        if (sig === signature) return;
        signature = sig;
        arreterPilote();
        arreterPilote = p.presence.subscribe(transmettre, liste) || (() => {});
      };

      brancher();
      reemettre.add(brancher);
      return () => {
        reemettre.delete(brancher);
        arreterPilote();
      };
    },
  });
  remplacer("groupes", {
    creer: garde((d) => p.groupes.creer(validerGroupe(d))),
    rejoindre: garde((id) => p.groupes.rejoindre(id)),
  });
  remplacer("pages", {
    creer: garde((d) => p.pages.creer(validerPage(d))),
    modifier: garde((id, patch) => p.pages.modifier(id, validerPagePatch(patch))),
    suivre: garde((id) => p.pages.suivre(id)),
  });

  enveloppe.upload = async (fichier, type = "image") => {
    verifierActif();
    const maxMo =
      type === "video" ? window.APP.videoMaxMo[MODE] ?? window.APP.videoMaxMo.firebase
      : type === "audio" ? 6   // deux minutes de parole tiennent largement dessous
      : type === "document" ? 9 // la règle de dépôt coupe à 10 : on reste dessous
      : 12;
    // `fichierSur` compare le type annoncé à un préfixe MIME. « document » est
    // le nom que l'application donne au geste ; « application » est ce que le
    // fichier déclare vraiment (application/pdf).
    await S.fichierSur(fichier, type === "document" ? "application" : type, maxMo);
    try {
      return await p.upload(fichier, type);
    } catch (e) {
      throw traduireRefus(e, "Envoi du fichier");
    }
  };
  return enveloppe;
}

/* ⚠️ PLUS D'`await` AU NIVEAU DU MODULE, ET CE N'EST PAS UN DÉTAIL DE STYLE.
   Ce fichier est désormais PARTAGÉ avec l'application native : Metro le lit tel
   quel, et le moteur JavaScript d'Android comme d'iOS ne sait pas rendre
   asynchrone le corps d'un module. Un seul `await` ici, et le téléphone ne
   pouvait plus assembler l'application du tout.

   Ce qui a été retiré : le choix automatique du pilote Supabase, qui se
   chargeait par `await import("./data-supabase.js")`. C'était du code MORT —
   `SERVICES.supabase.url` est vide, donc `SUPABASE_PRET` est faux, donc `MODE`
   ne valait jamais "supabase". Rien ne change, ni sur le site ni sur
   l'application.

   ⚠️ LE JOUR OÙ VOUS ACTIVEREZ SUPABASE : `data-supabase.js` est intact, ses
   822 lignes n'ont pas bougé. Il faudra le rebrancher SANS `await` de tête —
   par exemple en l'important normalement en haut du fichier, ou en différant
   la construction du magasin. Ne remettez pas l'ancienne ligne : elle
   recasserait le natif, et rien ne le dirait avant l'assemblage.

   ⚠️ ET IL ÉCHOUE DÉSORMAIS BRUYAMMENT — 20 octobre 2026, à la suite de
   l'audit. Remplir `SERVICES.supabase` basculait TOUT LE SITE en mode
   démonstration, en silence : `MODE` valait "supabase", et la ligne du bas le
   rangeait avec `local`. En prime, le pilote a deux mois et demi de retard sur
   le contrat : ni événements, ni réactions, ni cadences, ni jetons de dépôt,
   ni appareils. Tant qu'il n'est pas rebranché ET remis au niveau, le
   déclarer doit arrêter net — un développeur qui lit une erreur vaut mieux
   que cinq cents membres en mode démo. */
if (MODE === "supabase")
  throw new Error(
    "Le pilote Supabase est DÉBRANCHÉ. Remplir SERVICES.supabase basculait tout le site " +
    "en mode démonstration, en silence — et le pilote n'implémente plus le contrat complet " +
    "(événements, réactions, cadences, jetons, appareils). Videz SERVICES.supabase, ou " +
    "rebranchez et mettez à niveau data-supabase.js : voir le commentaire au-dessus de " +
    "cette erreur dans js/store.js, et ARCHITECTURE.md."
  );
const pilote = MODE === "firebase" ? cloud : local;

const impl = proteger(pilote);

export const Store = Object.assign(Object.create(impl), { mode: MODE, convoId, compressImage });
export default Store;
